// Utils
import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes, IdsDirection } from '../../core/ids-attributes';
import { isValidBooleanValue, stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';
import { exportToCSV, exportToXLSX } from '../../utils/ids-excel-exporter/ids-excel-exporter';
import { eventPath, findInPath } from '../../utils/ids-event-path-utils/ids-event-path-utils';
import type { HTMLElementEvent } from '../../utils/ids-event-path-utils/ids-event-path-utils';

// Dependencies
import IdsDataSource, { GroupableOptions } from '../../core/ids-data-source';
import IdsDataGridFormatters from './ids-data-grid-formatters';
import { editors } from './ids-data-grid-editors';
import IdsDataGridFilters, { IdsDataGridFilterConditions } from './ids-data-grid-filters';
import { containerArguments, containerTypes } from './ids-data-grid-container-arguments';
import { IdsDataGridContextmenuArgs, setContextmenu, getContextmenuElem } from './ids-data-grid-contextmenu';
import {
  IdsDataGridColumn, IdsDataGridColumnGroup, DEFAULT_COLUMN_MIN_WIDTH, RowIndicatorTypes,
  type RowIndicatorSettings
} from './ids-data-grid-column';
import IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';
import {
  IdsDataGridEmptyMessageElements,
  setEmptyMessage,
  resetEmptyMessageElements,
  hideEmptyMessage,
  IdsDataGridToggleEmptyMessage,
  emptyMessageTemplate
} from './ids-data-grid-empty-message';

// Styles
import styles from './ids-data-grid.scss';

// Sub Components
import IdsDataGridHeader from './ids-data-grid-header';
import IdsDataGridRow from './ids-data-grid-row';
import '../ids-virtual-scroll/ids-virtual-scroll';
import '../ids-layout-flex/ids-layout-flex';
import {
  calculateAggregate,
  formatSummaryCellContent,
  type SummaryCellFormatter
} from './ids-data-grid-summary-row';
import { escapeHTML } from '../../utils/ids-xss-utils/ids-xss-utils';

// Mixins
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsPagerMixin from '../../mixins/ids-pager-mixin/ids-pager-mixin';
import IdsDataGridSaveSettingsMixin from './ids-data-grid-save-settings-mixin';
import IdsDataGridTooltipMixin from './ids-data-grid-tooltip-mixin';
import IdsDataGridCell from './ids-data-grid-cell';
import { ExcelColumn } from '../../utils/ids-excel-exporter/ids-worksheet-templates';
import IdsLoadingIndicator from '../ids-loading-indicator/ids-loading-indicator';

// Types
import type IdsHyperlink from '../ids-hyperlink/ids-hyperlink';
import type IdsButton from '../ids-button/ids-button';
import IdsModal from '../ids-modal/ids-modal';
import '../ids-switch/ids-switch';
import '../ids-swappable/ids-swappable';
import IdsSearchField from '../ids-search-field/ids-search-field';
import type IdsSwitch from '../ids-switch/ids-switch';
import IdsSwappableItem from '../ids-swappable/ids-swappable-item';
import IdsDataGridSearchMixin from './ids-data-grid-search-mixin';
import IdsDataGridToolbarMixin from './ids-data-grid-toolbar-mixin';
import type { IdsDataGridActivationReason } from './ids-data-grid-cell';
import IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsInput from '../ids-input/ids-input';
import { requestAnimationTimeout } from '../../utils/ids-timer-utils/ids-timer-utils';

// Re-exported from ids-data-grid-column to preserve external imports
export { RowIndicatorTypes };
export type { RowIndicatorSettings };

export type ActiveCellCache = {
  rowIndex: number;
  columnIndex: number;
  currentValue?: string;
  hasLeaveViewport?: boolean;
  shouldReinstateCell?: boolean;
  wasEditing?: boolean;
};

export type ScrollSettings = {
  blockOuterScroll?: boolean;
  doScroll?: boolean;
  skipIfVisible?: boolean;
};

const DEFAULT_SCROLL_SETTINGS: ScrollSettings = {
  blockOuterScroll: false,
  doScroll: true,
  skipIfVisible: false
};

export interface AddRowSettings {
  setFocus?: string | number | false;
  suppressPageSwitch?: boolean;
}

/** Sentinel returned by Array.prototype.findIndex and indexOf when no match is found. */
const NOT_FOUND_INDEX = -1;

const Base = IdsPagerMixin(
  IdsDataGridToolbarMixin(
    IdsDataGridSearchMixin(
      IdsDataGridSaveSettingsMixin(
        IdsDataGridTooltipMixin(
          IdsKeyboardMixin(
            IdsLocaleMixin(
              IdsEventsMixin(
                IdsElement
              )
            )
          )
        )
      )
    )
  )
);

/**
 * IDS Data Grid Component
 * @type {IdsDataGrid}
 * @inherits IdsElement
 * @mixes IdsPagerMixin
 * @mixes IdsDataGridTooltipMixin
 * @mixes IdsKeyboardMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @part table - the table main element
 * @part body - the table body element
 * @part header - the header element
 * @part header-cell - the header cells
 * @part row - the row elements
 * @part cell - the cell elements
 */
@customElement('ids-data-grid')
@scss(styles)
export default class IdsDataGrid extends Base {
  readonly VALID_ROW_STATES = ['row-new', 'row-warning', 'row-error', 'row-removed'] as const;

  readonly TOO_MANY_COLUMNS = 30; // 30 is arbitrary number that we can recondsider

  /**
   * Horizontal look-ahead buffer (px) used when deciding which columns are "on-screen".
   * Columns within this distance of either viewport edge are rendered ahead of time so
   * their cells are ready before they scroll into view (avoids a visible "load-in" flash).
   */
  readonly COLUMN_ONSCREEN_BUFFER = 500;

  readonly cacheHash = Math.random().toString(32).substring(2, 7);

  readonly cacheSelected = 's';

  readonly cacheDeselected = 'd';

  readonly cacheExpanded = 'e';

  readonly cacheCollapsed = 'c';

  readonly cacheCellClass = 'C';

  readonly cacheCellPart = 'P';

  initialized = false;

  isResizing = false;

  isSelecting = false;

  isMovingColumnNodes = false;

  activeCell: Record<string, any> = {};

  lastActiveCell: null | Record<string, any> = {};

  #activeCellCache: ActiveCellCache | null = null;

  #editedCellsCache: Map<string, {
    rowIndex: number, columnIndex: number,
    currentValue: string, originalValue?: string
  }> = new Map();

  autoFitSet = false;

  currentColumns?: IdsDataGridColumn[];

  /** Columns that use a function for any cache-affecting property, memoized when columns are set */
  #functionColumns: IdsDataGridColumn[] = [];

  /**
   * Cached max `column.height` across visible columns. `undefined` means "not computed yet".
   * Reset when columns are set. Read on the hot path from row #setAttributes to skip the
   * per-cell height cleanup loop when no column defines a height (the common case).
   */
  #maxColumnHeight?: number;

  sortColumn?: { id: string, ascending: boolean };

  emptyMessageElements?: IdsDataGridEmptyMessageElements;

  openMenu: null | IdsPopupMenu = null;

  serverSideSelections: Array<{ index: number, data: Record<string, unknown> }> = [];

  isRowDisabled?: (rowIndex: number, rowData: Record<string, any>) => boolean;

  /** Optional callback to control keyboard navigation. Return false to block. */
  shouldAllowNavigation?: (keyboardEvent: KeyboardEvent) => boolean;

  filterFields: IdsTriggerField[] = [];

  #startSelectionCell?: HTMLElement | null;

  #endSelectionCell?: HTMLElement | null;

  #columnVisibilityUpdateTimer?: number;

  #columnsToUpdate: Set<string> = new Set();

  #currentScroll = {
    top: NaN,
    left: NaN
  };

  /** Last vertical scroll position, used to detect horizontal-only scrolls */
  #lastScrollTop = NaN;

  /** Whether the most recent scroll event changed the vertical (row) position */
  #lastScrollWasVertical = true;

  #afterSetActiveCellFn: (() => void) | undefined;

  /**
   * Types for contextmenu.
   */
  contextmenuTypes = { ...containerTypes };

  selectWithFilterDropdown = false;

  dirtyRows?: Set<string | number>;

  // Add property to track spanning cells
  private spanTracker: Map<string, number> = new Map();

  /** Tracks the currently expanded cell { row, cell } for expandOnActivate */
  #expandedCellInfo: { row: number, cell: number } | null = null;

  /** Cache of expanded row heights for expandOnActivate feature */
  expandedRowHeights: Record<number, number> = {};

  constructor() {
    super();

    this.initialized = false;
    this.state = {
      menuData: null
    };
  }

  /** #@see IdsDataGrid.header */
  #header?: IdsDataGridHeader;

  /**
   * Get the header element
   * @returns {IdsDataGridHeader | undefined} the header element
   */
  get header(): IdsDataGridHeader | undefined {
    if (!this.#header) {
      this.#header = this.container?.querySelector<IdsDataGridHeader>('ids-data-grid-header:not(.column-groups)') ?? undefined;
    }
    return this.#header;
  }

  /**
   * Get the body element
   * @returns {HTMLElement | null | undefined} the body element
   */
  get body() {
    return this.container?.querySelector<HTMLElement>('.ids-data-grid-body');
  }

  /**
   * Get the outside wrapper element
   * @returns {HTMLElement | undefined | null} the wrapper element
   */
  get wrapper() {
    return this.container?.parentNode as HTMLElement | undefined | null;
  }

  /**
   * Get the cell with active tabindex
   * @returns {IdsDataGridCell | null} the focused cell
   */
  get cellFocused(): IdsDataGridCell | null {
    return this.shadowRoot?.querySelector<IdsDataGridCell>('[tabindex="0"]') ?? null;
  }

  /**
   * Get all the editable cell elements in an array
   * @returns {IdsDataGridCell[]} array of editable cells
   */
  get cellsEditable(): IdsDataGridCell[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.container!.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell.is-editable:not(.is-readonly,.is-disabled)')
    ];
  }

  /**
   * Get all the row elements in an array
   * @returns {IdsDataGridRow[]} array of row elements
   */
  get rows(): IdsDataGridRow[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.container!.querySelectorAll<IdsDataGridRow>('.ids-data-grid-body ids-data-grid-row')
    ];
  }

  /**
   * Get all the selected row elements in an array
   * @returns {IdsDataGridRow[]} array of selected row elements
   */
  get rowsSelected(): IdsDataGridRow[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.container!.querySelectorAll<IdsDataGridRow>('.ids-data-grid-body ids-data-grid-row.selected')
    ];
  }

  /**
   * Get all the hidden row elements in an array
   * @returns {IdsDataGridRow[]} array of hidden row elements
   */
  get rowsHidden(): IdsDataGridRow[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.container!.querySelectorAll<IdsDataGridRow>('.ids-data-grid-body ids-data-grid-row[hidden]')
    ];
  }

  /**
   * Get all the visible row elements in an array
   * @returns {IdsDataGridRow[]} array of visible row elements
   */
  get rowsVisible(): IdsDataGridRow[] {
    // NOTE: Array.from() seems slower than dotdotdot array-destructuring.
    return [
      ...this.container!.querySelectorAll<IdsDataGridRow>('.ids-data-grid-body ids-data-grid-row:not([hidden])')
    ];
  }

  /**
   * Get rows used for virtual scrolling to operate on
   * @returns {Array<Record<string, any>>} virtual rows
   */
  get virtualRows(): Array<Record<string, any>> {
    return this.data.filter((row) => !row.rowHidden);
  }

  connectedCallback() {
    if (this.initialized) this.restoreAllSettings?.();

    super.connectedCallback();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
    this.#attachScrollEvents();
    this.#attachLoadingIndicatorEvents();

    if (this.initialized) {
      // restore scroll position after virtual scroll handlers are set
      this.#restoreScrollPosition(this.#currentScroll.top, this.#currentScroll.left);
    }
  }

  mountedCallback() {
    super.mountedCallback?.();
    if (this.stickyLayout) this.#applyStickyLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupStickyLayout();
    if (this.#columnVisibilityUpdateTimer) {
      clearTimeout(this.#columnVisibilityUpdateTimer);
    }
    this.offEvent('scroll.data-grid', this.container);
    this.offEvent('scroll.data-grid.virtual-scroll', this.container);
    this.offEvent('scrollend.data-grid.virtual-scroll', this.container);
    this.offEvent('started.loading-indicator', this.loadingIndicator);
    this.offEvent('stopped.loading-indicator', this.loadingIndicator);

    this.cleanupAllFilterPopups();

    this.filterFields.forEach((element: any) => {
      element.container = undefined;
      const filterPopup: any = this.shadowRoot?.querySelector(`.ids-data-grid-wrapper [target="#${element.id}"]`);
      filterPopup?.detachState();
    });

    this.filterFields = [];

    // Clear all references to DOM elements to prevent memory leaks
    this.#header = undefined;
    this.#editedCellsCache.clear();
    this.openMenu = null;
    this.emptyMessageElements = undefined;
  }

  #filters?: IdsDataGridFilters;

  /**
   * Cleans up all filter popups and removes attached menus
   * @param {boolean} isRedraw - Whether this cleanup is part of a redraw operation. Defaults to false
   * @returns {void}
   */
  cleanupAllFilterPopups(isRedraw: boolean = false) {
    // Clean up tracked popups first
    if (this.#filters) {
      this.#filters.detachAllFilterPopups(isRedraw);
      this.#filters.root = null;
      this.#filters = undefined;
    }

    this.#removeAttachedMenus();
  }

  /** Reference to datasource API */
  datasource: IdsDataSource = new IdsDataSource();

  /**
   * Get the filters instance attached to component
   * @returns {IdsDataGridFilters} the filters instance for this data grid
   */
  get filters() {
    if (!this.#filters) {
      this.#filters = new IdsDataGridFilters(this);
    }
    return this.#filters!;
  }

  /**
   * Get the API for list of formatters
   * @returns {IdsDataGridFormatters} the formatters instance for this data grid
   */
  get formatters() {
    return new IdsDataGridFormatters();
  }

  /**
   * Get the API for list of editors
   * @returns {typeof editors} the editors instance for this data grid
   */
  get editors() {
    return editors;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ADD_NEW_AT_END,
      attributes.ALLOW_ONE_EXPANDED_ROW,
      attributes.ALTERNATE_ROW_SHADING,
      attributes.AUTO_FIT,
      attributes.STICKY_LAYOUT,
      attributes.CELL_COPY_PASTE,
      attributes.DISABLE_HEADER_CHECKBOX,
      attributes.DISABLE_CLIENT_FILTER,
      attributes.DISABLE_ROW_HIGHLIGHT,
      attributes.EMPTY_MESSAGE_DESCRIPTION,
      attributes.EMPTY_MESSAGE_ICON,
      attributes.EMPTY_MESSAGE_LABEL,
      attributes.EDITABLE,
      attributes.EDIT_NEXT_ON_ENTER_PRESS,
      attributes.EXPANDABLE_ROW,
      attributes.EXPANDABLE_ROW_TEMPLATE,
      attributes.FILTER_ROW_DISABLED,
      attributes.FILTER_WHEN_TYPING,
      attributes.FILTERABLE,
      attributes.FIRST_DRAGGABLE_COLUMN,
      attributes.GROUP_SELECTS_CHILDREN,
      attributes.HEADER_MENU_ID,
      attributes.HEADER_OPACITY_VALUE,
      attributes.HEIGHT,
      attributes.HYPERLINK_BEHAVIOR,
      attributes.ID_COLUMN,
      attributes.LABEL,
      attributes.LIST_STYLE,
      attributes.PART,
      attributes.MAX_HEIGHT,
      attributes.MAX_WIDTH,
      attributes.MENU_ID,
      attributes.MIN_HEIGHT,
      attributes.MIN_WIDTH,
      attributes.OVERFLOW,
      attributes.RESET_PAGE_ON_SORT,
      attributes.ROW_HEIGHT,
      attributes.ROW_NAVIGATION,
      attributes.ROW_SELECTION,
      attributes.ROW_START,
      attributes.SCROLL_MAX_ROWS,
      attributes.SHOW_HEADER_EXPANDER,
      attributes.SHOW_SUMMARY_ROW,
      attributes.STRIP_COL_ID,
      attributes.SUPPRESS_CACHING,
      attributes.SUPPRESS_EMPTY_MESSAGE,
      attributes.SUPPRESS_ROW_CLICK_SELECTION,
      attributes.SUPPRESS_ROW_DEACTIVATION,
      attributes.SUPPRESS_ROW_DESELECTION,
      attributes.SUPPRESS_SELECT_ALL_EVENTS,
      attributes.TEXT_SEARCH_TYPE,
      attributes.TREE_GRID,
      attributes.VIRTUAL_SCROLL,
      attributes.UNIQUE_ID,
      attributes.USE_FORMATTERS_FOR_SIZING,
      attributes.WIDTH
    ];
  }

  /**
   * @returns {Array<string>} Drawer vetoable events
   */
  vetoableEventTypes = [
    'beforemenushow',
    'beforetooltipshow',
  ];

  /**
   * Inner template contents
   * @returns {string} The template
   * @private
   */
  template() {
    let cssClasses = `${this.alternateRowShading ? ' alt-row-shading' : ''}`;
    cssClasses += `${this.listStyle ? ' is-list-style' : ''}`;
    cssClasses += ' waiting-load';

    const emptyMesageTemplate = emptyMessageTemplate.apply(this);

    const innerTemplate = (!this.data?.length || !this.columns?.length)
      || (this?.data?.length === 0 && this?.columns?.length === 0)
      ? ''
      : `${IdsDataGridHeader.template(this)}${this.bodyTemplate()}`;
    const html = `<div class="ids-data-grid-wrapper">
        <span class="ids-data-grid-sort-arrows"></span>
        <div class="ids-data-grid${cssClasses}" role="table" part="table" aria-label="${this.label}" data-row-height="${this.rowHeight}">
        ${innerTemplate}
        </div>
        ${emptyMesageTemplate}
        <slot name="menu-container"></slot>
        <slot name="contextmenu"></slot>
        <slot name="header-contextmenu"></slot>
        <slot name="tooltip">
          <ids-tooltip id="tooltip" exportparts="tooltip-popup, tooltip-arrow"></ids-tooltip>
        </slot>
        <slot name="loading-container">
          <ids-loading-indicator stopped class="ids-data-grid-loading"></ids-loading-indicator>
        </slot>
        <slot name="modal-container">
        </slot>
      </div>`;

    return html;
  }

  /**
   * Collapse all expandable or tree rows.
   * @param {boolean} triggerAllRowsEvent If true, will trigger the event once for all rows
   * @param {boolean} triggerRowEvent If true, will trigger row event for each row
   * @returns {void}
   */
  collapseAll(triggerAllRowsEvent = true, triggerRowEvent = false) {
    this.header?.closeExpanderIcons();

    const rows: any[] = [];
    this.rows
      .forEach((row: IdsDataGridRow) => {
        const rowIndex = row.rowIndex;
        rows.push({ row: rowIndex, data: this.data[rowIndex] });
        if (row.isExpanded() && triggerRowEvent) {
          this.triggerEvent(`rowcollapsed`, this, {
            bubbles: true,
            detail: {
              elem: this,
              row: rowIndex,
              data: this.data[rowIndex],
            }
          });
        }
        row.doCollapse();
      });

    this.data.forEach((rowData, rowIndex) => {
      this.updateDataset(rowIndex, { rowExpanded: false, rowHidden: !!rowData.parentElement });
    });

    if (triggerAllRowsEvent) {
      this.triggerEvent(`rowcollapsed`, this, {
        bubbles: true,
        detail: { elem: this, rows, allRows: true }
      });
    }

    // Fix virtual scroll positioning for tree grids
    if (this.treeGrid && this.virtualScroll) {
      this.container!.scrollTop = 0;
      this.body?.style.setProperty('padding-bottom', '0px');
      this.#recycleAllTreeRows(0);
    }
  }

  /**
   * Expand all expandable or tree rows.
   * @returns {void}
   */
  expandAll() {
    this.data.forEach((rowData, rowIndex) => {
      this.updateDataset(rowIndex, { rowExpanded: true, rowHidden: false });
    });

    this.header?.openExpanderIcons();

    const rows: any[] = [];
    this.rows
      .forEach((row: IdsDataGridRow) => {
        const rowIndex = row.rowIndex;
        rows.push({ row: rowIndex, data: this.data[rowIndex] });
        row.doExpand();
      });

    this.triggerEvent(`rowexpanded`, this, {
      bubbles: true,
      detail: { elem: this, rows, allRows: true }
    });

    if (this.treeGrid && this.virtualScroll) {
      const currentScrollIndex = Math.floor(this.container!.scrollTop / this.virtualScrollSettings.ROW_HEIGHT);
      this.#recycleAllTreeRows(currentScrollIndex);
    }
  }

  /**
   * Toggle the empty message.
   * @param {boolean | number} hide If true, will hide empty message.
   * @returns {void}
   */
  toggleEmptyMessage(hide?: boolean | number): void {
    IdsDataGridToggleEmptyMessage.apply(this, [hide]);
  }

  /**
   * Apply the Filter with the currently selected conditions, or the ones passed in.
   * Empty conditions will rerun the current set of conditions. Passing empth array will clear the filter.
   * @param {Array} conditions An array of objects with the filter conditions.
   * @returns {void}
   */
  applyFilter(conditions?: Array<IdsDataGridFilterConditions>) {
    this.filters?.applyFilter(conditions);
    this.calculateRowspan();
  }

  /**
   * Clears the current filter by applying an empty array of filter conditions.
   * @returns {void}
   */
  clearFilter() {
    this?.applyFilter([]);
  }

  /**
   * Get the current filter conditions applied to the data grid
   * @returns {Array<IdsDataGridFilterConditions>} the current filter conditions
   */
  get filterConditions(): Array<IdsDataGridFilterConditions> {
    return this.filters?.filterConditions() ?? [];
  }

  /**
   * Sync and then redraw the body section
   * @private
   * @returns {void}
   */
  redrawBody() {
    this.calculateRowspan();
    this.#redrawBodyTemplate();

    // Skip pager sync for server-side pagination to preserve page size
    if (this.pagination !== 'server-side') {
      this.pager?.sync?.apply(this);
    }

    if (this.virtualScroll) {
      this.#detachScrollEvents();
      const rowStart = (!this.#rowStartApplied && this.rowStart) || 0;
      const virtualScrollSettings = this.virtualScrollSettings;
      const scrollTop = this.container?.scrollTop || 0;
      const currentScrollRow = Math.floor(scrollTop / virtualScrollSettings.ROW_HEIGHT);
      const dataLength = this.data.length;
      if (currentScrollRow > 0 && dataLength > virtualScrollSettings.MAX_ROWS_IN_DOM) {
        const startIndex = this.#calculateVirtualScrollStartIndex();
        const bodyTranslateY = startIndex * virtualScrollSettings.ROW_HEIGHT;
        this.body?.style.setProperty('transform', `translateY(${bodyTranslateY}px)`);
        this.#handleVirtualScroll(virtualScrollSettings.ROW_HEIGHT);
      } else if (rowStart && dataLength > virtualScrollSettings.MAX_ROWS_IN_DOM) {
        const startIndex = this.#calculateVirtualScrollStartIndex();
        const bodyTranslateY = startIndex * virtualScrollSettings.ROW_HEIGHT;
        this.body?.style.setProperty('transform', `translateY(${bodyTranslateY}px)`);
        this.container!.scrollTop = rowStart * virtualScrollSettings.ROW_HEIGHT;
        this.#rowStartApplied = true;
      } else {
        this.body?.style.setProperty('transform', `translateY(0px)`);
        this.#handleVirtualScroll(virtualScrollSettings.ROW_HEIGHT);
      }
      requestAnimationFrame(() => this.#attachScrollEvents());
    }

    this.refreshLeftFrozenHeaderClasses();
    this.updateSelectedCells();

    if (this.#pendingFocusFirstCell) {
      this.#pendingFocusFirstCell = false;
      requestAnimationFrame(() => {
        const firstCell = this.rowByIndex(0)?.cellByIndex(0);
        firstCell?.activate(false, 'keyboard');
      });
    }

    if (this.activeCell?.node && !this.activeCell.node.isConnected) {
      const cellIdx = this.activeCell.cell ?? 0;
      const rowIdx = this.activeCell.row ?? 0;
      const freshCell = this.rowByIndex(rowIdx)?.cellByIndex(cellIdx) ?? null;
      this.activeCell.node = freshCell;

      if (this.hasAttribute('tabindex')) {
        const filterFocused = !!this.shadowRoot?.activeElement?.closest('.ids-data-grid-header-cell-filter-wrapper');
        requestAnimationFrame(() => {
          freshCell?.activate(filterFocused, 'keyboard');
        });
      }
    }
  }

  /**
   * Redraw the body area
   * @private
   * @returns {void}
   */
  #redrawBodyTemplate() {
    if ((this.columns.length === 0 && this.data.length === 0) || !this.initialized) {
      return;
    }

    if (this.body) {
      this.body!.innerHTML = this.bodyInnerTemplate();
    }
    this.#resetLastSelectedRow();
    this.#resetInitiallySelectedRow();
    this.header?.setHeaderCheckbox();

    // Redraw summary row
    this.#redrawSummaryRow();

    // If the grid is empty, set the empty message
    if (this.body?.innerHTML === '') {
      this.#initVirtualScrollHeight();
      setEmptyMessage.apply(this);
    } else if (!this.emptyMessageElements?.em?.hasAttribute(attributes.HIDDEN)) {
      hideEmptyMessage.apply(this);
    }
  }

  /**
   * Redraws the summary footer row
   * @private
   * @returns {void}
   */
  #redrawSummaryRow(): void {
    const existing = this.body?.querySelectorAll('.summary-row');
    existing?.forEach((row) => row.remove());

    const summaryHtml = this.summaryRowTemplate();
    if (summaryHtml && this.body) {
      this.body.insertAdjacentHTML('beforeend', summaryHtml);
    }
  }

  /**
   * Handle keyboard navigation within the summary row
   * @private
   * @param {KeyboardEvent} e The keyboard event
   * @param {HTMLElement} currentCell The currently focused summary cell
   */
  #handleSummaryRowKeydown(e: KeyboardEvent, currentCell: HTMLElement): void {
    const cells = Array.from(this.body?.querySelectorAll<HTMLElement>('.summary-cell') ?? []);
    const idx = cells.indexOf(currentCell);
    if (idx === -1) return;

    e.preventDefault();
    e.stopPropagation();

    if (e.key === 'ArrowLeft') {
      if (idx > 0) cells[idx - 1].focus();
    } else if (e.key === 'ArrowRight') {
      if (idx < cells.length - 1) cells[idx + 1].focus();
    } else if (e.key === 'ArrowUp') {
      // Navigate back to the last data row at the same column
      const lastRowIndex = this.data.length - 1;
      const cell = this.findCellByLogicalPosition(lastRowIndex, idx);
      if (cell) {
        cell.focus?.();
        this.setActiveCell(idx, lastRowIndex, false, 'keyboard');
      }
    }
  }

  /**
   * Redraw the list by reapplying the template
   * @private
   */
  redraw() {
    if (this.columns.length === 0) {
      return;
    }

    if (this.#isEndingCellEdit) {
      if (this.#pendingRedraw) clearTimeout(this.#pendingRedraw);
      this.#pendingRedraw = setTimeout(() => {
        this.#pendingRedraw = undefined;
        this.redraw();
      });
      return;
    }

    // Cancel any pending column visibility updates
    if (this.#columnVisibilityUpdateTimer) {
      clearTimeout(this.#columnVisibilityUpdateTimer);
      this.#columnVisibilityUpdateTimer = undefined;
    }
    this.#columnsToUpdate.clear();

    // Clean up all filter popups including moved ones
    this.cleanupAllFilterPopups(true);

    this.filterFields.forEach((element: any) => {
      element.container = undefined;
    });

    this.filterFields = [];

    const header = IdsDataGridHeader.template(this);
    const body = this.bodyTemplate();

    this.#filters = new IdsDataGridFilters(this);
    if (this.container) this.container.innerHTML = header + body;

    // Restore columns that were hidden by user through personalization dialog
    this.hiddenAtRuntimeColumns.forEach((element: any) => {
      this.hideColumn(element.id, true);
    });

    this.#setColumnWidths();

    this.#applyAutoFit();
    this.header?.setHeaderCheckbox();
    this.#attachEventHandlers();
    this.#attachKeyboardListeners();
    this.#attachScrollEvents();
    this.setupTooltip();
    setContextmenu.apply(this);

    // Set initial padding-bottom for virtual scroll
    if (this.virtualScroll) {
      this.#initVirtualScrollHeight();
    }

    // Attach post filters setting
    this.filters.attachFilterSettings();

    // Set Counts/Totals
    this.#updateRowCount();

    this.updateSelectedCells();

    // Show/hide empty message
    this.toggleEmptyMessage();

    // Do some things after redraw
    this.afterRedraw();

    if (this.virtualScroll && this.rows?.[0]?.rowIndex > 0 && this.#rowStartApplied) {
      this.scrollRowIntoView(this.rows?.[0]?.rowIndex);
    }
  }

  /** Do some things after redraw */
  afterRedraw() {
    const rowStart = this.rowStart || 0;

    // Handle ready state
    const handleReady = () => {
      this.header?.setIsHeaderExpanderCollapsed?.();
      this.container?.classList.remove('waiting-load');
      this.#checkLastRowBorder();
      this.triggerEvent('afterrendered', this, { bubbles: true, detail: { elem: this } });

      // Fire scrollend if content doesn't overflow so infinite scroll can load more data
      this.#triggerScrollEndIfNotOverflowing();
    };

    if (!rowStart) {
      requestAnimationFrame(() => {
        // Set Focus
        this.setActiveCell(0, 0, true, 'redraw');

        if (this.#pendingFocusFirstCell) {
          this.#pendingFocusFirstCell = false;
          const firstCell = this.rowByIndex(0)?.cellByIndex(0);
          firstCell?.activate(false, 'keyboard');
        }

        handleReady();
      });
    } else {
      requestAnimationFrame(() => {
        this.scrollRowIntoView(rowStart);
        this.#rowStartApplied = true;
        handleReady();
      });
    }
  }

  /**
   * Contextmenu stuff use for info and events
   * @private
   */
  contextmenuInfo: {
    menu?: IdsPopupMenu,
    target?: HTMLElement,
    callbackArgs?: IdsDataGridContextmenuArgs
  } = {};

  /**
   * Track contextmenu data dynamicly changed by the user.
   * @private
   */
  isDynamicContextmenu = false;

  /**
   * Body template markup
   * @private
   * @returns {string} The template
   */
  bodyTemplate() {
    return `<div class="ids-data-grid-body" part="contents" role="rowgroup">${this.bodyInnerTemplate()}${this.summaryRowTemplate()}</div>`;
  }

  /**
   * Summary footer row template markup
   * @returns {string} The summary row template HTML
   */
  summaryRowTemplate(): string {
    if (!this.showSummaryRow || !this.data?.length) return '';

    const summaryColumns = this.visibleColumns;
    const hasAnySummary = summaryColumns.some((col) => col.summaryFunction);
    if (!hasAnySummary) return '';

    const cellsHtml = summaryColumns.map((column, columnIndex) => {
      let summaryContent = '';

      if (column.summaryFunction && column.field) {
        const fieldKey = column.field;
        const values = this.data
          .filter((row: Record<string, unknown>) => !row.isSummaryRow && !row.isGroupRow && !row.isGroupFooterRow)
          .map((row: Record<string, unknown>) => row[fieldKey] as number | string | null | undefined);
        const aggregateValue = calculateAggregate(
          values,
          column.summaryFunction,
          column.summaryCustomFunction
        );

        summaryContent = formatSummaryCellContent({
          fieldKey,
          aggregateValue,
          column,
          formatters: this.formatters as unknown as Record<string, SummaryCellFormatter>,
          grid: this as unknown as Record<string, unknown>,
          escapeHTML
        });
      }

      const alignClass = column.align ? ` align-${column.align}` : '';
      return `
        <div
          role="gridcell"
          part="summary-cell"
          class="ids-data-grid-cell summary-cell${alignClass}"
          aria-colindex="${columnIndex + 1}"
          column-id="${column.id}"
          tabindex="-1"
        >
          ${summaryContent}
        </div>
      `;
    }).join('');

    return `<div role="row" part="summary-row" class="ids-data-grid-row summary-row" aria-label="Summary row">${cellsHtml}</div>`;
  }

  /**
   * Get whether the summary row is shown
   * @returns {boolean} true if summary row is enabled
   */
  get showSummaryRow(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_SUMMARY_ROW));
  }

  /**
   * Set whether to show the summary row
   * @param {boolean | string | null} value the value to set
   */
  set showSummaryRow(value: boolean | string | null) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SHOW_SUMMARY_ROW, '');
    } else {
      this.removeAttribute(attributes.SHOW_SUMMARY_ROW);
    }
    this.redraw();
  }

  /**
   * Expose summary row utility functions for use by sub-components
   * @returns {{ calculateAggregate: typeof calculateAggregate, escapeHTML: typeof escapeHTML }} the summary utilities
   */
  get summaryRowUtils(): { calculateAggregate: typeof calculateAggregate; escapeHTML: typeof escapeHTML } {
    return { calculateAggregate, escapeHTML };
  }

  get cache() {
    return {
      row: IdsDataGridRow.rowCache,
      cell: IdsDataGridCell.cellCache,
    };
  }

  /**
   * Simple way to clear cache until a better cache-busting strategy is in implemented
   * @param {number|undefined} rowIndex - (optional) row-index to target specific rowCache to clear
   * @returns {void}
   */
  resetCache(rowIndex?: number): void {
    if (rowIndex === 0 || (rowIndex && rowIndex >= 1)) {
      // Clear specific row cache entries — safe to delete during Map.forEach
      IdsDataGridRow.rowCache.forEach((_, key) => {
        if (key.includes(`:${rowIndex}:`)) {
          IdsDataGridRow.rowCache.delete(key);
        }
      });
      // return; // TODO: returning currently breaks cell-editor tests... must fix
    }

    IdsDataGridRow.rowCache.clear();
    IdsDataGridCell.cellCache.clear();

    this.#customScrollEventCache = {};
    this.#header = undefined;
  }

  /**
   * Calculate the virtual scroll start index for non-tree grids.
   * Prefers the current scroll position, falls back to `rowStart` on first render.
   * @private
   * @returns {number} The first row index to render in the virtual scroll window
   */
  #calculateVirtualScrollStartIndex(): number {
    const virtualScrollSettings = this.virtualScrollSettings;
    if (this.data.length <= virtualScrollSettings.MAX_ROWS_IN_DOM) return 0;

    const rowStart = (!this.#rowStartApplied && this.rowStart) || 0;
    const scrollTop = this.container?.scrollTop || 0;
    const currentScrollRow = Math.floor(scrollTop / virtualScrollSettings.ROW_HEIGHT);

    if (currentScrollRow > 0) {
      return Math.max(0, currentScrollRow - virtualScrollSettings.BUFFER_ROWS);
    }
    if (rowStart) {
      return Math.max(0, rowStart - virtualScrollSettings.BUFFER_ROWS);
    }
    return 0;
  }

  /**
   * Body inner template markup
   * @private
   * @returns {string} The template
   */
  bodyInnerTemplate() {
    this.resetCache();

    let innerHTML = '';
    let data = this.data;
    const virtualScrollSettings = this.virtualScrollSettings;

    if (this.treeGrid && this.virtualScroll) {
      const visibleRows = this.virtualRows.slice(0, virtualScrollSettings.MAX_ROWS_IN_DOM);

      for (let i = 0; i < visibleRows.length; i++) {
        const dataIndex = visibleRows[i].vsRefId;
        innerHTML += IdsDataGridRow.template(data[dataIndex], dataIndex, dataIndex + 1, this);
      }

      return innerHTML;
    }

    if (this.virtualScroll && !this.treeGrid) {
      const startIndex = this.#calculateVirtualScrollStartIndex();
      data = data.slice(startIndex, startIndex + virtualScrollSettings.MAX_ROWS_IN_DOM);

      innerHTML += data.map((row, index) => {
        const dataIndex = startIndex + index;
        return IdsDataGridRow.template(row, dataIndex, dataIndex + 1, this);
      }).join('');

      return innerHTML;
    }

    for (let index = 0; index < data.length; index++) {
      innerHTML += IdsDataGridRow.template(data[index], index, index + 1, this);
    }

    return innerHTML;
  }

  /**
   * Check if row is selected.
   * @param {number} index The row index
   * @returns {boolean} True, if row is selected
   */
  rowIsSelected(index: number): boolean {
    if (this.pagination === 'server-side') {
      const record = this.data[index];
      const storedIndex = this.serverSideSelections.findIndex((item) => item.data[this.idColumn] === record[this.idColumn]);
      return storedIndex > -1;
    }

    return !!this.data[index].rowSelected;
  }

  /**
   * Keep flag for last selected row
   * @private
   */
  #lastSelectedRow: number | null = null;

  /**
   * Keep flag for initially selected row
   * @private
   */
  #initiallySelectedRow: number | null = null;

  /**
   * Keep reference to last shifted row
   * @private
   */
  #lastShiftedRow: number | null = null;

  /**
   * Keep reference to previous last row with no-border-bottom class
   * @private
   */
  #previousLastRow: IdsDataGridRow | null = null;

  /**
   * Flag to prevent multiple concurrent async row appends
   * @private
   */
  #pendingRowAppend = false;

  /** Tracks the row index currently showing the active row indicator icon */
  #activeIndicatorRow: number = -1;

  /**
   * Flag to focus the first cell after the next render (when host received focus before data loaded)
   * @private
   */
  #pendingFocusFirstCell = false;

  /** Tracks whether rowStart has been applied (consumed on first render) */
  #rowStartApplied = false;

  /** Tracks whether focus is currently inside the grid boundary */
  #hasBoundaryFocus = false;

  /**
   * Reset flag for last selected row
   * @private
   * @returns {void}
   */
  #resetLastSelectedRow(): void {
    this.#lastSelectedRow = null;
  }

  /**
   * Reset flag for last selected row
   * @private
   * @returns {void}
   */
  #resetInitiallySelectedRow(): void {
    this.#initiallySelectedRow = null;
  }

  /**
   * Reset reference to last shifted row
   * @private
   */
  #resetLastShiftedRow(): void {
    this.#lastShiftedRow = null;
  }

  /**
   * Check and apply styling to the last row border
   * @private
   */
  #checkLastRowBorder(): void {
    const allRows = this.rows;
    if (!allRows?.length || !this.container) return;

    // Remove class from previous last row
    if (this.#previousLastRow) {
      this.#previousLastRow.classList.remove('no-border-bottom');
    }

    const lastRow = allRows[allRows.length - 1];
    const firstCell = lastRow.querySelector<IdsDataGridCell>('ids-data-grid-cell');

    if (!firstCell) return;

    const containerBottom = this.container.offsetHeight;
    const cellBottom = firstCell.offsetTop + firstCell.offsetHeight - this.container.scrollTop;
    const bottomDiff = Math.abs(containerBottom - cellBottom);

    if (bottomDiff <= 1 && containerBottom > 0) {
      lastRow.classList.add('no-border-bottom');
      this.#previousLastRow = lastRow;
    } else {
      this.#previousLastRow = null;
    }
  }

  /**
   * Set the state of a row by applying a CSS part attribute
   * @param {number} rowIdx - The index of the row to update
   * @param {string} stateName - The state name to apply as a CSS part
   * @param {object} settings Additional settings for row state
   * @param {boolean} settings.suppressFieldIndicator Suppress the field indicator for row state
   */
  setRowState(rowIdx: number, stateName: string, settings?: {
    suppressFieldIndicator?: boolean;
  }): void {
    const row = this.rowByIndex(rowIdx);

    if (row && (this.VALID_ROW_STATES as readonly string[]).includes(stateName)) {
      row.setAttribute(attributes.PART, stateName);
      this.updateDataset(rowIdx, { rowState: stateName });

      if (settings?.suppressFieldIndicator) {
        row.classList.add('no-field-indicator');
      } else {
        row.classList.remove('no-field-indicator');
      }
    }
  }

  /**
   * Update the row indicator icon in the rowNumber column.
   * Updates the dataset for the given row and refreshes only the affected cell.
   * @param {number} rowIdx The zero-based index of the row to update
   * @param {RowIndicatorSettings} indicator Settings object with `rowIndicatorType`
   *   (a `RowIndicatorTypes` value or empty string to clear) and `activeCell` (boolean for active row icon)
   */
  updateRowIndicator(rowIdx: number, indicator: RowIndicatorSettings): void {
    if (rowIdx === null || rowIdx === undefined) return;

    const colIdx = this.columns.findIndex((col) => col.id === 'rowNumber');
    if (colIdx === -1) return;

    const vColIdx = this.visibleColumns.findIndex((col) => col.id === 'rowNumber');

    const field = this.columns[colIdx]?.icon;
    if (!field || typeof field !== 'string') return;

    if (indicator.rowIndicatorType !== undefined) {
      this.updateDataset(rowIdx, { [field]: indicator.rowIndicatorType });
    }

    if (indicator.activeCell !== undefined) {
      this.data[rowIdx].isActiveRow = indicator.activeCell;
    }

    this.rowByIndex(rowIdx)?.cellByIndex(vColIdx === -1 ? colIdx : vColIdx)?.refreshCell();

    if (indicator.activeCell) {
      this.#activeIndicatorRow = rowIdx;
    } else if (this.#activeIndicatorRow === rowIdx && this.#activeCellCache?.rowIndex !== rowIdx) {
      this.#activeIndicatorRow = -1;
    }
  }

  /**
   * Set the active row indicator (current icon) on the given row,
   * clearing it from the previous active row.
   * @param {number} rowIdx The zero-based row index to mark as active, or -1 to clear all
   */
  setActiveRowIndicator(rowIdx: number): void {
    const columnSettings = this.columns.find((col) => col?.rowIndicatorConfig?.activeRowIndicator);
    if (!columnSettings) return;

    if (this.#activeIndicatorRow !== -1 && this.#activeIndicatorRow !== rowIdx) {
      this.updateRowIndicator(this.#activeIndicatorRow, { activeCell: false });
    }

    if (rowIdx >= 0 && rowIdx < this.data.length) {
      this.updateRowIndicator(rowIdx, { activeCell: true });
    }
  }

  /**
   * Handles shift+click selection behavior to select rows between initial anchor and current row
   * @private
   * @param {number} index The row index being shift+clicked
   * @returns {void}
   */
  #toggleShiftSelectionInBetween(index: number): void {
    if (this.#lastSelectedRow === null) return;
    if (this.#initiallySelectedRow === null) {
      this.selectRow(index);
      return;
    }

    const start = Math.min(index, this.#lastSelectedRow);
    const end = Math.max(index, this.#lastSelectedRow);
    const toSelectStart = Math.min(index, this.#initiallySelectedRow);
    const toSelectEnd = Math.max(index, this.#initiallySelectedRow);
    for (let i = start; i <= end; i++) {
      if (toSelectStart > i || i > toSelectEnd) this.deSelectRow(i);
      else this.selectRow(i);
    }

    // Update lastSelectedRow to current index for next shift-selection
    this.#lastSelectedRow = index;

    this.#getSelection()?.removeAllRanges?.();
    this.header?.setHeaderCheckbox();

    this.triggerEvent('selectionchanged', this, {
      detail: { elem: this, selectedRows: this.selectedRows }
    });
  }

  /**
   * Get current selection
   * @private
   * @returns {Selection|null} The selection
   */
  #getSelection(): Selection | null {
    if (!(this.shadowRoot as any)?.getSelection) {
      return document.getSelection();
    }
    return (this.shadowRoot as any)?.getSelection();
  }

  /**
   * Handle all triggering and handling of events
   * @private
   */
  #attachEventHandlers() {
    // Add a cell click handler
    const body = this.body;

    // Recalculate the summary row when an edit changes a summarized column's value
    this.offEvent('endcelledit.summary-row', this);
    this.onEvent('endcelledit.summary-row', this, (e: CustomEvent) => {
      if (!this.showSummaryRow) return;
      const column = e.detail?.column as IdsDataGridColumn | undefined;
      if (column?.summaryFunction && e.detail?.oldValue !== e.detail?.newValue) {
        this.#redrawSummaryRow();
      }
    });

    // When the host receives focus (e.g., via Tab in a modal), delegate to the first data cell.
    // Active when the host has tabindex (set by lookup or other containers after connectedCallback).
    this.offEvent('focus.data-grid-host', this);
    this.onEvent('focus.data-grid-host', this, () => {
      if (!this.hasAttribute('tabindex')) return;
      if (this.shadowRoot?.activeElement?.closest('.ids-data-grid-header-cell-filter-wrapper')) return;
      if (this.activeCell?.node?.isConnected) {
        this.activeCell.node.activate(false, 'keyboard');
      } else {
        const firstRow = this.rowByIndex(0);
        const firstCell = firstRow?.cellByIndex(0);
        if (firstCell) {
          firstCell.activate(false, 'keyboard');
        } else {
          // Data not loaded yet; focus the first cell after the next render
          this.#pendingFocusFirstCell = true;
        }
      }
    });

    // Toggle host tabindex to prevent Shift+Tab loops between host and child.
    this.offEvent('focusin.data-grid-tabindex', this);
    this.onEvent('focusin.data-grid-tabindex', this, (e: FocusEvent) => {
      if (e.target !== this && this.getAttribute('tabindex') === '0') {
        this.setAttribute('tabindex', '-1');
      }
    });

    this.offEvent('focusout.data-grid-tabindex', this);
    this.onEvent('focusout.data-grid-tabindex', this, () => {
      requestAnimationFrame(() => {
        if (!this.hasAttribute('tabindex')) return;
        const stillInGrid = this.shadowRoot?.activeElement
          || this.contains(document.activeElement);
        if (!stillInGrid && this.getAttribute('tabindex') === '-1') {
          this.setAttribute('tabindex', '0');
        }
      });
    });

    // Focus boundary events: fire once when focus enters/leaves the grid
    this.offEvent('focusin.data-grid-boundary', this);
    this.onEvent('focusin.data-grid-boundary', this, () => {
      if (!this.#hasBoundaryFocus) {
        this.#hasBoundaryFocus = true;
        this.triggerEvent('focusenter', this, { bubbles: true, composed: true, detail: { elem: this } });
      }
    });

    this.offEvent('focusout.data-grid-boundary', this);
    this.onEvent('focusout.data-grid-boundary', this, () => {
      requestAnimationFrame(() => {
        const stillInGrid = this.shadowRoot?.activeElement
          || this.contains(document.activeElement);
        if (!stillInGrid && this.#hasBoundaryFocus) {
          this.#hasBoundaryFocus = false;
          this.triggerEvent('focusleave', this, { bubbles: true, composed: true, detail: { elem: this } });
        }
      });
    });

    if (this.cellSelection) {
      this.#attachDragSelection(body);
    } else {
      this.offEvent('mousedown.body', body);
      this.onEvent('mousedown.body', body, (e: any) => {
        const cell: IdsDataGridCell = (e.target as any).closest('ids-data-grid-cell');
        const expandableRow = (e.target as any).closest('.ids-data-grid-expandable-row');
        const isDraggableElement = (e.target as HTMLElement)?.draggable || (e.target as HTMLElement)?.closest('[draggable="true"]');

        if (cell !== this.activeCell.node && (typeof cell?.formatter === 'function' && cell?.formatter?.name !== 'rowReorder') && !expandableRow && !isDraggableElement) {
          e.preventDefault();
        }
      });
    }

    this.offEvent('click.body', body);
    this.onEvent('click.body', body, (e: any) => {
      let cell: IdsDataGridCell = (e.target as any).closest('ids-data-grid-cell');

      // Summary footer cells are not interactive data cells; ignore click handling
      if ((e.target as HTMLElement)?.closest?.('.summary-row')) return;

      if (!cell && this.rowNavigation) {
        const row = e.target.closest('ids-data-grid-row');
        if (row) {
          const rect = row.getBoundingClientRect();
          const x = e.clientX - rect.left;

          let accumulatedWidth = 0;
          for (let i = 0; i < row.children.length; i++) {
            const cellElem = row.children[i];
            const cellWidth = cellElem.getBoundingClientRect().width;
            accumulatedWidth += cellWidth;

            if (x <= accumulatedWidth) {
              cell = cellElem;
              break;
            }
          }

          if (!cell && row.children.length > 0) {
            cell = row.children[0];
          }
        }
      }

      if (!cell) return;

      if (cell?.isEditing) {
        if (cell?.tabIndex >= 0) {
          return;
        }
        // Don't end inline editors on click within the same cell
        if (cell?.column?.editor?.inline) {
          return;
        }
        cell?.endCellEdit();
      }

      if (this.cellSelection) {
        this.deSelectAllRows();
      }

      const cellNum = Number(cell.getAttribute(htmlAttributes.ARIA_COLINDEX)) - 1;
      const row = <IdsDataGridRow>cell.parentNode;
      if (row.disabled) return;

      const rowNum = row.rowIndex;

      if (!e.shiftKey) this.#resetLastShiftedRow();

      const isHyperlink = e.target?.nodeName === 'IDS-HYPERLINK' || e.target?.nodeName === 'A';
      const isHyperlinkAnchor = e.target?.nodeName === 'A';
      const isButton = e.target?.nodeName === 'IDS-BUTTON';
      const isExpandButton = isButton && e.target?.classList.contains('expand-button');
      const isClickable = isButton || isHyperlink;
      const column: IdsDataGridColumn = this.visibleColumns[cellNum];

      if (typeof column.formatter === 'function' && column.formatter?.name && e?.target?.classList?.contains('text-mask')) {
        const formatterFn = new IdsDataGridFormatters();
        const dataIndex = row.getAttribute('data-index');

        if (dataIndex === null) {
          return;
        }

        const cellData = this.data[+dataIndex][column.id];
        formatterFn.textmaskAction(cellData, cell, column);
        this.tooltip.detachAllEvents();
        e.preventDefault();
        return;
      }

      // Handle hyperlinks in dropdown columns - only when actually clicking the hyperlink element
      if (isHyperlink && column.editor?.type === 'dropdown' && !e.ctrlKey && !e.shiftKey
        && (e.target?.nodeName === 'IDS-HYPERLINK' || e.target?.closest('ids-hyperlink'))) {
        // For normal hyperlink clicks, let the hyperlink handle it and prevent cell editing
        if (column.click) {
          column.click(this.data[rowNum], this.visibleColumns[cellNum], e);
        }
        return;
      }

      // Focus cell for all other cases (including clicking cell area outside hyperlink)
      this.setActiveCell(cellNum, rowNum, isHyperlinkAnchor, 'click');

      // Handle click callbacks
      if (isClickable && column.click !== undefined && !e.target?.getAttribute('disabled')) {
        column.click(this.data[rowNum], this.visibleColumns[cellNum], e);
      }

      // hide menus on body click
      this.hideOpenMenus();

      // Handle popup menus
      if (isClickable && column?.menuId !== undefined && !e.target?.getAttribute('disabled')) {
        const menuEl = document.querySelector<IdsPopupMenu>(`#${column.menuId}`);
        if (!menuEl) return;
        menuEl.target = null;
        menuEl.hide();
        menuEl.triggerType = 'immediate-closed';
        menuEl.target = e.target;
        menuEl.align = 'bottom, left';
        menuEl.arrow = 'bottom';
        menuEl.triggerType = 'immediate';
        menuEl.offEvent('selected', menuEl);
        menuEl.onEvent('selected', menuEl, (evt: CustomEvent) => {
          column?.selected?.(this.data[rowNum], this.visibleColumns[cellNum], evt);
        });
      }

      // Fires for each row that is clicked
      this.triggerEvent('rowclick', this, {
        detail: {
          elem: this, row, data: this.data[rowNum]
        }
      });

      // Handle row selection
      const handleSingleSelection = () => {
        const isLink = e.target?.nodeName === 'IDS-HYPERLINK' || e.target?.nodeName === 'A';
        const behavior = isHyperlink ? column?.hyperlinkBehavior : null;

        // Early return for hyperlink behaviors that prevent action
        if (behavior === 'none') return;
        if (isLink) {
          switch (behavior) {
            case 'default':
              this.selectRow(rowNum, true);
              break;
            case 'no-deselect':
              this.selectRow(rowNum, true);
              break;
            case 'no-select':
              this.deSelectRow(rowNum);
              break;
            default:
              this.selectRow(rowNum, true);
              break;
          }
        } else {
          row.toggleSelection();
        }
      };

      // Handle multiple or mixed selection
      const handleMultipleOrMixedSelection = () => {
        if (['multiple', 'mixed'].includes(String(this.rowSelection))) {
          const isLink = e?.target?.nodeName === 'IDS-HYPERLINK' || e?.target?.nodeName === 'A';
          const behavior = isLink ? column?.hyperlinkBehavior : null;

          // Early return for hyperlink behaviors that prevent action
          if (behavior === 'none') return;

          if (e.shiftKey && this.#lastSelectedRow !== null) {
            if (behavior === 'no-select') {
              this.deSelectRow(rowNum);
            } else if (behavior === 'no-deselect') {
              // For no-deselect, we still want the multi-select behavior
              this.#toggleShiftSelectionInBetween(rowNum);
              // Ensure the clicked row stays selected
              this.selectRow(rowNum);
            } else {
              this.#toggleShiftSelectionInBetween(rowNum);
            }
          } else if (isLink) {
            this.#resetInitiallySelectedRow();
            switch (behavior) {
              case 'default':
                row.toggleSelection();
                break;
              case 'no-deselect':
                this.selectRow(rowNum);
                break;
              case 'no-select':
                this.deSelectRow(rowNum);
                break;
              default:
                row.toggleSelection();
                break;
            }
          } else {
            this.#resetInitiallySelectedRow();
            row.toggleSelection();
          }
          this.#lastSelectedRow = rowNum;

          if (this.#initiallySelectedRow === null) {
            this.#initiallySelectedRow = rowNum;
          } else if (!row.selected && this.#initiallySelectedRow === rowNum) {
            this.#resetInitiallySelectedRow();
          }
        } else if (this.rowSelection === 'single') {
          handleSingleSelection();
        }
      };

      // Handle Expand/Collapse Clicking
      if (isClickable && isExpandButton && !this.groupable) {
        if (this.allowOneExpandedRow) {
          const isExpanded = row.isExpanded();
          const isCollapsed = !isExpanded;
          if (isCollapsed) {
            this.collapseAll(false, true);
          }
          row.toggleExpandCollapse();
        } else {
          row.toggleExpandCollapse();
        }
        return;
      }

      if (isClickable && isExpandButton && this.groupable && row.classList.contains('is-group-row')) {
        row.toggleGroupChildRows();
      }

      // Handle mixed selection
      if (this.rowSelection === 'mixed') {
        if (cell.children[0]?.classList.contains('ids-data-grid-checkbox-container')) {
          handleMultipleOrMixedSelection();
        } else {
          row.toggleRowActivation();
        }
      }

      // Handle selection if not disabled
      if (this.rowSelection !== false && this.rowSelection !== 'mixed') {
        if ((!this.suppressRowClickSelection) || (
          this.suppressRowClickSelection
          && (cell.children[0]?.classList.contains('is-selection-checkbox') || cell.children[0]?.classList.contains('is-selection-radio'))
        )) handleMultipleOrMixedSelection();
      }

      // Handle Editing
      if (this.activeCellEditor && this.activeCellEditor !== cell && this.activeCellEditor.column?.editor?.inline) {
        this.activeCellEditor.endCellEdit();
      }
      if ((this.editable || column.editable) && column.editor) {
        cell.startCellEdit(e);
        this.updateSelectionBorder(true);
      }
    });

    // Add double click to the container
    this.offEvent('dblclick.container', this.container);
    this.onEvent('dblclick.container', this.container, (e: MouseEvent) => {
      // do not fire for disabled cells
      if ((e.target as HTMLElement).classList?.contains('ids-data-grid-checkbox-container')
        && (e.target as HTMLElement).getAttribute('disabled') === 'true') {
        e.stopPropagation();
        return;
      }

      e.stopPropagation();
      const eventDetail = {
        ...containerArguments.apply(this, [e]),
        originalEvent: e
      };

      this.triggerEvent('dblclick', this, {
        bubbles: true,
        detail: eventDetail
      });

      // Trigger cell-specific double-click event
      const cell = (e.target as HTMLElement).closest('ids-data-grid-cell');
      if (cell) {
        this.triggerEvent('celldblclick', this, {
          bubbles: true,
          detail: {
            ...eventDetail,
            cell
          }
        });
      }
    });

    // Row reorder handling
    this.onEvent('dragstart.body', this.body, (e) => {
      const target = e.target as HTMLElement;
      const cell = target instanceof Element ? target.closest('ids-data-grid-cell') : null;
      const isDraggableElement = target.draggable || (target instanceof Element && target.closest('[draggable="true"]'));

      // Allow draggable elements (like text) to work, but prevent other drags except row reorder
      if (!cell?.classList.contains('formatter-row-reorder') && !isDraggableElement) {
        e.preventDefault();
      }

      // If it's a draggabrle element but not row eorder, allow the drag but don't handle row reorder logic
      if (isDraggableElement && !cell?.classList.contains('formatter-row-reorder')) {
        return;
      }

      const row = cell?.closest('ids-data-grid-row') as HTMLElement;
      if (row && e.dataTransfer && cell) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.dropEffect = 'move';
        e.dataTransfer.setData('text/plain', row.getAttribute('row-index') || '0');
        const dragContainer = document.createElement('ids-data-grid-row');
        dragContainer.classList.add('row-drag-container');
        dragContainer.style.height = `${this.rowPixelHeight}px`;
        dragContainer.style.width = `${window.innerWidth - cell.getBoundingClientRect().left - 32}px`;

        const cells = [cell];
        let nextCell = cell.nextElementSibling as HTMLElement;
        for (let i = 0; i < this.visibleColumns.length && nextCell; i++) {
          cells.push(nextCell);
          nextCell = nextCell.nextElementSibling as HTMLElement;
        }

        cells.forEach((c) => {
          const cellClone = document.createElement('div') as any;
          cellClone.innerHTML = c.innerHTML.replace(/\sstyle="[^"]*"/g, '');
          cellClone.className = c.className;
          dragContainer.appendChild(cellClone);
        });

        this.container?.appendChild(dragContainer);
        e.dataTransfer.setDragImage(dragContainer, this.rowPixelHeight / 2, cell.getBoundingClientRect().width / 2);
        requestAnimationFrame(() => dragContainer.remove());
      }
    });

    this.onEvent('dragover.body', this.body, (e: DragEvent) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'move';
    });

    this.onEvent('drop.body', this.body, (e: DragEvent) => {
      e.preventDefault();
      const draggedRowIndex = Number(e.dataTransfer?.getData('text/plain'));
      const targetRow = (e.target as HTMLElement).closest('ids-data-grid-row') as any;

      if (!targetRow || Number.isNaN(draggedRowIndex)) return;

      const targetRowIndex = targetRow.rowIndex;
      if (draggedRowIndex === targetRowIndex) return;

      this.#reorderRows(draggedRowIndex, targetRowIndex, { row: targetRow });
    });

    let touchRowIndex: number | null = null;

    this.onEvent('touchstart.body', this.body, (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const cell = target.closest('ids-data-grid-cell');
      const row = target.closest('ids-data-grid-row') as HTMLElement;
      const rowIndexAttr = row?.getAttribute(attributes.ROW_INDEX);

      touchRowIndex = cell?.classList.contains('formatter-row-reorder') && rowIndexAttr
        ? parseInt(rowIndexAttr, 10) : null;
    }, { passive: true });

    this.onEvent('touchmove.body', this.body, (e: TouchEvent) => {
      if (touchRowIndex !== null) e.preventDefault();
    }, { passive: false });

    this.onEvent('touchend.body', this.body, (e: TouchEvent) => {
      if (touchRowIndex === null) return;

      const touch = e.changedTouches[0];
      let targetRow = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('ids-data-grid-row') as any;

      if (!targetRow) {
        const bodyTop = this.body?.getBoundingClientRect()?.top || 0;
        const targetRowIndex = Math.floor((touch.clientY - bodyTop) / this.rowPixelHeight);
        if (targetRowIndex >= 0) targetRow = this.rowByIndex(targetRowIndex);
      }

      if (targetRow) {
        const targetRowIndex = parseInt(targetRow.getAttribute('row-index') || '0');
        if (targetRowIndex !== touchRowIndex) {
          this.#reorderRows(touchRowIndex, targetRowIndex, { row: targetRow });
        }
      }

      touchRowIndex = null;
    }, { passive: true });

    if (this.cellCopyPaste) this.#attachCopyPasteCellHandlers();

    this.filters?.attachFilterEventHandlers();
    this.attachSaveSettingsEventHandlers?.();
  }

  /**
   * Reorders rows in the data grid by moving a row from its current index to a target index.
   * Updates the underlying data source and triggers a 'rowreorder' event with details about the operation.
   * @param {number} draggedRowIndex - The index of the row being dragged.
   * @param {number} targetRowIndex - The index where the dragged row should be inserted.
   * @param {Record<string, unknown>} eventData - Extra event data
   */
  #reorderRows(draggedRowIndex: number, targetRowIndex: number, eventData: Record<string, unknown>): void {
    if (!this.datasource) return;

    const data = [...this.datasource.currentData];
    const draggedRowData = data[draggedRowIndex];

    if (!draggedRowData) return;

    data.splice(draggedRowIndex, 1);
    const insertIndex = draggedRowIndex < targetRowIndex ? targetRowIndex - 1 : targetRowIndex;
    data.splice(insertIndex, 0, draggedRowData);

    this.data = data;

    const draggedRow = this.rowByIndex(draggedRowIndex)!;
    const targetRow = this.rowByIndex(targetRowIndex);
    targetRow?.insertAdjacentElement('beforebegin', draggedRow);
    requestAnimationFrame(() => this.#checkLastRowBorder());

    this.triggerEvent('rowreorder', this, {
      detail: {
        elem: this,
        row: eventData.row,
        data: draggedRowData,
        draggedRowIndex,
        targetRowIndex
      }
    });
  }

  /**
   * Handle the drag and select behavior
   * @private
   * @param {HTMLElement | null} body Datagrid body
   */
  #attachDragSelection(body?: HTMLElement | null) {
    let isDragging = false;
    let deselectAllRows = true;
    let startCell: { row: number; col: number, cell?: HTMLElement } | null = null;
    let prevBounds: { minRow: number; maxRow: number; minCol: number; maxCol: number } | null = null;
    let selectionBorder: HTMLElement | null = null;

    // Prevent focus on mousedown
    this.offEvent('mousedown.body', body);
    this.onEvent('mousedown.body', body, (e: any) => {
      const cell: IdsDataGridCell = (e.target as any).closest('ids-data-grid-cell');
      const expandableRow = (e.target as any).closest('.ids-data-grid-expandable-row');

      if (cell !== this.activeCell.node && !expandableRow) {
        e.preventDefault();
      }

      if (!cell || cell.classList.contains('formatter-selectionCheckbox') || isDragging) return;

      if (selectionBorder) selectionBorder.style.display = 'none';

      isDragging = true;

      startCell = {
        row: parseInt(cell.parentElement?.getAttribute(attributes.ROW_INDEX) || '0'),
        col: parseInt(cell.getAttribute(htmlAttributes.ARIA_COLINDEX) || '1') - 1,
        cell
      };
    });

    this.offEvent('mousemove.body', body);
    this.onEvent('mousemove.body', body, (e: any) => {
      const cell: IdsDataGridCell = (e.target as any).closest('ids-data-grid-cell');

      if (!isDragging || !startCell || !cell) return;

      if (startCell && startCell.cell !== this.activeCell.node) {
        this.setActiveCell(startCell.col, startCell.row, false, 'mouse');
      }

      if (deselectAllRows) {
        this.deSelectAllRows();
        deselectAllRows = false;
      }

      const currentRow = parseInt(cell.parentElement?.getAttribute(attributes.ROW_INDEX) || '0');
      const currentCol = parseInt(cell.getAttribute(htmlAttributes.ARIA_COLINDEX) || '0') - 1;

      const bounds = {
        minRow: Math.min(startCell.row, currentRow),
        maxRow: Math.max(startCell.row, currentRow),
        minCol: Math.min(startCell.col, currentCol),
        maxCol: Math.max(startCell.col, currentCol)
      };

      const isSelected = (row: number, col: number): boolean => {
        const c = this.cellByIndex(row, col);
        return c?.classList.contains('selected') || c?.parentElement?.classList.contains('selected') || false;
      };

      if (prevBounds) {
        for (let row = prevBounds.minRow; row <= prevBounds.maxRow; row++) {
          for (let col = prevBounds.minCol; col <= prevBounds.maxCol; col++) {
            const isOutside = row < bounds.minRow || row > bounds.maxRow || col < bounds.minCol || col > bounds.maxCol;

            if (isOutside) this.selectCell(row, col, false);
          }
        }
      }

      for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
        for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
          if (!isSelected(row, col)) this.selectCell(row, col, true);
        }
      }

      if (!selectionBorder) {
        selectionBorder = this.#createSelectionBorder();
        this.container?.appendChild(selectionBorder);
      } else {
        selectionBorder.style.display = 'block';
      }

      this.#updateSelectionBorder(startCell.cell || cell, cell);

      prevBounds = bounds;
    });

    this.onEvent('mouseup.body', body, () => {
      isDragging = false;
      deselectAllRows = true;
      prevBounds = null;
      startCell = null;
    });

    this.onEvent('mouseleave', this.container, () => {
      isDragging = false;
      deselectAllRows = true;
      prevBounds = null;
      startCell = null;
    });
  }

  /**
   * Create the selection border when drag and selecting cells
   * @private
   * @returns {HTMLElement} The initial selection border element
   */
  #createSelectionBorder(): HTMLElement {
    const border = document.createElement('div');
    border.classList.add('selection-border');
    return border;
  }

  /**
   * Update the size and position of the selection border as the mouse moves
   * @private
   * @param {HTMLElement} startCell Cell where selection started
   * @param {HTMLElement} endCell Current cell where the mouse is on
   */
  #updateSelectionBorder(startCell: HTMLElement, endCell: HTMLElement) {
    const border = this.container?.querySelector<HTMLElement>('.selection-border');
    if (!border) return;

    const startRect = startCell.getBoundingClientRect();
    const endRect = endCell.getBoundingClientRect();
    const containerRect = this.container?.getBoundingClientRect();

    if (!containerRect) return;

    // Calculate positions relative to container
    const left = Math.min(startRect.left, endRect.left) - containerRect.left;
    const top = Math.min(startRect.top, endRect.top) - containerRect.top;
    const right = Math.max(startRect.right, endRect.right) - containerRect.left;
    const bottom = Math.max(startRect.bottom, endRect.bottom) - containerRect.top;

    // Update border position
    border.style.left = `${left}px`;
    border.style.top = `${top}px`;
    border.style.width = `${right - left - 2}px`;
    border.style.height = `${bottom - top - 2}px`;

    // Note the start and end cells
    this.#startSelectionCell = startCell;
    this.#endSelectionCell = endCell;
  }

  /**
   * Update the selection border.
   * @param {boolean} clearSelection True if selected cells should be cleared. Will clear only if there is a selection border
   */
  updateSelectionBorder(clearSelection: boolean = false) {
    const border = this.container?.querySelector<HTMLElement>('.selection-border');
    if (!(this.#startSelectionCell && this.#endSelectionCell)) {
      if (border) border.style.display = 'none';
      return;
    }

    if (clearSelection) {
      if (border) border.style.display = 'none';

      this.deSelectAllRows();
    } else {
      this.#updateSelectionBorder(this.#startSelectionCell, this.#endSelectionCell);
    }
  }

  /**
   * Handle Locale (and language) change
   */
  onLocaleChange = () => {
    this.redraw();
  };

  /**
   * Move a column to a new position. Use `columnIndex` to get the column by id.
   * @param {number} fromIndex The column index to movex
   * @param {number} toIndex The new column index
   * @param {boolean} includeInvisibleColumns use the invisible columns in the move
   */
  moveColumn(fromIndex: number, toIndex: number, includeInvisibleColumns = false) {
    if (fromIndex === NOT_FOUND_INDEX || toIndex === NOT_FOUND_INDEX) return;

    // Early-return when source and destination are the same column — no DOM work needed
    if (fromIndex === toIndex) return;

    this.setColumnMoving?.(true);

    let correctFromIndex = NOT_FOUND_INDEX;
    let correctToIndex = NOT_FOUND_INDEX;

    if (includeInvisibleColumns) {
      correctFromIndex = this.columnIdxById(this.columns[fromIndex].id);
      correctToIndex = this.columnIdxById(this.columns[toIndex].id);
    } else {
      correctFromIndex = this.columnIdxById(this.visibleColumns[fromIndex].id);
      correctToIndex = this.columnIdxById(this.visibleColumns[toIndex].id);
    }

    const minDraggableIndex = this.firstDraggableColumn;
    if (correctToIndex < minDraggableIndex || correctFromIndex < minDraggableIndex) {
      return;
    }

    const fromColumnId = this.columns[correctFromIndex]?.id;
    const toColumnId = this.columns[correctToIndex]?.id;

    // Early-return when resolved column ids match — no DOM work needed
    if (fromColumnId === toColumnId) {
      this.setColumnMoving?.(false);
      return;
    }

    // Capture visible positions before the reorder — defines the range to re-number below
    const visibleFromIndex = this.visibleColumnIdxById(fromColumnId);
    const visibleToIndex = this.visibleColumnIdxById(toColumnId);

    const element = this.columns[correctFromIndex];
    this.columns.splice(correctFromIndex, 1);
    this.columns.splice(correctToIndex, 0, element);

    // Lightweight column width update (skip full redraw, just update CSS variables)
    // Only regenerate the widths string without recalculating max-content sizes
    this.#updateColumnWidthsOnly();

    // Rebuild columnId → new visible column index map once for cell-index updates below.
    // The `cell` field on dirtyCells/invalidCells stores the visible column index (aria-colindex - 1),
    // so we must look up by columnId (stable) rather than by a numeric old→new index map.
    const colIdToVisibleIndex: Record<string, number> = {};
    const visibleColumnsAfter = this.visibleColumns;
    for (let i = 0; i < visibleColumnsAfter.length; i++) {
      colIdToVisibleIndex[visibleColumnsAfter[i].id] = i;
    }

    // Update dirty & invalid cell positions in a single pass over the affected rows.
    // Row-scoped iteration avoids the O(dirtyCells + invalidCells) getter cost from the
    // grid-wide getters which walk the entire dataset each call.
    for (let rowIdx = 0; rowIdx < this.data.length; rowIdx++) {
      const row: any = this.data[rowIdx];
      if (!row) continue;

      const dirty = row.dirtyCells;
      if (dirty) {
        for (let i = 0; i < dirty.length; i++) {
          const newIdx = colIdToVisibleIndex[dirty[i].columnId];
          if (newIdx !== undefined) dirty[i].cell = newIdx;
        }
      }

      const invalid = row.invalidCells;
      if (invalid) {
        for (let i = 0; i < invalid.length; i++) {
          const columnId = invalid[i].columnId;
          if (!columnId) continue;
          const newIdx = colIdToVisibleIndex[columnId];
          if (newIdx !== undefined) invalid[i].cell = newIdx;
        }
      }

      // Move the column-selection flag alongside the column
      if (row.colSelected) {
        const prev = row.colSelected[correctFromIndex];
        row.colSelected.splice(correctFromIndex, 1);
        row.colSelected.splice(correctToIndex, 0, prev);
      }
    }

    this.isMovingColumnNodes = true;

    // Move the column header
    // Look up by column-id (stable) rather than DOM index (stale after previous moves)
    const headerRow = this.header?.querySelector('.ids-data-grid-row');
    const headerCell = headerRow?.querySelector<HTMLElement>(`.ids-data-grid-header-cell[column-id="${fromColumnId}"]`) ?? null;
    const targetHeaderCell = headerRow?.querySelector<HTMLElement>(`.ids-data-grid-header-cell[column-id="${toColumnId}"]`) ?? null;
    if (headerCell && targetHeaderCell && headerRow) {
      // When moving right, insert after the target; when moving left, insert before it
      const insertRef = correctFromIndex < correctToIndex
        ? targetHeaderCell.nextElementSibling
        : targetHeaderCell;
      headerRow.insertBefore(headerCell, insertRef);
      this.header?.resetColumnsCache();
    }

    // Move cells in each row and re-number aria-colindex in a single pass. Look up by
    // column-id (stable) rather than DOM index (which is stale after previous moves).
    // Pre-build the selector strings once so per-row work stays allocation-free.
    const movingRight = correctFromIndex < correctToIndex;
    const fromCellSelector = `ids-data-grid-cell[column-id="${fromColumnId}"]`;
    const toCellSelector = `ids-data-grid-cell[column-id="${toColumnId}"]`;
    const rowsList = this.rows; // Single query — avoids re-running the rows getter below

    // Scope `aria-colindex` re-numbering to the moved range. On wide grids this avoids
    // writing every cell's attribute. Falls back to a full walk for rowspan grids where
    // DOM child position doesn't map 1:1 to column index.
    const firstMovedIndex = Math.min(visibleFromIndex, visibleToIndex);
    const lastMovedIndex = Math.max(visibleFromIndex, visibleToIndex);
    const canScopeRenumber = firstMovedIndex > NOT_FOUND_INDEX
      && !this.columns.some((column: IdsDataGridColumn) => column.rowspan === true);

    for (let r = 0; r < rowsList.length; r++) {
      const row = rowsList[r];
      const fromCell = row.querySelector<IdsDataGridCell>(fromCellSelector);
      const toCell = row.querySelector<IdsDataGridCell>(toCellSelector);
      if (!fromCell || !toCell) continue;
      // null insertRef appends to end, which is correct when moving past the last column
      const insertRef = movingRight ? toCell.nextElementSibling as IdsDataGridCell | null : toCell;
      fromCell.moveCellBefore(insertRef);

      // Re-number aria-colindex to keep it in sync with the new DOM order. Folded into the
      // same loop to avoid a second walk of `this.rows`.
      if (canScopeRenumber) {
        const rowCells = row.children;
        for (let i = firstMovedIndex; i <= lastMovedIndex; i++) {
          rowCells[i]?.setAttribute(htmlAttributes.ARIA_COLINDEX, `${i + 1}`);
        }
      } else {
        const visibleCells = row.querySelectorAll<HTMLElement>('ids-data-grid-cell');
        for (let i = 0; i < visibleCells.length; i++) {
          visibleCells[i].setAttribute(htmlAttributes.ARIA_COLINDEX, `${i + 1}`);
        }
      }
    }

    // Update header aria-colindex to match the body
    const headerVisibleCells = headerRow?.querySelectorAll<HTMLElement>('.ids-data-grid-header-cell');
    if (headerVisibleCells) {
      for (let i = 0; i < headerVisibleCells.length; i++) {
        headerVisibleCells[i].setAttribute(htmlAttributes.ARIA_COLINDEX, `${i + 1}`);
      }
    }

    // Reset the moving flag so downstream cell renders (redraw, sort state, etc.) work correctly
    this.isMovingColumnNodes = false;

    // Reset cached empty-message DOM references — the following redraw (when filters are
    // applied) rebuilds container.innerHTML, and stale references would point to detached nodes.
    resetEmptyMessageElements.apply(this);

    // Only redraw when there are filter conditions to restore — see IDS-21824.
    // A full redraw rebuilds every DOM node and neutralizes the targeted `insertBefore`
    // gains above, so we skip it when no filter state needs to be re-applied.
    const savedConditions = this.filterable ? this.filters?.filterConditions() : null;
    if (savedConditions?.length) {
      this.redraw();
      this.filters?.setFilterConditions(savedConditions);
    } else {
      // Lightweight cell cache refresh without full row redraw
      IdsDataGridCell.cellCache.clear();
    }
    this.triggerEvent('columnmoved', this, { detail: { elem: this, fromIndex: correctFromIndex, toIndex: correctToIndex } });
    if (this.sortColumn) this.header?.setSortState(this.sortColumn.id, this.sortColumn.ascending);
    this.saveSettings?.();

    this.setColumnMoving?.(false);

    if (this.#startSelectionCell || this.#endSelectionCell) {
      this.deSelectAllRows();
    }
  }

  #attachCopyPasteCellHandlers() {
    const body = this.body;

    this.offEvent('copy.body', body);
    this.onEvent('copy.body', body, (e: ClipboardEvent) => {
      this.#handleCopy(e);
    });

    this.offEvent('copy', this);
    this.onEvent('copy', this, (e: ClipboardEvent) => {
      this.#handleCopy(e);
    });

    this.offEvent('paste.body', body);
    this.onEvent('paste.body', body, (e: ClipboardEvent) => {
      this.#handlePaste(e);
    });

    this.offEvent('paste', this);
    this.onEvent('paste', this, (e: ClipboardEvent) => {
      this.#handlePaste(e);
    });
  }

  /**
   * Handle ArrowLeft/ArrowRight navigation within a filter wrapper.
   * Returns true if the event was handled and the caller should return early.
   * @private
   * @param {KeyboardEvent} e The keyboard event
   * @param {HTMLElement} filterWrapper The filter wrapper element
   * @returns {boolean} Whether the event was handled
   */
  #handleFilterArrowNavigation(e: KeyboardEvent, filterWrapper: HTMLElement): boolean {
    if (!filterWrapper) return false;

    const input = filterWrapper.querySelector<IdsInput>('ids-input');
    const inputValue = input?.value ?? '';

    if (!inputValue) {
      e.preventDefault();
      e.stopPropagation();

      const filterHeaderCell = filterWrapper.closest('.ids-data-grid-header-cell') as HTMLElement;
      const colIndexAttr = filterHeaderCell?.getAttribute(htmlAttributes.ARIA_COLINDEX);
      if (colIndexAttr) {
        const parsedIndex = parseInt(colIndexAttr, 10);
        if (!Number.isNaN(parsedIndex)) {
          const currColIndex = parsedIndex - 1;
          const nextColIndex = e.key === 'ArrowRight' ? currColIndex + 1 : currColIndex - 1;

          if (nextColIndex >= 0 && nextColIndex < this.visibleColumns.length) {
            const nextHeaderCell = this.header?.querySelector(
              `.ids-data-grid-header-cell[aria-colindex="${nextColIndex + 1}"]`
            ) as HTMLElement;
            nextHeaderCell?.focus();
          }
        }
      }
      return true;
    }

    return false;
  }

  /**
   * Focus a header cell, delegating to its inner checkbox when present.
   * @private
   * @param {HTMLElement} cell The header cell to focus
   */
  #focusHeaderCell(cell: HTMLElement): void {
    const checkbox = cell.querySelector('.ids-data-grid-checkbox[tabindex="0"]') as HTMLElement;
    if (checkbox) {
      checkbox.focus();
    } else {
      cell.focus();
    }
  }

  /**
   * Establish Internal Keyboard shortcuts
   * @private
   * @returns {object} This API object for chaining
   */
  #attachKeyboardListeners() {
    // Handle arrow navigation
    this.listen(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'], this, (e: KeyboardEvent) => {
      if (this.shouldAllowNavigation && !this.shouldAllowNavigation(e)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) && document.activeElement?.nodeName === 'IDS-DATA-GRID'
        && this.#activeCellCache && this.#activeCellCache?.hasLeaveViewport) {
        if (this.activeCellEditor?.isEditing) return;
        e.preventDefault();
        e.stopPropagation();

        const previousActiveRow = this.#activeCellCache.rowIndex;
        let newRowIndex = 0;
        let newColIndex = 0;

        if (e.key === 'ArrowUp') newRowIndex = -1;
        if (e.key === 'ArrowDown') newRowIndex = 1;
        if (e.key === 'ArrowRight') newColIndex = 1;
        if (e.key === 'ArrowLeft') newColIndex = -1;

        // For tree grids, skip hidden rows when navigating vertically
        if (this.treeGrid && newRowIndex !== 0) {
          const direction = newRowIndex === 1 ? IdsDirection.Down : IdsDirection.Up;
          const nextRowIndex = this.#findNextVisibleRow(this.#activeCellCache.rowIndex, direction);
          this.#activeCellCache.rowIndex = nextRowIndex !== -1 ? nextRowIndex : this.#activeCellCache.rowIndex;
        } else {
          const maxRow = this.data.length - 1;
          this.#activeCellCache.rowIndex = Math.min(Math.max(this.#activeCellCache.rowIndex + newRowIndex, 0), maxRow);
        }
        const maxCol = this.visibleColumns.length - 1;
        this.#activeCellCache.columnIndex = Math.min(Math.max(this.#activeCellCache.columnIndex + newColIndex, 0), maxCol);

        // Clear editing state when navigating to a different row to prevent
        // the old cell's value from being copied into the new cell
        if (this.#activeCellCache.rowIndex !== previousActiveRow || newColIndex !== 0) {
          this.#activeCellCache.currentValue = undefined;
          this.#activeCellCache.wasEditing = false;
        }

        // Scroll to the NEW row position (after index update) so the target cell is visible
        if (this.treeGrid) {
          const visibleRowIdx = this.#getVisibleRowIndex(this.#activeCellCache.rowIndex);
          if (visibleRowIdx !== -1 && this.container) {
            const { ROW_HEIGHT } = this.virtualScrollSettings;
            const headerHeight = this.header?.clientHeight ?? 0;
            const maxScroll = this.container.scrollHeight - this.container.clientHeight;
            const targetScrollTop = Math.max(visibleRowIdx * ROW_HEIGHT - headerHeight, 0);
            this.container.scrollTop = Math.min(targetScrollTop, maxScroll);
          }
        } else {
          this.#scrollRowIntoView(Math.max(this.#activeCellCache.rowIndex - 3, 0));
        }

        // Handle shift+arrow selection/deselection for rows out of viewport
        if ((this.rowSelection === 'mixed' || this.rowSelection === 'multiple') && newRowIndex !== 0 && e.shiftKey) {
          const activeRowIndex = this.#activeCellCache.rowIndex;
          this.#lastShiftedRow ??= previousActiveRow;
          const shiftSelectFrom = Math.min(activeRowIndex, this.#lastShiftedRow);
          const shiftSelectTo = Math.max(activeRowIndex, this.#lastShiftedRow);

          if (previousActiveRow < shiftSelectFrom || previousActiveRow > shiftSelectTo) {
            this.deSelectRow(previousActiveRow);
          } else {
            this.selectRow(activeRowIndex);
          }
        } else if (!e.shiftKey) {
          this.#resetLastShiftedRow();
        }

        requestAnimationTimeout(() => {
          this.#restoreActiveCellAfterScroll();
        });
        return;
      }

      const inFilter = findInPath(eventPath(e), '.ids-data-grid-header-cell-filter-wrapper');
      const key = e.key;

      if (!e.shiftKey) this.#resetLastShiftedRow();
      if (inFilter) return;
      if (this.openMenu) return;

      // Handle keyboard navigation when a summary cell is focused
      const focusedEl = this.shadowRoot?.activeElement as HTMLElement;
      if (focusedEl?.classList?.contains('summary-cell')) {
        this.#handleSummaryRowKeydown(e, focusedEl);
        return;
      }

      // Get active cell or default to first cell if null
      let activeCell = this.activeCell.node;
      if (!activeCell) {
        const firstRow = this.rowByIndex(0);
        activeCell = firstRow?.cellByIndex(0) ?? null;
        activeCell?.activate(false, 'keyboard');
      }
      if (activeCell?.isEditing) return;

      e.preventDefault();
      e.stopPropagation();

      const previousActiveRow = Number(activeCell?.rowIndex || 0);

      if (key === 'ArrowUp') {
        // If we're in the first row, move to header (but not from a group header row)
        if (activeCell?.rowIndex === 0 && !this.data[0]?.isGroupRow) {
          const columnIndex = activeCell?.columnIndex ?? 0;
          const headerCell = this.header?.querySelector(`[aria-colindex="${columnIndex + 1}"].ids-data-grid-header-cell`) as HTMLElement;
          if (headerCell) {
            // Clear active row indicator when navigating to header
            this.setActiveRowIndicator(-1);
            // Ensure the header cell is focusable and remove any mouse-focus state from all header cells
            headerCell.setAttribute(attributes.TABINDEX, '0');
            // Use roving tabindex: set the target header cell to 0, all others to -1
            this.header?.querySelectorAll('.ids-data-grid-header-cell').forEach((cell) => {
              cell.setAttribute(attributes.TABINDEX, '-1');
              cell.classList.remove('is-focused');
            });
            headerCell.setAttribute(attributes.TABINDEX, '0');
            headerCell.focus();
            return;
          }
        }
        const prevCell = activeCell;
        activeCell = this.findNextNavigableCell(activeCell, IdsDirection.Up);

        // Handle rows not yet in DOM for virtual scroll
        if (!activeCell && this.virtualScroll && prevCell && prevCell.rowIndex > 0) {
          const nextRowIndex = this.#findNextVisibleRow(prevCell.rowIndex, IdsDirection.Up);
          if (nextRowIndex !== -1) {
            this.#scrollToActivateRow(nextRowIndex, prevCell.columnIndex, IdsDirection.Up);
            return;
          }
        }

        if (this.virtualScroll && activeCell) {
          this.#ensureRowInViewport(activeCell.rowIndex, IdsDirection.Up);
        }
      } else if (key === 'ArrowDown') {
        const prevCell = activeCell;
        activeCell = this.findNextNavigableCell(activeCell, IdsDirection.Down);

        // Handle rows not yet in DOM for virtual scroll
        if (!activeCell && this.virtualScroll && prevCell && prevCell.rowIndex < this.data.length - 1) {
          const nextRowIndex = this.#findNextVisibleRow(prevCell.rowIndex, IdsDirection.Down);
          if (nextRowIndex !== -1) {
            this.#scrollToActivateRow(nextRowIndex, prevCell.columnIndex, IdsDirection.Down);
            return;
          }
        }

        // Move focus to summary row when at the last data row
        if (!activeCell && this.showSummaryRow && prevCell) {
          const colIdx = prevCell.columnIndex ?? 0;
          const cells = this.body?.querySelectorAll<HTMLElement>('.summary-cell');
          if (cells && cells.length > 0) {
            const idx = Math.min(colIdx, cells.length - 1);
            cells[idx].focus();
            return;
          }
        }

        if (this.virtualScroll && activeCell) {
          this.#ensureRowInViewport(activeCell.rowIndex, IdsDirection.Down);
        }
      } else if (key === 'ArrowLeft' && !e.metaKey) {
        activeCell = this.findNextNavigableCell(activeCell, IdsDirection.Left);
      } else if (key === 'ArrowRight' && !e.metaKey) {
        activeCell = this.findNextNavigableCell(activeCell, IdsDirection.Right);
      } else if (key === 'PageUp') {
        const columnIndex = activeCell?.columnIndex ?? 0;
        activeCell = this.findCellByLogicalPosition(0, columnIndex);
      } else if (key === 'PageDown') {
        // Get the last cell in the current column
        const lastRowIndex = this.data.length - 1;
        const columnIndex = activeCell?.columnIndex ?? 0;
        activeCell = this.findCellByLogicalPosition(lastRowIndex, columnIndex);
      } else if (key === 'Home' || (key === 'ArrowLeft' && e.metaKey)) {
        // Get the first cell in the current row
        const currentRowIndex = activeCell?.rowIndex ?? 0;
        activeCell = this.findCellByLogicalPosition(currentRowIndex, 0);
      } else if (key === 'End' || (key === 'ArrowRight' && e.metaKey)) {
        // Get the last cell in the current row
        const currentRowIndex = activeCell?.rowIndex ?? 0;
        const lastColumnIndex = this.visibleColumns.length - 1;
        activeCell = this.findCellByLogicalPosition(currentRowIndex, lastColumnIndex);
      }

      // Prevent false movement detection when navigation hits boundary
      const activeRowIndex = activeCell?.rowIndex ?? previousActiveRow;
      activeCell?.focus?.();

      if (activeCell && activeCell.columnIndex >= 0) {
        this.setActiveCell(activeCell.columnIndex, activeRowIndex, false, 'keyboard');
      }

      // Handle row selection
      const movingVertical = key === 'ArrowDown' || key === 'ArrowUp';
      if ((this.rowSelection === 'mixed' || this.rowSelection === 'multiple') && movingVertical && e.shiftKey) {
        // For mixed mode with rowNavigation, allow shift-selection even without existing selection
        const allowShiftSelection = this.rowSelection === 'mixed' && this.rowNavigation
          ? true
          : this.selectedRows.length > 0;

        // Only process shift-selection if we actually moved to a different row AND conditions are met
        if (activeRowIndex !== previousActiveRow && allowShiftSelection) {
          this.#lastShiftedRow ??= previousActiveRow;
          const shiftSelectFrom = Math.min(activeRowIndex, this.#lastShiftedRow);
          const shiftSelectTo = Math.max(activeRowIndex, this.#lastShiftedRow);

          if (Number.isNaN(shiftSelectFrom) || Number.isNaN(shiftSelectTo)) return;

          // Don't deselect the previous row if it's at the boundary and we didn't actually move
          const isAtLastRow = previousActiveRow === this.data.length - 1;
          const isAtFirstRow = previousActiveRow === 0;
          const tryingToGoDown = key === 'ArrowDown' && isAtLastRow;
          const tryingToGoUp = key === 'ArrowUp' && isAtFirstRow;

          if (!tryingToGoDown && !tryingToGoUp
            && (previousActiveRow < shiftSelectFrom || previousActiveRow > shiftSelectTo)) {
            this.deSelectRow(previousActiveRow);
          }

          for (let i = shiftSelectFrom; i <= shiftSelectTo; i++) {
            this.selectRow(i);
          }
        }
      }

      if (this.rowSelection === 'mixed' && this.rowNavigation) {
        (activeCell?.parentElement as IdsDataGridRow).toggleRowActivation();
      }
    });

    // Handle Selection and Expand
    this.listen([' ', 'Space'], this, (e: Event) => {
      const inFilter = findInPath(eventPath(e), '.ids-data-grid-header-cell-filter-wrapper');
      if (inFilter) return;

      let activeCell = this.activeCell.node;
      if (!activeCell) {
        const firstRow = this.rowByIndex(0);
        activeCell = firstRow?.cellByIndex(0) ?? null;
        activeCell?.activate(false, 'keyboard');
      }

      if (!activeCell) return;
      if (this.openMenu) return;
      if (activeCell.isEditing) return;

      // Allow Space to reach focusable inputs/textareas inside the cell (e.g. position fields
      // rendered by a custom formatter) — the grid should not consume the key in this case.
      const path = eventPath(e as HTMLElementEvent<HTMLElement[]>);
      const targetEl = path[0] as HTMLElement;
      if (targetEl instanceof HTMLInputElement || targetEl instanceof HTMLTextAreaElement) {
        return;
      }

      // Allow tree expand/collapse toggle even on inline editable cells
      const expandButton = (activeCell as Element).querySelector<IdsButton>('ids-button.expand-button');
      if (expandButton) {
        expandButton.click();
        e.preventDefault();
        return;
      }

      if (!activeCell.canClose()) return;

      // If the active cell is an editable non-checkbox cell, let Space start edit mode
      // instead of toggling row selection. This allows Space as a typing keystroke.
      const column = activeCell.column;
      const child = activeCell.children[0];
      const isCheckbox = child?.classList.contains('ids-data-grid-checkbox-container')
        && !child?.classList.contains('is-selection-checkbox');
      const isSelectionCheckbox = child?.classList.contains('is-selection-checkbox');

      if (!isCheckbox && !isSelectionCheckbox && column?.editor && this.editable) {
        activeCell.startCellEdit?.();
        return;
      }

      const row = this.rowByIndex(activeCell.rowIndex)!;
      if (!row || row.disabled) return;

      const button = (activeCell as Element).querySelector<IdsButton>('ids-button');
      if (button) {
        button.click();
        e.preventDefault();
        return;
      }

      if (isCheckbox) {
        activeCell.click();
        e.preventDefault();
        return;
      }
      row.toggleSelection();
      e.preventDefault();
    });

    // Follow links with keyboard and start editing
    this.listen(['Enter'], this, (e: KeyboardEvent) => {
      const cellNode = (this.activeCell.node as IdsDataGridCell);
      const target = (e.target as any);

      // Hyperlink
      if (cellNode?.classList.contains('formatter-hyperlink')) {
        if (
          !this.selectWithFilterDropdown
          && target.activeCell
          && Object.keys(target.activeCell).length !== 0
        ) {
          cellNode.querySelector<IdsHyperlink>('ids-hyperlink')?.container?.click();
        }
        return;
      }

      // Button
      if (cellNode?.classList.contains('formatter-button')) {
        cellNode.querySelector<IdsButton>('ids-button')?.button?.click();
        return;
      }

      // Editable Cell
      if (!cellNode?.isEditing) {
        // Don't start editing a new cell if an editor popup is already open
        if (this.activeCellEditor?.editor?.popup?.visible) {
          return;
        }
        cellNode?.startCellEdit();
        return;
      }

      // If a datepicker/timepicker/lookup popup is open, don't propagate Enter to next row
      if (['datepicker', 'timepicker', 'lookup'].includes(cellNode?.column?.editor?.type as string)
        && this.activeCellEditor?.editor?.popup?.visible) {
        return;
      }

      // Also check activeCellEditor in case activeCell diverged from the editing cell
      if (this.activeCellEditor?.editor?.popup?.visible) {
        return;
      }

      // For inline datepicker/timepicker/lookup, Enter ends the current edit and moves down
      // without auto-opening the next editor (matches editable.html behavior)
      if (cellNode?.column?.editor?.inline
        && ['datepicker', 'timepicker', 'lookup'].includes(cellNode?.column?.editor?.type)) {
        cellNode.endCellEdit();
        const nextCell = e.shiftKey ? cellNode?.cellAbove : cellNode?.cellBelow;
        if (nextCell) {
          this.setActiveCell(nextCell.columnIndex, nextCell.rowIndex, false, 'keyboard');
          nextCell.focus();
        }
        return;
      }

      const nextCell = e.shiftKey ? cellNode?.cellAbove : cellNode?.cellBelow;

      if (this.editNextOnEnterPress) {
        if (nextCell) {
          this.setActiveCell(nextCell.columnIndex, nextCell.rowIndex, false, 'keyboard');
          nextCell.startCellEdit();
        }
      }
      if (!nextCell?.isEditing) nextCell?.focus?.(); // focus non-editable cells
    });

    // Commit Edit
    this.listen(['F2'], this, () => {
      const cellNode = this.activeCell.node;
      if (this.activeCellEditor) {
        cellNode.endCellEdit();
        cellNode.focus();
      }
    });

    // Cancel Edit
    this.listen(['Escape'], this, () => {
      if (this.openMenu) return;
      const cellNode = this.activeCell.node;
      if (this.activeCellEditor) {
        // If any popup/modal is open, close it instead of cancelling edit
        if (this.activeCellEditor?.editor?.popup?.visible) {
          const editor = this.activeCellEditor.editor;
          const isInline = this.activeCellEditor?.column?.editor?.inline;
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          (async () => {
            await editor.popup?.hide();
            if (isInline) {
              editor.input?.focus();
            }
          })();
          return;
        }
        if (!this.activeCellCanClose()) return;
        cellNode.cancelCellEdit();
        cellNode.focus();
      }
    });

    // Edit Next
    this.listen(['Tab'], this, (e: KeyboardEvent) => {
      if (!this.isEditable) {
        return;
      }

      // If any editor popup/modal is open, let the popup handle Tab internally
      if (this.activeCellEditor?.editor?.popup?.visible) {
        return;
      }

      const path = e.composedPath?.() || [];
      if (path.some((node: EventTarget) => (node as HTMLElement)?.tagName?.startsWith?.('IDS-PAGER'))) {
        return;
      }

      // Use the actively-editing cell (activeCellEditor) as the navigation origin
      // when it differs from activeCell.node (e.g. after editFirstCell)
      const navOrigin = (this.activeCellEditor instanceof IdsDataGridCell && this.activeCellEditor !== this.activeCell?.node)
        ? this.activeCellEditor
        : this.activeCell?.node;

      let nextCell = e.shiftKey
        ? navOrigin?.cellLeftEditable
        : navOrigin?.cellRightEditable;

      while (nextCell && (nextCell.hasAttribute?.('hidden'))) {
        nextCell = e.shiftKey ? nextCell?.cellLeftEditable : nextCell?.cellRightEditable;
      }

      // If there's no next editable cell, check for addNewAtEnd functionality
      if (!nextCell) {
        if (!e.shiftKey && this.addNewAtEnd && navOrigin) {
          const currentRowIndex = navOrigin.rowIndex;
          const isLastRow = currentRowIndex === this.data.length - 1;

          // Prevent multiple concurrent async row appends
          if (isLastRow && this.#pendingRowAppend) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
            return;
          }

          if (isLastRow) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();

            // Create a new row with empty data
            const newRowData: Record<string, any> = {};
            this.visibleColumns.forEach((column) => {
              if (column.field && column.field !== 'selectionCheckbox' && column.field !== 'selectionRadio') {
                newRowData[column.field] = '';
              }
            });

            // Helper to add row and focus the first editable cell
            const appendRowAndFocus = (data: Record<string, any>) => {
              this.addRow(data);

              requestAnimationFrame(() => {
                const newRowIndex = this.data.length - 1;
                const newRow = this.rowByIndex(newRowIndex);

                if (newRow) {
                  const firstEditableCell = newRow.querySelector<IdsDataGridCell>('.is-editable:not(.is-readonly):not(.is-disabled):not(.formatter-selectionCheckbox)');

                  if (firstEditableCell) {
                    const columnIndex = firstEditableCell.columnIndex;
                    this.setActiveCell(columnIndex, newRowIndex, false, 'keyboard');

                    if (this.editNextOnEnterPress) {
                      firstEditableCell.startCellEdit?.();
                    } else {
                      firstEditableCell.focus?.();
                    }
                  }
                }
              });
            };

            // Fire beforerowappend event to allow cancellation, data override, or async handling.
            // The response callback accepts:
            //   false                          - cancel row creation
            //   true                           - proceed with default empty row
            //   Record<string, any>            - proceed with custom data
            //   Promise<boolean | Record>      - async: resolved value is handled as above
            type BeforeRowAppendResult = boolean | Record<string, unknown>;
            type BeforeRowAppendResponse = BeforeRowAppendResult | Promise<BeforeRowAppendResult>;
            let responseValue: BeforeRowAppendResponse | unknown = true;
            const eventResponse = (result: BeforeRowAppendResponse) => {
              responseValue = result;
            };
            this.triggerEvent('beforerowappend', this, {
              bubbles: true,
              detail: {
                data: newRowData,
                response: eventResponse
              }
            });

            // Handle async (Promise) responses
            if (responseValue instanceof Promise) {
              this.#pendingRowAppend = true;
              responseValue
                .then((result) => {
                  if (result === false) return;
                  if (result !== true && typeof result === 'object' && result !== null) {
                    appendRowAndFocus(result as Record<string, unknown>);
                  } else {
                    appendRowAndFocus(newRowData);
                  }
                })
                .catch(() => { /** ignore if promise rejected */ })
                .finally(() => { this.#pendingRowAppend = false; });
            } else {
              // Handle sync responses
              if (responseValue === false) return;
              if (responseValue !== true && typeof responseValue === 'object' && responseValue !== null) {
                appendRowAndFocus(responseValue as Record<string, unknown>);
              } else {
                appendRowAndFocus(newRowData);
              }
            }

            return;
          }
        }

        // If not adding new row, clear active indicator and let tab behave normally
        if (e.shiftKey && navOrigin?.rowIndex === 0) {
          this.setActiveRowIndicator(-1);
        }
        return;
      }

      // Only prevent default if we found a next cell to edit
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();

      // First activate and focus the next cell
      this.setActiveCell(nextCell.columnIndex, nextCell.rowIndex, false, 'keyboard');

      // Then handle the editing state
      if (this.editNextOnEnterPress) {
        // Wrap in setTimeout to ensure current cell edit completion
        setTimeout(() => {
          nextCell.startCellEdit?.();
        }, 0);
      } else if (!nextCell.isEditing) {
        nextCell.focus?.();
      }
    });

    // Select all
    this.listen(['a', 'A'], this, (e: KeyboardEvent) => {
      // Handle Ctrl+A or Command+A (Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
        // Check if any element has focus that should handle its own select-all
        const activeElement = document.activeElement;
        const shadowActiveElement = this.shadowRoot?.activeElement;

        // If we're in a text input or textarea, let the default behavior handle it
        const isTextInput = activeElement instanceof HTMLInputElement && activeElement.type === 'text';
        const isTextArea = activeElement instanceof HTMLTextAreaElement;
        const isContentEditable = activeElement?.hasAttribute('contenteditable');

        // Check if we're in a filter input within the shadow DOM
        const isInFilter = shadowActiveElement?.closest('.ids-data-grid-header-cell-filter-wrapper') !== null;

        if (this.activeCell?.node?.isEditing || this.activeCell?.isEditing || isTextInput || isTextArea
          || isContentEditable || isInFilter) {
          // Let the default behavior handle text selection
          return;
        }

        // Otherwise prevent default and handle grid-level selection
        e.preventDefault();
        e.stopPropagation();
        this.toggleSelectAllRows();
      }
    });

    this.offEvent('keydown.body', this.header);
    this.onEvent('keydown.body', this.header, (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)
        && this.shouldAllowNavigation && !this.shouldAllowNavigation(e)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const path = eventPath(e);
      const inFilter = findInPath(path, '.ids-data-grid-header-cell-filter-wrapper');
      const target = e.target as HTMLElement;

      const isEditable = inFilter
        || target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target?.isContentEditable
        || findInPath(path, 'ids-input');

      if (isEditable && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        if (this.#handleFilterArrowNavigation(e, inFilter as HTMLElement)) return;
      }

      // Resolve the header cell from the target (target may be an inner element like a checkbox)
      const headerCell = (target.closest('.ids-data-grid-header-cell') ?? target) as HTMLElement;
      const isOnHeaderCell = headerCell.classList.contains('ids-data-grid-header-cell');
      const isOnInnerTabbable = isOnHeaderCell && target !== headerCell;
      const hasFilterRow = this.filterable && !!headerCell.querySelector('.ids-data-grid-header-cell-filter-wrapper');

      if (e.key === 'Tab' && isOnHeaderCell) {
        if (hasFilterRow) {
          const filterSelectors = 'ids-input, ids-dropdown, ids-trigger-field, ids-menu-button, ids-time-picker, ids-date-picker';
          const allFilterInputs = [...(this.header?.querySelectorAll<HTMLElement>(
            `.ids-data-grid-header-cell-filter-wrapper ${filterSelectors}`
          ) ?? [])];

          if (isOnInnerTabbable && inFilter) {
            const closestWidget = target.closest(filterSelectors) as HTMLElement;
            const currentIndex = allFilterInputs.indexOf(closestWidget);

            if (!e.shiftKey) {
              if (currentIndex >= 0 && currentIndex < allFilterInputs.length - 1) {
                e.preventDefault();
                e.stopPropagation();
                allFilterInputs[currentIndex + 1].focus();
              }
            } else if (currentIndex > 0) {
              e.preventDefault();
              e.stopPropagation();
              allFilterInputs[currentIndex - 1].focus();
            }
            return;
          }

          if (!isOnInnerTabbable && !e.shiftKey) {
            const filterWrapper = headerCell.querySelector('.ids-data-grid-header-cell-filter-wrapper');
            const firstFilterInput = filterWrapper?.querySelector<HTMLElement>(filterSelectors);
            if (firstFilterInput) {
              e.preventDefault();
              e.stopPropagation();
              firstFilterInput.focus();
              return;
            }
          }
        }

        if (!isOnInnerTabbable && !e.shiftKey) {
          const checkbox = headerCell.querySelector('.ids-data-grid-checkbox[tabindex="0"]') as HTMLElement;
          if (checkbox) {
            e.preventDefault();
            e.stopPropagation();
            checkbox.focus();
            return;
          }
        }

        const colIndex = headerCell.getAttribute(htmlAttributes.ARIA_COLINDEX);
        if (colIndex) {
          const targetIndex = e.shiftKey ? parseInt(colIndex) - 1 : parseInt(colIndex) + 1;
          const adjacentHeaderCell = this.header?.querySelector(
            `[aria-colindex="${targetIndex}"].ids-data-grid-header-cell`
          ) as HTMLElement;
          if (adjacentHeaderCell) {
            e.preventDefault();
            e.stopPropagation();
            headerCell.setAttribute(attributes.TABINDEX, '-1');
            adjacentHeaderCell.setAttribute(attributes.TABINDEX, '0');
            this.#focusHeaderCell(adjacentHeaderCell);
            return;
          }
        }
        return;
      }

      const isFilterTextWidget = inFilter && isOnInnerTabbable
        && !!(target.closest('ids-input') || target.closest('ids-dropdown')
          || target.closest('ids-trigger-field') || target.closest('ids-time-picker')
          || target.closest('ids-date-picker'));

      if (inFilter && isOnInnerTabbable) {
        if (e.key === 'ArrowDown') {
          const columnIndexAttr = headerCell.getAttribute(htmlAttributes.ARIA_COLINDEX);
          if (columnIndexAttr) {
            e.preventDefault();
            e.stopPropagation();
            const columnIndex = parseInt(columnIndexAttr) - 1;
            const targetCell = this.rowByIndex(0)?.cellByIndex(columnIndex);
            if (targetCell) {
              this.header?.querySelectorAll('.ids-data-grid-header-cell').forEach((cell) => {
                cell.setAttribute(attributes.TABINDEX, '-1');
              });
              this.setActiveCell(columnIndex, 0, false, 'keyboard');
              targetCell.focus();
            }
          }
          return;
        }

        if (isFilterTextWidget) {
          return;
        }
      }

      if (isOnInnerTabbable && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        this.header?.querySelectorAll('.ids-data-grid-header-cell').forEach((cell) => {
          cell.setAttribute(attributes.TABINDEX, '-1');
          cell.classList.remove('is-focused');
        });
        headerCell.setAttribute(attributes.TABINDEX, '0');

        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          const columnIndexAttr = headerCell.getAttribute(htmlAttributes.ARIA_COLINDEX);
          if (columnIndexAttr) {
            const currentColumnIndex = parseInt(columnIndexAttr) - 1;
            const nextColumnIndex = e.key === 'ArrowRight'
              ? currentColumnIndex + 1
              : currentColumnIndex - 1;

            if (nextColumnIndex >= 0 && nextColumnIndex < this.visibleColumns.length) {
              const nextHeaderCell = this.header?.querySelector(`[aria-colindex="${nextColumnIndex + 1}"].ids-data-grid-header-cell`) as HTMLElement;
              if (nextHeaderCell) {
                headerCell.setAttribute(attributes.TABINDEX, '-1');
                nextHeaderCell.setAttribute(attributes.TABINDEX, '0');
                nextHeaderCell.focus();
              }
            } else {
              headerCell.focus();
            }
          } else {
            headerCell.focus();
          }
        } else if (e.key === 'ArrowDown') {
          const columnIndexAttr = headerCell.getAttribute(htmlAttributes.ARIA_COLINDEX);
          if (columnIndexAttr) {
            const columnIndex = parseInt(columnIndexAttr) - 1;
            const targetCell = this.rowByIndex(0)?.cellByIndex(columnIndex);
            if (targetCell) {
              headerCell.setAttribute(attributes.TABINDEX, '-1');
              this.setActiveCell(columnIndex, 0, false, 'keyboard');
              targetCell.focus();
            }
          }
        } else {
          headerCell.focus();
        }
        return;
      }
      if (isEditable && e.key === 'ArrowDown') {
        const columnIndexAttr = headerCell?.getAttribute(htmlAttributes.ARIA_COLINDEX);
        if (columnIndexAttr) {
          e.preventDefault();
          e.stopPropagation();
          const columnIndex = parseInt(columnIndexAttr) - 1;
          const targetCell = this.rowByIndex(0)?.cellByIndex(columnIndex);
          if (targetCell) {
            this.header?.querySelectorAll('.ids-data-grid-header-cell').forEach((cell) => {
              cell.setAttribute(attributes.TABINDEX, '-1');
            });
            this.setActiveCell(columnIndex, 0, false, 'keyboard');
            targetCell.focus();
            return;
          }
        }
      }

      if (isEditable) {
        return;
      }

      if (e.key === 'ArrowUp') {
        // Ignore ArrowUp when already on header - nowhere up to go
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();

        const focusedHeaderCell = e.target as HTMLElement;
        const columnIndexAttr = focusedHeaderCell.getAttribute(htmlAttributes.ARIA_COLINDEX);

        if (columnIndexAttr) {
          const columnIndex = parseInt(columnIndexAttr) - 1;

          const firstRow = this.rowByIndex(0);
          if (firstRow) {
            const targetCell = firstRow.cellByIndex(columnIndex);
            if (targetCell) {
              // Reset header cell tabindex when leaving header
              focusedHeaderCell.setAttribute(attributes.TABINDEX, '-1');
              this.setActiveCell(columnIndex, 0, false, 'keyboard');
              targetCell.focus();
              return;
            }
          }
        }
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const focusedHeaderCell = e.target as HTMLElement;
        const columnIndexAttr = focusedHeaderCell.getAttribute(htmlAttributes.ARIA_COLINDEX);

        // Handle Shift+Arrow for column resize accessibility
        if (e.shiftKey && columnIndexAttr) {
          e.preventDefault();
          e.stopPropagation();
          const columnId = focusedHeaderCell.getAttribute('column-id');

          if (columnId) {
            const column = this.columnDataById(columnId);
            if (column && column.resizable) {
              const RESIZE_INCREMENT = 5;
              const DEFAULT_COLUMNSIZE = 110;
              const currentWidth = typeof column.width === 'number' ? column.width : DEFAULT_COLUMNSIZE;
              const proposedWidth = e.key === 'ArrowRight' ? currentWidth + RESIZE_INCREMENT : currentWidth - RESIZE_INCREMENT;
              const minWidth = (column as any).minWidth || DEFAULT_COLUMN_MIN_WIDTH;
              const maxWidth = (column as any).maxWidth || Number.MAX_SAFE_INTEGER;
              const newWidth = Math.max(minWidth, Math.min(maxWidth, proposedWidth));

              if (newWidth !== currentWidth) {
                // Fire beforecolumnresized event (can be vetoed)
                let canResize = true;
                const response = (veto: boolean) => { canResize = !veto; };

                this.triggerEvent('beforecolumnresized', this, {
                  detail: { column, width: currentWidth, response }
                });

                if (!canResize) return;

                this.setColumnWidth(columnId, newWidth);

                // Fire aftercolumnresized for keyboard resize
                this.triggerEvent('aftercolumnresized', this, {
                  detail: {
                    column,
                    width: newWidth,
                    startWidth: currentWidth,
                    columns: this.columns
                  }
                });

                let columnText = this.localeAPI?.translate('ColumnResizeAria');
                columnText = columnText.replace('{0}', column.name).replace('{1}', newWidth);
                focusedHeaderCell.setAttribute('aria-label', columnText);
              }
            }
          }
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (columnIndexAttr) {
          const currentColumnIndex = parseInt(columnIndexAttr) - 1;
          const nextColumnIndex = e.key === 'ArrowRight'
            ? currentColumnIndex + 1
            : currentColumnIndex - 1;

          if (nextColumnIndex >= 0 && nextColumnIndex < this.visibleColumns.length) {
            const nextHeaderCell = this.header?.querySelector(`[aria-colindex="${nextColumnIndex + 1}"].ids-data-grid-header-cell`) as HTMLElement;
            if (nextHeaderCell) {
              // Roving tabindex: move tabindex="0" to the new cell
              focusedHeaderCell.setAttribute(attributes.TABINDEX, '-1');
              nextHeaderCell.setAttribute(attributes.TABINDEX, '0');
              nextHeaderCell.focus();
              return;
            }
          }
        }
      }

      if (e.key === ' ' || e.key === 'Space') {
        const focusedHeaderCell = e.target as HTMLElement;
        if (focusedHeaderCell.hasAttribute('tabindex') && focusedHeaderCell.classList.contains('ids-data-grid-header-cell')) {
          e.preventDefault();
          e.stopPropagation();

          const headerCheckbox = focusedHeaderCell.querySelector('.ids-data-grid-checkbox');
          if (headerCheckbox) {
            if (headerCheckbox.classList.contains('checked') || headerCheckbox.classList.contains('indeterminate')) {
              this.deSelectAllRows();
            } else {
              this.selectAllRows();
            }
          }

          const columnCheckbox = focusedHeaderCell.querySelector('.ids-checkbox input');
          if (columnCheckbox) {
            const checkbox = columnCheckbox as HTMLInputElement;
            checkbox.checked = !checkbox.checked;
            const columnIndex = parseInt(focusedHeaderCell.getAttribute(htmlAttributes.ARIA_COLINDEX) || '1') - 1;
            if (checkbox.checked) {
              this.selectColumn(columnIndex);
            } else {
              this.deSelectColumn(columnIndex);
            }
          }
          return;
        }
      }

      this.activeCell = {};
    });

    // Enter Edit by typing
    this.offEvent('keydown.body', this);
    this.onEvent('keydown.body', this, (e: KeyboardEvent) => {
      const isCtrlDown = e.ctrlKey || e.metaKey;
      const isKeyCopyPaste = e.key === 'v' || e.key === 'c';

      if (isCtrlDown && isKeyCopyPaste) return;

      const isPrintableKey = e.key.length === 1;
      if (!this.activeCellEditor && isPrintableKey) {
        // Start Edit Mode
        const activeCell = this.activeCell?.node;
        if (activeCell?.tabIndex === 0) {
          activeCell?.startCellEdit?.();
        }

        // Depending on autoselect property replace the cell value if autoselect is true
        const inputElem = (this.activeCellEditor as any)?.editor?.input as any;
        const columnSettings = activeCell?.column?.editor?.editorSettings;
        if (inputElem && columnSettings?.autoselect) {
          requestAnimationFrame(() => {
            e.preventDefault();
            e.stopPropagation();
            inputElem.value = e.key;
          });
        }
      }
    });
    return this;
  }

  /**
   * Apply all pending column visibility changes to the DOM
   * @private
   */
  #applyColumnVisibilityChanges() {
    let needsFrozenRefresh = false;

    this.#columnsToUpdate.forEach((columnId) => {
      const column = this.columnDataById(columnId);
      if (!column) return;

      const headerCell = this.container?.querySelector(`[column-id="${columnId}"]`);
      if (headerCell) {
        const ariaColIndex = headerCell.getAttribute(htmlAttributes.ARIA_COLINDEX);
        const isHidden = column.hidden || column.hiddenAtRuntime;
        const wasFrozen = headerCell.classList.contains('frozen') || headerCell.classList.contains('frozen-last');

        (headerCell as HTMLElement).toggleAttribute('hidden', isHidden);

        if (ariaColIndex) {
          const cells = this.container?.querySelectorAll(`[aria-colindex="${ariaColIndex}"]`);
          cells?.forEach((cell: Element) => {
            (cell as HTMLElement).toggleAttribute('hidden', isHidden);
          });
        }

        if (wasFrozen) needsFrozenRefresh = true;
      }
    });

    this.#columnsToUpdate.clear();
    this.#setColumnWidths();

    if (needsFrozenRefresh) {
      this.refreshLeftFrozenHeaderClasses();
    }
  }

  /**
   * Schedule a debounced update of column visibility
   * @private
   */
  #scheduleColumnVisibilityUpdate() {
    if (this.#columnVisibilityUpdateTimer) {
      clearTimeout(this.#columnVisibilityUpdateTimer);
    }

    this.#columnVisibilityUpdateTimer = setTimeout(() => {
      this.#applyColumnVisibilityChanges();
      this.#columnVisibilityUpdateTimer = undefined;
    }, 0) as unknown as number;
  }

  /**
   * Set the column widths by generating the lengths in the css grid
   * and setting the css variable.
   * @private
   */
  #setColumnWidths() {
    let colWidths = '';
    const visibleColumns = this.columns?.filter((column: IdsDataGridColumn) => !column.hidden) ?? [];
    const rowHeightPadding = this.rowHeightPadding; // Cache to avoid repeated getter calls

    visibleColumns.forEach((column: IdsDataGridColumn) => {
      // Special Columns
      if ((column.id === 'selectionCheckbox' || column.id === 'selectionRadio') && !column.width) {
        column.width = 45;
      }
      // Percent Columns
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') > -1) {
        colWidths += `minmax(${column.width}, 1fr) `;
      }
      // Other (fr)
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') === -1 && column.width !== 'max-content' && column.width.indexOf('ch') === -1) {
        colWidths += `${column.width} `;
      }
      // Character Width (ch) — depends on rowHeight padding, must recalculate
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') === -1 && column.width.indexOf('ch') > -1 && column.width !== 'max-content') {
        colWidths += `minmax(${(Number(column.width.replace('ch', '')) + (rowHeightPadding / 2) + 3)}ch, 1fr) `;
      }
      // Fixed pixel
      if (column.width && typeof column.width === 'number') {
        colWidths += `${column.width}px `;
      }
      // Max content
      if (column.width && typeof column.width === 'string' && column.width === 'max-content') {
        const maxContentWidth: any = this.#calcMaxContentWidth();
        colWidths += !maxContentWidth ? `minmax(110px, 1fr) ` : `minmax(${maxContentWidth[column.id]}px, 1fr) `;
      }
      // Default 110px or stretch to fit
      if (!column.width) {
        colWidths += `minmax(110px, 1fr) `;
      }
    });

    this.container?.style.setProperty('--ids-data-grid-column-widths', colWidths);
    this.#setColumnGroupsWidth();
    this.#setFrozenColumnWidth();
  }

  /**
   * Whether any visible column width depends on row-height padding.
   * @private
   * @returns {boolean} true when a column uses a `ch` or `max-content` width
   */
  get #hasRowHeightDependentWidths(): boolean {
    return this.columns?.some((column: IdsDataGridColumn) => typeof column.width === 'string'
      && (column.width === 'max-content' || column.width.indexOf('ch') > -1)) ?? false;
  }

  /**
   * Lightweight column widths update for column moves (avoids full redraw & max-content recalculation)
   * Only updates CSS grid template without triggering expensive calculations
   * @private
   * @returns {void}
   */
  #updateColumnWidthsOnly(): void {
    let colWidths = '';
    const visibleColumns = this.columns?.filter((column: IdsDataGridColumn) => !column.hidden) ?? [];

    visibleColumns.forEach((column: IdsDataGridColumn) => {
      // Special Columns
      if ((column.id === 'selectionCheckbox' || column.id === 'selectionRadio') && !column.width) {
        column.width = 45;
      }
      // Percent Columns
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') > -1) {
        colWidths += `minmax(${column.width}, 1fr) `;
      }
      // Other (fr)
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') === -1 && column.width !== 'max-content' && column.width.indexOf('ch') === -1) {
        colWidths += `${column.width} `;
      }
      // Character Width (ch)
      if (column.width && typeof column.width === 'string' && column.width.indexOf('%') === -1 && column.width.indexOf('ch') > -1 && column.width !== 'max-content') {
        colWidths += `minmax(${(Number(column.width.replace('ch', '')) + (this.rowHeightPadding / 2) + 3)}ch, 1fr) `;
      }
      // Fixed pixel
      if (column.width && typeof column.width === 'number') {
        colWidths += `${column.width}px `;
      }
      // Max content — skip expensive calculation, use cached/default
      if (column.width && typeof column.width === 'string' && column.width === 'max-content') {
        // Use cached max-content or default — avoid recalculation during column move
        const maxContentWidth: any = this.maxContentColumnWidths;
        colWidths += Object.keys(maxContentWidth).length > 0 && maxContentWidth[column.id]
          ? `minmax(${maxContentWidth[column.id]}px, 1fr) `
          : `minmax(110px, 1fr) `;
      }
      // Default 110px or stretch to fit
      if (!column.width) {
        colWidths += `minmax(110px, 1fr) `;
      }
    });

    // Update only the CSS variable — skip full width recalc & frozen column updates
    this.container?.style.setProperty('--ids-data-grid-column-widths', colWidths);
  }

  /**
   * Correct the position after render to prevent movement of columns when scrolling horizontally
   * Happens when some of the frozen columns don't have a width in the preset during build and render
   * @private
   * @returns {void}
   */
  #setFrozenColumnWidth(): void {
    let debounceFrozenCols: any = null;
    clearTimeout(debounceFrozenCols);
    debounceFrozenCols = setTimeout(() => {
      const frozenCols = this.shadowRoot?.querySelectorAll('.ids-data-grid-header-cell.frozen-left');
      if (!frozenCols) return;
      let widthIncrement = 0;
      for (let index = 0; index < frozenCols.length; index++) {
        if (index > 0) {
          const targetColWidth = frozenCols[index - 1].getBoundingClientRect().width;
          widthIncrement += targetColWidth;
          this.container?.style.setProperty(`--ids-data-grid-frozen-column-left-width-${index + 1}`, `${widthIncrement}px`);
        }
      }
    });
  }

  /**
   * Cached text widths for `max-content` columns (before row-height padding).
   * Survives row-height changes; invalidated on data/column changes.
   * @private
   */
  #maxContentTextWidths: Record<string, number> = {};

  /**
   * Resets cached `max-content` widths so they are recalculated on next access.
   * @returns {void}
   */
  resetMaxContentWidths(): void {
    this.maxContentColumnWidths = {};
  }

  /* Calculate an array with maxContentWidth for each column */
  #calcMaxContentWidth() {
    if (Object.keys(this.maxContentColumnWidths).length > 0) return this.maxContentColumnWidths;

    let context: CanvasRenderingContext2D | null | undefined;
    const measureTextWidth = (text: string, font: string) => {
      // Canvas created on first use — grids without `max-content` columns skip this entirely
      context ??= document.createElement('canvas').getContext('2d');
      if (!context) return 0;
      context.font = font;
      return context.measureText(text).width;
    };

    // Get only visible rows if virtual scroll is enabled
    const rowsToProcess = this.virtualScroll
      ? this.virtualRows.slice(0, this.virtualScrollSettings.MAX_ROWS_IN_DOM)
      : this.data;

    this.visibleColumns.forEach((column: IdsDataGridColumn, i: number) => {
      if (column.width === 'max-content') {
        const cachedTextWidth = this.#maxContentTextWidths[column.id];
        if (cachedTextWidth !== undefined) {
          this.maxContentColumnWidths[column.id] = cachedTextWidth + (this.rowHeightPadding * 2)
            + this.#maxContentExtraPadding(column);
          return;
        }

        let maxWidth = measureTextWidth((column.name || '').toString(), '600 16px arial');

        // Only process rows if there is data
        if (this.data.length > 0) {
          rowsToProcess.forEach((row) => {
            let cellText = '';

            // Use formatters for sizing if enabled
            if (column.useValueForSizing !== false && column.formatter) {
              // Get formatted value using the column's formatter
              let formatterResult = '';
              if (typeof column.formatter === 'function' && !column.formatter.hasOwnProperty('bind')) {
                formatterResult = column.formatter.call(this.formatters, row, column, i, this);
              } else if (typeof column.formatter === 'function') {
                formatterResult = column.formatter(row, column, i, this);
              }

              // Extract text from formatter result (might be HTML)
              if (typeof formatterResult === 'string') {
                // Remove HTML tags if the formatter returns HTML
                cellText = formatterResult.replace(/<[^>]*>/g, '');
              } else {
                // Fallback to original value if formatter doesn't return a string
                cellText = row[column.field || ''] ? row[column.field || ''].toString() : '';
              }
            } else {
              // Use original value without formatter
              cellText = row[column.field || ''] ? row[column.field || ''].toString() : '';

              // Check if this is a dropdown formatter and has editor settings
              if (typeof column?.formatter === 'function' && column?.formatter?.name === 'dropdown' && column?.editor?.editorSettings?.options) {
                // Find the matching option in the dropdown options
                const option = (column.editor.editorSettings.options as Array<{ value: any; label: string }>).find(
                  (opt) => opt.value.toString() === cellText
                );
                // Use the label if found, otherwise use the original value
                cellText = option?.label || cellText;
              }
            }

            const cellWidth = measureTextWidth(cellText, `400 16px arial`);
            if (cellWidth > maxWidth) {
              maxWidth = cellWidth;
            }
          });
        }

        // Add minimum width of 100px if column has a filter
        if (column?.filterType && !(Array.isArray(column.filterConditions) && column.filterConditions.length === 0)) {
          maxWidth = Math.max(maxWidth, 100);
        }

        this.#maxContentTextWidths[column.id] = maxWidth;
        this.maxContentColumnWidths[column.id] = maxWidth + (this.rowHeightPadding * 2)
          + this.#maxContentExtraPadding(column);
      }
    });

    return this.maxContentColumnWidths;
  }

  /**
   * Extra padding for badge/tag formatters in `max-content` columns.
   * @private
   * @param {IdsDataGridColumn} column The column info
   * @returns {number} The extra padding in pixels
   */
  #maxContentExtraPadding(column: IdsDataGridColumn): number {
    const formatterName = typeof column?.formatter === 'function' ? column.formatter.name : '';
    if (formatterName === 'badge' || formatterName === 'tag') return this.rowHeightPadding;
    return 10;
  }

  #maxContentColumnWidths: Record<string, number> = {};

  /**
   * Calculated `max-content` column widths, keyed by column id.
   * @returns {Record<string, number>} The calculated widths
   */
  get maxContentColumnWidths(): Record<string, number> {
    return this.#maxContentColumnWidths;
  }

  /**
   * Setting to empty forces re-measurement and clears text-width cache.
   * @param {Record<string, number>} value The calculated widths
   */
  set maxContentColumnWidths(value: Record<string, number>) {
    this.#maxContentColumnWidths = value ?? {};
    if (!Object.keys(this.#maxContentColumnWidths).length) this.#maxContentTextWidths = {};
  }

  /**
   * Utility function to get padding for each rowHeight,
   * note that there is a little extra padding than the css would be for better look.
   * @returns {string} The corresponding padding value
   */
  get rowHeightPadding(): number {
    const rowHeight = this.rowHeight;
    switch (rowHeight) {
      case 'xxs':
        return 8;
      case 'xs':
        return 9;
      case 'sm':
        return 10;
      case 'md':
        return 12;
      default:
        return 16;
    }
  }

  /**
   * Set one column's width (used for resizing)
   * @param {string} columnId The column id
   * @param {number} width The column id (or field) to sort
   */
  setColumnWidth(columnId: string, width: number) {
    const idx = this.columnIdxById(columnId);
    const column = this.columnDataById(columnId);

    // Constrain to a min and max width
    const minWidth = (column as any).minWidth || DEFAULT_COLUMN_MIN_WIDTH;
    const maxWidth = (column as any).maxWidth || Number.MAX_SAFE_INTEGER;

    if (this.columns[idx] && width >= minWidth && width <= maxWidth) {
      this.columns[idx].width = width;
      this.#setColumnWidths();
      this.#setColumnGroupsWidth();
    }
    this.triggerEvent('columnresized', this, { detail: { index: idx, column, columns: this.columns } });
    this.saveSettings?.();
  }

  /**
   * Set a column to visible or hidden
   * @param {string} columnId The column id
   * @param {boolean} visible True to show or false to hide
   */
  setColumnVisible(columnId: string, visible: boolean) {
    if (visible) {
      this.showColumn(columnId);
    } else {
      this.hideColumn(columnId);
    }
  }

  /**
   * Caches a filter field element for later cleanup and management.
   * Maintains a collection of filter trigger fields that can be properly disposed of when needed.
   * @param {IdsTriggerField} element The filter field element to cache
   */
  cacheFilterFields(element: IdsTriggerField) {
    if (!this.filterFields) {
      this.filterFields = [];
    }

    this.filterFields.push(element);
  }

  /**
   * Hide a column from view
   * @param {string} columnId The column id to hide
   * @param {boolean} skipColumnWidth Whether to skip recalculating column widths after hiding
   */
  hideColumn(columnId: string, skipColumnWidth = false) {
    const column = this.columnDataById(columnId);
    if (!column) return;
    if (column.hidden) return;

    column.hidden = true;
    column.hiddenAtRuntime = true;

    if (!skipColumnWidth) {
      this.#columnsToUpdate.add(columnId);
      this.#scheduleColumnVisibilityUpdate();
    }

    this.triggerEvent('columnhidden', this, {
      detail: { elem: this, columnId, column }
    });
  }

  /**
   * Show a previously hidden column
   * @param {string} columnId The column id
   */
  showColumn(columnId: string) {
    const column = this.columnDataById(columnId);
    if (!column) return;
    if (!column.hidden) return;

    const wasHidden = column.hidden;
    column.hidden = false;
    column.hiddenAtRuntime = false;

    // If column was initially hidden, insert the missing cell without regenerating the entire header
    if (wasHidden && !this.container?.querySelector(`[column-id="${columnId}"]`)) {
      const headerContainer = this.container?.querySelector('.ids-data-grid-header');
      if (headerContainer) {
        this.#insertHeaderCell(columnId, column);
        this.#header = undefined;
      }

      const bodyContainer = this.container?.querySelector('.ids-data-grid-body');
      if (bodyContainer) {
        bodyContainer.innerHTML = this.bodyInnerTemplate();
      }

      this.#setColumnWidths();
      this.#reapplyDirtyStates();
      this.filters?.attachFilterEventHandlers();
      this.filters?.attachFilterSettings();
    } else {
      this.#columnsToUpdate.add(columnId);
      this.#scheduleColumnVisibilityUpdate();
    }

    this.triggerEvent('columnshown', this, {
      detail: { elem: this, columnId, column }
    });
  }

  /**
   * Inserts a single column's header and filter cells into the existing header
   * without regenerating the entire header DOM.
   * @param {string} columnId The column id to insert
   * @param {IdsDataGridColumn} column The column data
   * @private
   */
  #insertHeaderCell(columnId: string, column: IdsDataGridColumn): void {
    const headerContainer = this.container?.querySelector('.ids-data-grid-header');
    if (!headerContainer) return;

    // Must match the `visibleColumns` order used to build the header and body rows.
    // Columns hidden at runtime keep their DOM cells, so they occupy a position too.
    const visibleColumns = this.columns.filter(
      (c: IdsDataGridColumn) => !c.hidden || c.hiddenAtRuntime || c.id === columnId
    );
    const insertIndex = visibleColumns.indexOf(column);
    const headerRow = headerContainer.querySelector('.ids-data-grid-row');
    if (!headerRow) return;

    // Use the new efficient method to generate only the header cell we need
    const newCell = IdsDataGridHeader.createHeaderCellElement(
      this,
      column,
      insertIndex
    );

    if (newCell) {
      const existingCells = headerRow.querySelectorAll('.ids-data-grid-header-cell:not(.ids-data-grid-header-cell-filter-wrapper)');
      const refCell = existingCells[insertIndex] || null;
      headerRow.insertBefore(newCell, refCell);

      // Re-index all header cells to match the regenerated body aria-colindex values
      const updatedCells = headerRow.querySelectorAll('.ids-data-grid-header-cell:not(.ids-data-grid-header-cell-filter-wrapper)');
      updatedCells.forEach((cell: Element, idx: number) => {
        cell.setAttribute(htmlAttributes.ARIA_COLINDEX, String(idx + 1));
      });
    }

    this.#header = undefined;
  }

  /**
   * Refreshes the frozen-last class on frozen columns after visibility changes.
   * Updates both header cells and body row cells to ensure the rightmost visible
   * frozen column is properly marked with the frozen-last class.
   * @returns {void}
   */
  refreshLeftFrozenHeaderClasses() {
    if (this.leftFrozenColumns.length > 0) {
      const nextFrozenIndex = this.#moveLeftFrozenHeaderCellClass();
      this.#moveLeftFrozenRowCellClass(nextFrozenIndex);
    }
  }

  /**
   * Updates the frozen-last class on header cells after column visibility changes.
   * Removes frozen-last from all frozen header cells and applies it to the rightmost visible frozen column.
   * @returns {number} The aria-colindex of the new last frozen column, or -1 if no frozen columns exist
   */
  #moveLeftFrozenHeaderCellClass() {
    const frozenCells = this.header?.querySelectorAll('.ids-data-grid-header-cell.frozen.frozen-left:not([hidden])');

    if (!frozenCells?.length) {
      return -1;
    }

    frozenCells.forEach((cell) => cell.classList.remove('frozen-last'));

    if (frozenCells.length > 0) {
      const lastFrozenCell = frozenCells[frozenCells.length - 1];
      lastFrozenCell.classList.add('frozen-last');
      return +(lastFrozenCell.ariaColIndex || -1);
    }

    return -1;
  }

  /**
   * Updates the frozen-last class on body row cells after column visibility changes.
   * Removes frozen-last from all body cells and applies it to cells in the specified column index.
   * @param {number} nextFrozenIndex The aria-colindex of the column that should receive the frozen-last class
   */
  #moveLeftFrozenRowCellClass(nextFrozenIndex: number) {
    if (nextFrozenIndex < 0) {
      return;
    }

    const frozenCells = this.shadowRoot?.querySelectorAll('.ids-data-grid-body .ids-data-grid-cell.frozen-last');

    if (!frozenCells?.length) {
      return;
    }

    frozenCells.forEach((cell) => cell.classList.remove('frozen-last'));

    const nextFrozenCells = this.shadowRoot?.querySelectorAll(`.ids-data-grid-body .ids-data-grid-cell[aria-colindex="${nextFrozenIndex}"]:not([hidden])`);
    if (nextFrozenCells?.length) {
      nextFrozenCells.forEach((cell) => cell.classList.add('frozen-last'));
    }
  }

  /**
   * Reapply dirty states to cells after column visibility changes.
   * This ensures that dirty cell states are maintained in the correct positions
   * when columns are shown/hidden.
   * @private
   */
  #reapplyDirtyStates() {
    // Loop through each row in the grid
    this.data.forEach((rowData: any, rowIndex: number) => {
      const row = this.rowByIndex(rowIndex);
      if (!row || !rowData.dirtyCells) return;

      // Loop through each dirty cell in the row
      rowData.dirtyCells.forEach((dirtyCell: any) => {
        // Get the column data for this dirty cell
        const column = this.columns[dirtyCell.cell];
        if (!column) return;

        // Find the new visible index for this column
        const visibleIndex = this.visibleColumns.findIndex((col: any) => col.id === column.id);

        // Only proceed if the column is visible
        if (visibleIndex !== -1) {
          dirtyCell.cell = visibleIndex;
        }
      });
    });
  }

  /**
   * Set a column's readonly state
   * @param {string} columnId The column id
   * @param {boolean} value True to make readonly, false to make editable
   */
  setColumnReadonly(columnId: string, value: boolean) {
    const columnIndex = this.visibleColumnIdxById(columnId);
    const column = this.columnDataById(columnId);
    if (!column) return;

    column.readonly = value;

    if (columnIndex === -1) return;

    this.visibleColumns[columnIndex].readonly = value;

    if (this.activeCell?.node) {
      const activeColIndex = stringToNumber(this.activeCell.node.getAttribute(htmlAttributes.ARIA_COLINDEX));
      const activeNode = this.activeCell.node as IdsDataGridCell;
      if (activeColIndex === columnIndex + 1 && activeNode.classList.contains('is-editing')) this.#endCellEditAndClear(activeNode);
    }

    this.#toggleColumnClass(columnIndex, value, 'is-readonly');
  }

  /**
   * Set a column's disabled state
   * @param {string} columnId The column id
   * @param {boolean} value True to make readonly, false to make editable
   */
  setColumnDisabled(columnId: string, value: boolean) {
    const columnIndex = this.visibleColumnIdxById(columnId);
    const column = this.columnDataById(columnId);
    if (!column) return;

    column.disabled = value;

    if (columnIndex === -1) return;

    this.visibleColumns[columnIndex].disabled = value;

    if (this.activeCell?.node) {
      const activeColIndex = stringToNumber(this.activeCell.node.getAttribute(htmlAttributes.ARIA_COLINDEX));
      const activeNode = this.activeCell.node as IdsDataGridCell;
      if (activeColIndex === columnIndex + 1 && activeNode.classList.contains('is-editing')) this.#endCellEditAndClear(activeNode);
    }

    this.#toggleColumnClass(columnIndex, value, 'is-disabled');
  }

  /**
   * End cell edit and clear cache to update classes
   * @param {IdsDataGridCell} cell datagrid cell to end edit
   */
  #endCellEditAndClear(cell: IdsDataGridCell) {
    if (!this.activeCellEditor) return;
    cell.endCellEdit(true);
    cell.focus();
  }

  /**
   * Add or remove a class in a column of cells
   * @param {number} columnIndex Column index
   * @param {boolean} addClass True if to add class in cell, false if not.
   * @param {string} className Class to add/remove in cell
   */
  #toggleColumnClass(columnIndex: number, addClass: boolean, className: string) {
    const cells = this.container?.querySelectorAll<IdsDataGridCell>(`[aria-colindex="${columnIndex + 1}"]`);
    cells?.forEach((cell: IdsDataGridCell) => {
      if (addClass) {
        cell.classList.add(className);
      } else {
        cell.classList.remove(className);
      }
    });
  }

  /**
   * Set a column's required state
   * @param {string} columnId The column id
   * @param {boolean} value True to make required, false to make optional
   */
  setColumnRequired(columnId: string, value: boolean) {
    const column = this.columnDataById(columnId);
    if (!column) return;

    column.required = value;

    // Update header cell UI only if column is visible
    const columnIndex = this.visibleColumnIdxById(columnId);
    if (columnIndex === -1) return;

    const headerCell = this.container?.querySelector(`[column-id="${columnId}"]`);
    if (headerCell) {
      if (value) {
        headerCell.classList.add('is-required');
      } else {
        headerCell.classList.remove('is-required');
      }
    }
  }

  /**
   * Set a column's validation rules
   * @param {string} columnId The column id
   * @param {string | undefined} validation The validation rule (e.g., 'required', 'email', etc.)
   * or undefined to remove validation
   */
  setColumnValidation(columnId: string, validation: string | undefined) {
    const column = this.columnDataById(columnId);
    if (!column) return;

    // Initialize editor if it doesn't exist
    if (!column.editor) {
      column.editor = { type: 'input' };
    }

    // Initialize editorSettings if it doesn't exist
    if (!column.editor.editorSettings) {
      column.editor.editorSettings = {};
    }

    // Set the validation
    if (validation === undefined) {
      delete column.editor.editorSettings.validate;
    } else {
      column.editor.editorSettings.validate = validation;
    }

    // Update header cell UI only if column is visible
    const columnIndex = this.visibleColumnIdxById(columnId);
    if (columnIndex === -1) return;

    const headerCell = this.container?.querySelector(`[column-id="${columnId}"]`);
    if (headerCell) {
      if (validation === 'required') {
        headerCell.classList.add('is-required');
      } else {
        headerCell.classList.remove('is-required');
      }
    }
  }

  /**
   * Set the column groups widths based on the provided colspans.
   * With some error handling.
   * @private
   */
  #setColumnGroupsWidth() {
    if (this.columnGroups) {
      let counter = 1;

      const groupElems = this.container?.querySelector('.ids-data-grid-column-groups')?.childNodes;
      this.columnGroups.forEach((group: IdsDataGridColumnGroup, index: number) => {
        let colspan = group.colspan;
        // decrease if hidden
        for (let i = 1; i <= colspan; i++) {
          if (this.columns[counter]?.hidden) {
            colspan -= 1;
          }
          counter++;
        }
        (groupElems?.item(index) as HTMLElement)?.style.setProperty('grid-column-start', `span ${colspan}`);
      });
    }
  }

  /**
   * Set the sort column and sort direction
   * @param {string} id The column id (or field) to sort
   * @param {boolean} ascending Sort ascending (lowest first) or descending (lowest last)
   */
  setSortColumn(id: string, ascending = true) {
    const column = this.columnDataById(id);
    const sortField = column?.field !== column?.id ? column?.field : column?.id;
    this.sortColumn = { id, ascending };

    // Detach any in-progress editor before sorting
    if (this.activeCellEditor?.isEditing) {
      this.activeCellEditor.detachEditor();
    }

    this.datasource.sort(sortField || '', ascending);
    this.spanTracker.clear();

    // Recalculate rowspan based on sorted data
    this.calculateRowspan();

    // Reset to page 1 when sorting with client-side pagination
    if (this.resetPageOnSort && this.pagination === 'client-side') {
      this.pageNumber = 1;
    }

    this.redrawBody();

    this.#activeIndicatorRow = this.data.findIndex((row: Record<string, unknown>) => row.isActiveRow === true);
    this.header?.setSortState(id, ascending);
    this.triggerEvent('sorted', this, { detail: { elem: this, sortColumn: this.sortColumn } });
    this.saveSettings?.();
  }

  /**
   * Set the sort column and sort direction on the UI only
   * @param {string} id The column id (or field) to set
   * @param {boolean} ascending Sort ascending (lowest first) or descending (lowest last)
   * @param {boolean} triggerEvent Trigger sorted event, set to true to trigger
   */
  setSortState(id: string, ascending = true, triggerEvent = false) {
    this.header?.setSortState(id, ascending);

    if (triggerEvent) {
      // Update sortColumn state so the event has current data
      this.sortColumn = { id, ascending };
      this.triggerEvent('sorted', this, { detail: { elem: this, sortColumn: this.sortColumn } });
    }
  }

  /**
   * Get column group data by given column group id
   * @param {string} columnGroupId The column group id
   * @returns {object} The column group data
   */
  columnGroupDataById(columnGroupId: string) {
    return this.columnGroups?.filter(
      (columnGroup: IdsDataGridColumnGroup) => columnGroup.id === columnGroupId
    )[0];
  }

  /**
   * Get column group index by given column group id
   * @param {string} columnGroupId The column group id
   * @returns {number} The column group index
   */
  columnGroupIdxById(columnGroupId: string): number {
    return this.columnGroups?.findIndex(
      (columnGroup: IdsDataGridColumn) => columnGroup.id === columnGroupId
    );
  }

  /**
   * Get column data by given column id
   * @param {string} columnId The column id
   * @returns {object} The column data
   */
  columnDataById(columnId: string) {
    return this.columns?.filter((column: IdsDataGridColumn) => column.id === columnId)[0];
  }

  /**
   * Get column index by given column id
   * @param {string} columnId The column id
   * @returns {number} The column index
   */
  columnIdxById(columnId: string): number {
    return this.columns?.findIndex((column: IdsDataGridColumn) => column.id === columnId);
  }

  /**
   * Get column index by given column id but only for visible columns
   * @param {string} columnId The column id
   * @returns {number} The column index
   */
  visibleColumnIdxById(columnId: string): number {
    return this.visibleColumns?.findIndex((column: IdsDataGridColumn) => column.id === columnId);
  }

  /**
   * Get the visible column data (via hidden attributes)
   * @returns {Array<IdsDataGridColumn>} the visible column data
   */
  get visibleColumns(): Array<IdsDataGridColumn> {
    return this.columns?.filter((column: IdsDataGridColumn) => !column.hidden || column.hiddenAtRuntime);
  }

  /**
   * Get the visible column data (via hidden attributes)
   * @returns {Array<IdsDataGridColumn>} the visible column data
   */
  get hiddenAtRuntimeColumns(): Array<IdsDataGridColumn> {
    return this.columns?.filter((column: IdsDataGridColumn) => column.hiddenAtRuntime);
  }

  /**
   * Get the columns frozen on the right
   * @returns {Array<IdsDataGridColumn>} the frozen column data
   */
  get rightFrozenColumns(): Array<IdsDataGridColumn> {
    return this.columns?.filter((column: IdsDataGridColumn) => !column.hidden && column.frozen === 'right');
  }

  /**
   * Get the columns frozen on the left
   * @returns {Array<IdsDataGridColumn>} the frozen column data
   */
  get leftFrozenColumns(): Array<IdsDataGridColumn> {
    return this.columns?.filter((column: IdsDataGridColumn) => !column.hidden && column.frozen === 'left');
  }

  /**
   * Check if any columns are frozen
   * @returns {boolean} true if any columns are frozen
   */
  get hasFrozenColumns(): boolean {
    return this.leftFrozenColumns.length > 0 || this.rightFrozenColumns.length > 0;
  }

  /**
   * Get column data by given column header element
   * @param {HTMLElement} elem The column header element
   * @returns {IdsDataGridColumn} The column data
   */
  columnDataByHeaderElem(elem: HTMLElement) {
    const columnId = elem?.getAttribute('column-id');
    return this.columnDataById(columnId || '');
  }

  // Private property for nextId getter/setter
  private nextIdValue?: number;

  /**
   * Get the next unique ID for new rows
   * @returns {number} The next unique ID
   */
  get nextId(): number {
    if (!this.nextIdValue) {
      // Initialize based on existing data
      const existingIds = this.data.map((row) => row[this.idColumn]).filter((id) => typeof id === 'number');
      this.nextIdValue = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
    }
    return this.nextIdValue++;
  }

  /**
   * Set the next unique ID counter
   * @param {number} value The next ID value
   */
  set nextId(value: number) {
    this.nextIdValue = value;
  }

  /**
   * Set to show header expander icon for expandable and tree rows
   * @param {boolean|string} value The value
   */
  set showHeaderExpander(value) {
    if (stringToBool(value)) this.setAttribute(attributes.SHOW_HEADER_EXPANDER, '');
    else this.removeAttribute(attributes.SHOW_HEADER_EXPANDER);
  }

  /**
   * Get the show header expander state
   * @returns {boolean} true if header expander is shown
   */
  get showHeaderExpander() {
    return this.hasAttribute(attributes.SHOW_HEADER_EXPANDER);
  }

  /**
   * Set a style on every alternate row for better readability.
   * @param {boolean|string} value true to use alternate row shading
   */
  set alternateRowShading(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.ALTERNATE_ROW_SHADING, 'true');
      this.shadowRoot?.querySelector('.ids-data-grid')?.classList.add('alt-row-shading');
      return;
    }

    this.shadowRoot?.querySelector('.ids-data-grid')?.classList.remove('alt-row-shading');
    this.setAttribute(attributes.ALTERNATE_ROW_SHADING, 'false');
  }

  /**
   * Get the alternate row shading state
   * @returns {boolean} true if alternate row shading is enabled
   */
  get alternateRowShading() {
    return stringToBool(this.getAttribute(attributes.ALTERNATE_ROW_SHADING)) || false;
  }

  /**
   * Strip the special characters in column ID
   * @param {boolean|string} value true to strip
   */
  set stripColumnId(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.STRIP_COL_ID, 'true');
      return;
    }

    this.setAttribute(attributes.STRIP_COL_ID, 'false');
  }

  /**
   * Get the strip column ID state
   * @returns {boolean} true if column IDs are stripped
   */
  get stripColumnId() {
    return stringToBool(this.getAttribute(attributes.STRIP_COL_ID)) || false;
  }

  /**
   * Setting for toggling row highlighting
   * @param {boolean} val If true, disables row highlighting
   */
  set disableRowHighlight(val: boolean) {
    const disabled = stringToBool(val);
    this.toggleAttribute(attributes.DISABLE_ROW_HIGHLIGHT, disabled);
  }

  /**
   * Get the disable row highlight state
   * @returns {boolean} true if row highlighting is disabled
   */
  get disableRowHighlight(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLE_ROW_HIGHLIGHT));
  }

  /**
   * Setting for toggling sorting
   * @param {boolean | string} val If true, disables sort
   */
  set disableClientSort(val: boolean | string) {
    const disabled = stringToBool(val);
    this.toggleAttribute(attributes.DISABLE_CLIENT_SORT, disabled);
  }

  /**
   * Get the disable client sort state
   * @returns {boolean | string} true if client sorting is disabled
   */
  get disableClientSort(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLE_CLIENT_SORT));
  }

  /**
   * Set whether to reset the page number to 1 when sorting changes
   * @param {boolean | string} val If true, resets to page 1 on sort
   */
  set resetPageOnSort(val: boolean | string) {
    this.toggleAttribute(attributes.RESET_PAGE_ON_SORT, stringToBool(val));
  }

  /**
   * Get whether the page number resets to 1 on sort
   * @returns {boolean} true if page resets on sort
   */
  get resetPageOnSort(): boolean {
    return stringToBool(this.getAttribute(attributes.RESET_PAGE_ON_SORT));
  }

  /**
   * Setting for suppressing select all events to improve performance
   * @param {boolean | string} val If true, suppresses select all events
   */
  set suppressSelectAllEvents(val: boolean | string) {
    const suppressed = stringToBool(val);
    this.toggleAttribute(attributes.SUPPRESS_SELECT_ALL_EVENTS, suppressed);
  }

  /**
   * Get the suppress select all events state
   * @returns {boolean} true if select all events are suppressed
   */
  get suppressSelectAllEvents(): boolean {
    return stringToBool(this.getAttribute(attributes.SUPPRESS_SELECT_ALL_EVENTS));
  }

  /**
   * Setting for disabling header checkbox
   * @param {boolean | string} val If true, disables header checkbox
   */
  set disableHeaderCheckbox(val: boolean | string) {
    // Skip setting when set to non-boolean value
    if (!isValidBooleanValue(val)) {
      return;
    }

    const disabled = stringToBool(val);
    this.toggleAttribute(attributes.DISABLE_HEADER_CHECKBOX, disabled);

    this.header?.disableHeaderCheckbox(disabled);
  }

  /**
   * Get the disable header checkbox state
   * @returns {boolean | string} true if header checkbox is disabled
   */
  get disableHeaderCheckbox(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLE_HEADER_CHECKBOX)) || false;
  }

  /**
   * Setting to enable the copy-paste handling that's based on
   * cell selection. Default is false.
   * @param {boolean|string|null} val If true, enables copy-paste handling
   */
  set cellCopyPaste(val: boolean | string | null) {
    this.toggleAttribute(attributes.CELL_COPY_PASTE, stringToBool(val));
  }

  /**
   * Get the cell copy paste state
   * @returns {boolean} true if cell copy paste is enabled
   */
  get cellCopyPaste(): boolean {
    return stringToBool(this.getAttribute(attributes.CELL_COPY_PASTE));
  }

  /**
   * Setting for hiding header checkbox
   * @param {boolean | string} val If true, disables header checkbox
   */
  set hideHeaderCheckbox(val: boolean | string) {
    // Skip setting when set to non-boolean value
    if (!isValidBooleanValue(val)) {
      return;
    }

    const hidden = stringToBool(val);
    this.toggleAttribute(attributes.HIDE_HEADER_CHECKBOX, hidden);

    this.header?.hideHeaderCheckbox(hidden);
  }

  /**
   * Get the hide header checkbox state
   * @returns {boolean | string} true if header checkbox is hidden
   */
  get hideHeaderCheckbox(): boolean | string {
    return stringToBool(this.getAttribute(attributes.HIDE_HEADER_CHECKBOX)) || false;
  }

  /**
   * Set the columns of the data grid
   * @param {Array} value The array to use
   */
  set columns(value: IdsDataGridColumn[] | undefined | null) {
    // Validate first if the column construction is properly aligned with the row selection
    if (value !== null && value !== undefined) {
      if (this.rowSelection === 'single') {
        if (value?.findIndex((obj: any) => obj.id === 'selectionRadio') < 0 && value?.findIndex((obj: any) => obj.id === 'selectionCheckbox') >= 0) {
          const selectionIndex = value?.findIndex((obj: any) => obj.id === 'selectionCheckbox');
          value[selectionIndex].id = 'selectionRadio';
          value[selectionIndex].formatter = this.formatters.selectionRadio;
        }
      } else if (this.rowSelection === 'mixed' || this.rowSelection === 'multiple') {
        if (value?.findIndex((obj: any) => obj.id === 'selectionCheckbox') < 0 && value?.findIndex((obj: any) => obj.id === 'selectionRadio') >= 0) {
          const selectionIndex = value?.findIndex((obj: any) => obj.id === 'selectionRadio');
          value[selectionIndex].id = 'selectionCheckbox';
          value[selectionIndex].formatter = this.formatters.selectionCheckbox;
        }
      }

      // Edit fields as needed, currently for stripping special characters in id
      if (this.stripColumnId) {
        value.forEach((column: IdsDataGridColumn) => {
          column.id = column.id.replace(/[^a-zA-Z0-9:_-]/g, '');
        });
      }
    }

    this.resetCache();
    this.currentColumns = value ? value.map((col) => ({ ...col })) : [{ id: '', name: '' }];
    this.#memoizeFunctionColumns();
    this.#maxColumnHeight = undefined;
    this.resetMaxContentWidths();
    this.redraw();
  }

  /**
   * Get the columns of the data grid
   * @returns {IdsDataGridColumn[]} the column array
   */
  get columns(): IdsDataGridColumn[] {
    return this.currentColumns || ([{ id: '', name: '', field: '' }]);
  }

  /**
   * Get the max `column.height` across visible columns. Cached and recomputed lazily; reset
   * whenever the columns setter runs. Rows use this to short-circuit the per-cell inline-height
   * cleanup loop on virtual-scroll row recycles when no column defines a height (common case).
   * @returns {number} the largest column height (0 when no columns define one)
   */
  get maxColumnHeight(): number {
    if (this.#maxColumnHeight === undefined) {
      let max = 0;
      const cols = this.currentColumns ?? [];
      for (let i = 0; i < cols.length; i++) {
        const col = cols[i];
        if (col.hidden) continue;
        const h = col.height;
        if (h && h > max) max = h;
      }
      this.#maxColumnHeight = max;
    }
    return this.#maxColumnHeight;
  }

  /**
   * Set the columns groups of the data grid
   * @param {Array} value The array to use
   */
  set columnGroups(value) {
    this.state.columnsGroups = value;
    this.redraw();
  }

  /**
   * Get the column groups of the data grid
   * @returns {any} the column groups array
   */
  get columnGroups() { return this.state?.columnsGroups || null; }

  /**
   * Use this to add more data to the datagrid's existing dataset.
   * This will automatically render additional rows in the datagrid.
   * @param {Array} value The array to use
   */
  appendData(value: Array<Record<string, any>>) {
    if (!value?.length) return;

    if (this.virtualScroll) {
      if (this.activeCellEditor) {
        const rowIndex = this.activeCell.row;
        const cell = this.activeCell.cell;
        const activeNode = this.activeCell.node;
        const currentValue = activeNode?.editor?.value?.() ?? this.#activeCellCache?.currentValue ?? '';
        this.updateActiveCellCache(rowIndex, cell, currentValue, activeNode?.isEditing);
      }

      if (this.#activeCellCache) {
        this.#activeCellCache.shouldReinstateCell = true;
      }

      // NOTE: using originalData skips pagination-logic; it's ok in context of infinite-scroll
      this.datasource.data = [...this.datasource.currentData, ...value];
      this.header?.resetSortStates();
      this.resetCache();

      if (this.treeGrid) {
        this.#recycleAllTreeRows(Math.floor(this.container!.scrollTop / this.virtualScrollSettings.ROW_HEIGHT));
        this.#restoreActiveCellAfterScroll();
      } else {
        this.#appendMissingRows();
        this.#restoreActiveCellAfterScroll();
      }

      // Fire scrollend if content still doesn't overflow after append
      this.#initVirtualScrollHeight();
      if (this.container) {
        requestAnimationFrame(() => this.#triggerScrollEndIfNotOverflowing());
      }
    } else {
      this.data = this.data.concat(value);
    }
  }

  /* Append missing rows for virtual-scrolling */
  #appendMissingRows() {
    if (!this.virtualScroll) return;

    const data = this.data;
    const rows = this.rows;
    if (!data.length || !rows.length) return;

    const { MAX_ROWS_IN_DOM, ROW_HEIGHT } = this.virtualScrollSettings;

    const rowsNeeded = Math.min(data.length, MAX_ROWS_IN_DOM) - rows.length;
    const missingRows: any[] = [];

    const lastRow: any = rows[rows.length - 1];
    const lastRowIndex = lastRow?.rowIndex || 0;

    while (missingRows.length < rowsNeeded) {
      const rowIndex = lastRowIndex + missingRows.length + 1;
      const rowData = data[rowIndex];
      if (rowData) {
        const clonedRow = IdsDataGridRow.template(data[rowIndex], rowIndex, rowIndex + 1, this);
        missingRows.push(clonedRow);
      }
    }

    if (missingRows.length && this.body) {
      this.body.insertAdjacentHTML('beforeend', missingRows.join(''));
    }

    // trigger recycling if missingRows is 0 because MAX_ROWS_IN_DOMS has
    // been reached but there are still new row data to be rendered
    if (missingRows.length === 0 && data.length - 1 > lastRowIndex) {
      this.#handleVirtualScroll(ROW_HEIGHT);
    }
  }

  /**
   * Process rowspan data for a row
   * @param {any} rowData the row data
   * @param {number} rowIndex the row index
   */
  processRowspan(rowData: any, rowIndex: number): void {
    if (rowData.rowspan) {
      Object.keys(rowData.rowspan).forEach((columnId) => {
        const spanCount = rowData.rowspan[columnId];
        // Track which cells will be spanned in future rows
        for (let i = 1; i < spanCount; i++) {
          this.spanTracker.set(`${rowIndex + i}-${columnId}`, spanCount - i);
        }
      });
    }
  }

  /**
   * Calculate rowspan for columns that have consecutive repeating values
   * @private
   */
  calculateRowspan(): void {
    this.spanTracker.clear();

    // Clear existing rowspan data
    this.data.forEach((row) => {
      delete row.rowspan;
    });

    // Find columns that should have rowspan (those marked with rowspan: true)
    const rowspanColumns = this.columns.filter((col) => col.rowspan === true).map((col) => col.id);

    if (!rowspanColumns.length) return;

    rowspanColumns.forEach((columnId) => {
      let currentGroup: number[] = [];
      let currentValue: any = null;

      for (let i = 0; i < this.data.length; i++) {
        const rowValue = this.data[i][columnId];

        if (rowValue === currentValue && currentValue !== null && currentValue !== '') {
          currentGroup.push(i);
        } else {
          // Process previous group if it has multiple rows
          if (currentGroup.length > 1) {
            const firstRowIndex = currentGroup[0];
            if (!this.data[firstRowIndex].rowspan) {
              this.data[firstRowIndex].rowspan = {};
            }
            this.data[firstRowIndex].rowspan[columnId] = currentGroup.length;
          }

          // Start new group
          currentGroup = [i];
          currentValue = rowValue;
        }
      }

      // Process final group
      if (currentGroup.length > 1) {
        const firstRowIndex = currentGroup[0];
        if (!this.data[firstRowIndex].rowspan) {
          this.data[firstRowIndex].rowspan = {};
        }
        this.data[firstRowIndex].rowspan[columnId] = currentGroup.length;
      }
    });

    // Process the calculated rowspan data
    this.data.forEach((rowData, rowIndex) => {
      this.processRowspan(rowData, rowIndex);
    });
  }

  /**
   * Update the height of all rowspan cells when row height changes
   * @private
   */
  #updateRowspanCellHeights() {
    // Skip when no column uses rowspan
    if (!this.columns.some((column: IdsDataGridColumn) => column.rowspan === true)) return;

    const rowspanCells = this.container?.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell[rowspan]');
    rowspanCells?.forEach((cell) => {
      const spanCount = parseInt(cell.getAttribute('rowspan') || '1');
      if (spanCount > 1) {
        cell.setRowspan(spanCount);
      }
    });
  }

  /**
   * Check if cell should be skipped due to spanning
   * @param {number} rowIndex the row index
   * @param {string} columnId the column id
   * @returns {boolean} true if cell should be skipped
   */
  shouldSkipCell(rowIndex: number, columnId: string): boolean {
    return this.spanTracker.has(`${rowIndex}-${columnId}`);
  }

  /**
   * Find a cell by logical position, accounting for rowspan
   * @param {number} rowIndex the row index
   * @param {number} columnIndex the logical column index
   * @returns {IdsDataGridCell | null} the cell at the logical position
   */
  findCellByLogicalPosition(rowIndex: number, columnIndex: number): IdsDataGridCell | null {
    if (rowIndex < 0 || rowIndex >= this.data.length) return null;
    if (columnIndex < 0 || columnIndex >= this.visibleColumns.length) return null;

    // Skip hidden rows (tree grid or collapsed groups)
    if (this.data[rowIndex]?.rowHidden) return null;

    const rowData = this.data[rowIndex];
    const row = this.rowByIndex(rowIndex);
    if (!row) return null;

    // Group header rows have a single spanning cell; group footer rows may have
    // merged cells. In both cases resolve the cell at the column or fall back.
    if (rowData?.isGroupRow || rowData?.isGroupFooterRow) {
      const columnId = this.visibleColumns[columnIndex]?.id;
      return row.querySelector<IdsDataGridCell>(`ids-data-grid-cell[column-id="${columnId}"]`)
        ?? row.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${columnIndex + 1}"]`)
        ?? row.querySelector<IdsDataGridCell>('ids-data-grid-cell')
        ?? null;
    }

    const columnId = this.visibleColumns[columnIndex]?.id;
    if (!columnId) return null;

    // Check if this position is covered by a rowspan from a previous row
    if (this.shouldSkipCell(rowIndex, columnId)) {
      // Find the row that contains the spanning cell
      for (let prevRowIndex = rowIndex - 1; prevRowIndex >= 0; prevRowIndex--) {
        const prevRowData = this.data[prevRowIndex];
        const spanCount = prevRowData?.rowspan?.[columnId];
        if (spanCount && spanCount > 1 && (prevRowIndex + spanCount - 1) >= rowIndex) {
          // This cell spans into the current position
          const prevRow = this.rowByIndex(prevRowIndex);
          return prevRow?.cellByLogicalIndex?.(columnIndex) ?? prevRow?.cellByIndex(columnIndex) ?? null;
        }
      }
    }

    return row.cellByLogicalIndex?.(columnIndex) ?? row.cellByIndex(columnIndex) ?? null;
  }

  /**
   * Find the next navigable cell in a given direction
   * @param {IdsDataGridCell} currentCell the current cell
   * @param {IdsDirection} direction the navigation direction
   * @returns {IdsDataGridCell | null} the next navigable cell
   */
  findNextNavigableCell(currentCell: IdsDataGridCell, direction: IdsDirection): IdsDataGridCell | null {
    const currentRowIndex = currentCell.rowIndex;
    const currentColumnIndex = currentCell.columnIndex;

    switch (direction) {
      case IdsDirection.Up: {
        if (currentRowIndex === 0) return null;
        // A group header is the top of its group — can't navigate above it
        if (this.data[currentRowIndex]?.isGroupRow) return null;

        for (let targetRowIndex = currentRowIndex - 1; targetRowIndex >= 0; targetRowIndex--) {
          // Stop if we reach the previous group's footer (different group)
          if (this.data[targetRowIndex]?.isGroupFooterRow) return null;
          const cell = this.findCellByLogicalPosition(targetRowIndex, currentColumnIndex);
          if (cell) return cell;
        }
        return null;
      }
      case IdsDirection.Down: {
        if (currentRowIndex >= this.data.length - 1) return null;
        // A group footer is the bottom of its group — can't navigate below it
        if (this.data[currentRowIndex]?.isGroupFooterRow) return null;

        // Check if current cell has rowspan that extends beyond current row
        const currentRowData = this.data[currentRowIndex];
        const columnId = this.visibleColumns[currentColumnIndex]?.id;
        const currentSpan = currentRowData?.rowspan?.[columnId] || 1;

        const startRowIndex = currentRowIndex + currentSpan;
        for (let targetRowIndex = startRowIndex; targetRowIndex < this.data.length; targetRowIndex++) {
          // Stop if we reach the next group's header (different group)
          if (this.data[targetRowIndex]?.isGroupRow) return null;
          const cell = this.findCellByLogicalPosition(targetRowIndex, currentColumnIndex);
          if (cell) return cell;
        }
        return null;
      }
      case IdsDirection.Left: {
        // Try to find the previous cell in the same row
        for (let colIndex = currentColumnIndex - 1; colIndex >= 0; colIndex--) {
          const cell = this.findCellByLogicalPosition(currentRowIndex, colIndex);
          if (cell && cell !== currentCell) return cell;
        }
        // Wrap to previous row's last cell, but don't cross a group boundary
        const prevRow = this.data[currentRowIndex - 1];
        if (currentRowIndex > 0 && !prevRow?.isGroupRow && !prevRow?.isGroupFooterRow) {
          for (let colIndex = this.visibleColumns.length - 1; colIndex >= 0; colIndex--) {
            const cell = this.findCellByLogicalPosition(currentRowIndex - 1, colIndex);
            if (cell) return cell;
          }
        }
        return null;
      }
      case IdsDirection.Right: {
        // Try to find the next cell in the same row
        for (let colIndex = currentColumnIndex + 1; colIndex < this.visibleColumns.length; colIndex++) {
          const cell = this.findCellByLogicalPosition(currentRowIndex, colIndex);
          if (cell && cell !== currentCell) return cell;
        }
        // Wrap to next row's first cell, but don't cross a group boundary
        const nextRow = this.data[currentRowIndex + 1];
        if (currentRowIndex < this.data.length - 1 && !nextRow?.isGroupRow && !nextRow?.isGroupFooterRow) {
          for (let colIndex = 0; colIndex < this.visibleColumns.length; colIndex++) {
            const cell = this.findCellByLogicalPosition(currentRowIndex + 1, colIndex);
            if (cell) return cell;
          }
        }
        return null;
      }
      default:
        return null;
    }
  }

  /**
   * Set the data of the data grid
   * @param {Array} value The array to use
   */
  set data(value: Array<Record<string, any>>) {
    // Detach any in-progress editor before applying
    if (this.activeCellEditor?.isEditing) {
      this.activeCellEditor.detachEditor();
    }

    this.spanTracker.clear();
    let filterConditions: Array<IdsDataGridFilterConditions> = [];
    if (value) {
      hideEmptyMessage.apply(this);
      if (!this.datasource) this.datasource = new IdsDataSource();
      this.datasource.flatten = this.treeGrid;
      this.datasource.groupable = this.groupable;
      this.datasource.data = value;
      if (this.pager && this.pagination !== 'server-side') this.pager.total = value.length;
      if ((this.initialized && this.columns.length && this.pagination !== 'server-side')) {
        if (this.keepFilterConditions) filterConditions = this.filterConditions;
        this.redrawBody();
        this.resetMaxContentWidths();
        this.#setColumnWidths();
        this.#updateRowCount();
        this.syncPagerAfterDatasetChange();
        if (this.keepFilterConditions && filterConditions.length > 0) {
          requestAnimationFrame(() => {
            this.filters.filterIsProcessing = false;
            this.filters.setFilterConditions(filterConditions);
          });
        }
        this.toggleEmptyMessage();
        return;
      }

      this.initialized = true;
      if (this.pagination === 'server-side') this.syncServerSelections();
      this.resetMaxContentWidths();

      if (this.keepFilterConditions) filterConditions = this.filterConditions;

      // Calculate rowspan for consecutive repeating values
      this.calculateRowspan();

      if (this.pagination === 'server-side') {
        this.redrawBody();
      } else {
        this.redraw();
      }

      this.restoreAllSettings?.();
      if (this.keepFilterConditions && filterConditions.length > 0) {
        requestAnimationFrame(() => {
          this.filters.filterIsProcessing = false;
          this.filters.setFilterConditions(filterConditions);
        });
      }
    } else {
      this.datasource.data = [];
    }
  }

  /**
   * Get the data of the data grid
   * @returns {Array<Record<string, any>>} the data array
   */
  get data(): Array<Record<string, any>> { return this?.datasource?.data || []; }

  /**
   * Set empty message description
   * @param {string} value The value
   */
  set emptyMessageDescription(value: string | null) {
    if (typeof value === 'string') {
      this.setAttribute(attributes.EMPTY_MESSAGE_DESCRIPTION, value);
    } else {
      this.removeAttribute(attributes.EMPTY_MESSAGE_DESCRIPTION);
    }
    setEmptyMessage.apply(this);
  }

  get emptyMessageDescription(): string | null {
    return this.getAttribute(attributes.EMPTY_MESSAGE_DESCRIPTION);
  }

  /**
   * Set empty message icon
   * @param {string} value The value
   */
  set emptyMessageIcon(value: string | null) {
    if (typeof value === 'string') {
      this.setAttribute(attributes.EMPTY_MESSAGE_ICON, value);
    } else {
      this.removeAttribute(attributes.EMPTY_MESSAGE_ICON);
    }
    setEmptyMessage.apply(this);
  }

  get emptyMessageIcon(): string | null {
    return this.getAttribute(attributes.EMPTY_MESSAGE_ICON);
  }

  /**
   * Set empty message label
   * @param {string} value The value
   */
  set emptyMessageLabel(value: string | null) {
    if (typeof value === 'string') {
      this.setAttribute(attributes.EMPTY_MESSAGE_LABEL, value);
    } else {
      this.removeAttribute(attributes.EMPTY_MESSAGE_LABEL);
    }
    setEmptyMessage.apply(this);
  }

  get emptyMessageLabel(): string | null {
    return this.getAttribute(attributes.EMPTY_MESSAGE_LABEL);
  }

  /**
   * Set header menu id
   * @param {string} value The header menu id
   */
  set headerMenuId(value) {
    if (value) {
      this.setAttribute(attributes.HEADER_MENU_ID, value);
      return;
    }
    this.removeAttribute(attributes.HEADER_MENU_ID);
  }

  get headerMenuId() { return this.getAttribute(attributes.HEADER_MENU_ID); }

  /**
   * Set the opacity value applied to header cells with headerOpacity enabled
   * @param {string | number | null} value The opacity value (e.g. '0.2' or 0.2)
   */
  set headerOpacityValue(value: string | number | null) {
    if (value !== null && value !== undefined) {
      const numVal = parseFloat(String(value));
      if (Number.isNaN(numVal)) return;
      const clamped = Math.max(0, Math.min(1, numVal));
      this.setAttribute(attributes.HEADER_OPACITY_VALUE, String(clamped));
      this.container?.style.setProperty('--ids-data-grid-header-cell-opacity', String(clamped));
    } else {
      this.removeAttribute(attributes.HEADER_OPACITY_VALUE);
      this.container?.style.removeProperty('--ids-data-grid-header-cell-opacity');
    }
  }

  get headerOpacityValue(): string | null { return this.getAttribute(attributes.HEADER_OPACITY_VALUE); }

  /**
   * Toggle the header opacity state on a specific column without a full redraw
   * @param {string} columnId The column id to toggle
   * @param {boolean} enable Whether to enable or disable the opacity
   */
  setColumnHeaderOpacity(columnId: string, enable: boolean) {
    const column = this.columnDataById(columnId);
    if (!column) return;

    column.headerOpacity = enable;
    const headerCell = this.header?.querySelector(`[column-id="${columnId}"]`);
    if (headerCell) {
      headerCell.classList.toggle('has-opacity', enable);
      headerCell.setAttribute('tabindex', enable ? '-1' : '0');
      if (enable) {
        headerCell.setAttribute('inert', '');
      } else {
        headerCell.removeAttribute('inert');
      }
    }
  }

  /**
   * Set the header menu data
   * @param {Array} value The array to use
   */
  set headerMenuData(value) {
    if (this?.header?.state) {
      this.header.state.headerMenuData = value;
    }
    if (!this.isDynamicContextmenu) {
      const headerMenu: any = getContextmenuElem.apply(this, [true]);
      if (headerMenu) headerMenu.data = value;
      setContextmenu.apply(this);
    }
    this.isDynamicContextmenu = false;
  }

  get headerMenuData() { return this?.header?.state?.headerMenuData; }

  /**
   * Set menu id
   * @param {string} value The menu id
   */
  set menuId(value) {
    if (value) {
      this.setAttribute(attributes.MENU_ID, value);
      return;
    }
    this.removeAttribute(attributes.MENU_ID);
  }

  get menuId() { return this.getAttribute(attributes.MENU_ID); }

  /**
   * Set the menu data
   * @param {Array} value The array to use
   */
  set menuData(value) {
    this.state.menuData = value;
    if (!this.isDynamicContextmenu) {
      const menu: any = getContextmenuElem.apply(this);
      if (menu) menu.data = value;
      setContextmenu.apply(this);
    }
    this.isDynamicContextmenu = false;
  }

  get menuData() { return this?.state?.menuData; }

  /**
   * Set the list view to use virtual scrolling for a large amount of rows
   * @param {boolean | string} value true to use virtual scrolling
   */
  set virtualScroll(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.VIRTUAL_SCROLL, 'true');
    } else {
      this.removeAttribute(attributes.VIRTUAL_SCROLL);
    }
    this.redraw();
  }

  get virtualScroll(): boolean | string { return stringToBool(this.getAttribute(attributes.VIRTUAL_SCROLL)); }

  /**
   * Set the overflow property to enable/disable scrollbars
   * @param {string} val for the overflow property
   */
  set overflow(val: string) {
    const finalVal = val && val.trim().length > 0 ? val.trim() : 'auto';
    this.setAttribute(attributes.OVERFLOW, finalVal);
    this.container?.style.setProperty('overflow', finalVal);
  }

  get overflow(): string {
    return this.getAttribute(attributes.OVERFLOW) ?? 'auto';
  }

  /**
   * Some future configurable virtual scroll settings
   * @returns {object} the current settings
   */
  get virtualScrollSettings() {
    const ENABLED = !!this.virtualScroll;
    const ROW_HEIGHT = this.rowPixelHeight || 50;
    const MAX_ROWS_IN_DOM = this.#virtualScrollMaxRowsInDom;
    const BUFFER_ROWS = Math.ceil(MAX_ROWS_IN_DOM * 0.25);

    return {
      ENABLED,
      ROW_HEIGHT,
      MAX_ROWS_IN_DOM,
      BUFFER_ROWS
    };
  }

  /** Max rows to keep in the DOM during virtual scrolling. */
  #virtualScrollMaxRowsInDom = 80;

  /**
   * Setting for max virtual scroll rows in DOM
   * @param {number} virtualRows number of rows
   */
  set scrollMaxRows(virtualRows: number) {
    const prevMaxRows = this.#virtualScrollMaxRowsInDom;
    const newMaxRows = Number(virtualRows);
    this.#virtualScrollMaxRowsInDom = !Number.isNaN(newMaxRows) ? newMaxRows : prevMaxRows;

    // if max rows changed, refresh virtual scroll rows
    if (!this.virtualScroll || prevMaxRows === newMaxRows) return;

    const rowIndex = Math.floor(this.container!.scrollTop / this.virtualScrollSettings.ROW_HEIGHT);
    if (this.treeGrid) {
      this.#recycleAllTreeRows(rowIndex);
    } else {
      // Note: Non-tree grids need testing for dynamic max rows
      this.#recycleAllRows(rowIndex);
    }
  }

  get scrollMaxRows(): number {
    return this.#virtualScrollMaxRowsInDom;
  }

  /* Attach Events for global scrolling */
  #attachScrollEvents() {
    const virtualScrollSettings = this.virtualScrollSettings;

    this.offEvent('scroll.data-grid', this.container);
    this.onEvent('scroll.data-grid', this.container, () => {
      const scrollTop = this.container!.scrollTop;
      // This handler runs before the virtual-scroll handler (registered first), so it
      // owns detecting whether the vertical position changed for both handlers.
      const isVerticalScroll = scrollTop !== this.#lastScrollTop;
      this.#lastScrollTop = scrollTop;
      this.#lastScrollWasVertical = isVerticalScroll;
      this.#currentScroll.top = scrollTop;
      this.#currentScroll.left = this.container!.scrollLeft;

      // Infinite-scroll start/end detection only applies to vertical scrolling. Reading
      // scrollHeight/offsetHeight forces a layout reflow, so skip it on horizontal scroll
      // (a very expensive reflow on wide grids that made horizontal scrolling stutter).
      if (!isVerticalScroll) return;

      const virtualRowHeight = virtualScrollSettings.ROW_HEIGHT;
      const rowIndex = Math.floor(scrollTop / virtualRowHeight);
      const rows = this.rows;

      const reachedTheTop = rowIndex <= 0;
      const reachedTheBottom = this.container!.offsetHeight + this.container!.scrollTop >= this.container!.scrollHeight;

      if (reachedTheTop) {
        const firstRow: any = rows[0];
        this.#triggerCustomScrollEvent(firstRow?.rowIndex ?? 0, 'start');
      }
      if (reachedTheBottom) {
        const lastRowIndex = this.datasource.originalData.length - 1;
        this.#triggerCustomScrollEvent(lastRowIndex, 'end');
        this.#checkLastRowBorder();
      }
      if (!reachedTheTop && !reachedTheBottom) {
        this.#triggerCustomScrollEvent(0);
      }
    }, { capture: true, passive: true }); // @see https://javascript.info/bubbling-and-capturing#capturing
    this.#attachVirtualScrollEvent();
  }

  /* Attach Events for virtual scrolling */
  #attachVirtualScrollEvent() {
    if (!this.virtualScroll) return;

    const virtualScrollSettings = this.virtualScrollSettings;
    const virtualRowHeight = virtualScrollSettings.ROW_HEIGHT;

    let onscreenRaf: number | null = null;
    this.offEvent('scroll.data-grid.virtual-scroll', this.container);
    this.onEvent('scroll.data-grid.virtual-scroll', this.container, (evt) => {
      evt.stopImmediatePropagation();

      // On a horizontal-only scroll the rows don't move vertically, so skip the entire
      // vertical virtual-scroll pipeline. That pipeline mutates body transform/padding
      // and reads layout, which — interleaved with the column measurements below —
      // forced expensive synchronous reflows on every horizontal scroll tick.
      if (this.#lastScrollWasVertical) {
        // Collapse expanded cells visually but keep tracking for re-expand
        this.collapseCell(false);

        this.#handleVirtualScroll(virtualRowHeight);

        this.#restoreActiveCellAfterScroll();
        this.#restoreEditedCellsAfterScroll();
      }

      // The on-screen column set can change on either axis, so always recompute it.
      // Schedule on the next animation frame (instead of a setTimeout) so the column
      // measurement + re-render lands with the scroll's paint, keeping newly visible
      // cells in sync with the frame rather than a beat behind. This is the only work
      // needed for a horizontal scroll.
      if (!this.treeGrid) {
        if (onscreenRaf !== null) cancelAnimationFrame(onscreenRaf);
        onscreenRaf = requestAnimationFrame(() => {
          onscreenRaf = null;
          if (!this.isConnected) return;
          this.#calculateColumnsOnscreen();
        });
      }
    }, { capture: true, passive: true });// @see https://javascript.info/bubbling-and-capturing#capturing

    let debounceScrollend: any = null;
    this.offEvent('scrollend.data-grid.virtual-scroll', this.container);
    this.onEvent('scrollend.data-grid.virtual-scroll', this.container, (evt) => {
      evt.stopImmediatePropagation();

      if (!this.treeGrid) {
        clearTimeout(debounceScrollend);
        debounceScrollend = setTimeout(() => {
          const isEditing = this.activeCellEditor
            || this.activeCell?.node?.classList.contains('is-editing');
          if (isEditing) return;
          this.#handleVirtualScroll(virtualRowHeight);
        }, 500);
      }

      if (document.activeElement?.nodeName === 'BODY' && this.#activeCellCache?.hasLeaveViewport) {
        this.container?.focus({ preventScroll: true });
      }

      if (this.#afterSetActiveCellFn) {
        this.#afterSetActiveCellFn();
        this.#afterSetActiveCellFn = undefined;
      }
    }, { capture: true, passive: true });// @see https://javascript.info/bubbling-and-capturing#capturing

    this.offEvent('rowexpanded.data-grid.virtual-scroll', this);
    this.onEvent('rowexpanded.data-grid.virtual-scroll', this, (evt: CustomEvent) => {
      this.#handleTreeRowExpandCollapse(true, evt.detail);
    });

    this.offEvent('rowcollapsed.data-grid.virtual-scroll', this);
    this.onEvent('rowcollapsed.data-grid.virtual-scroll', this, (evt: CustomEvent) => {
      this.#handleTreeRowExpandCollapse(false, evt.detail);
    });
  }

  /**
   * Adds "column-onscreen" attribute to any column-header that is in browser window viewport
   * @returns {void}
   * @see IdsDataGridCell.isOnScreen()
   */
  #calculateColumnsOnscreen(): void {
    const headerColumns = this.header?.columns;
    if (!headerColumns || headerColumns.length < this.TOO_MANY_COLUMNS) return;

    const columnSettings = this.columns;

    // Column indexes that just transitioned from off-screen to on-screen. Only these
    // columns' cells need re-rendering: their content may be stale after vertical-scroll
    // recycling skipped them while off-screen. Columns that stayed on-screen are already
    // up to date, and columns that moved off-screen are no longer visible.
    const enteredColumnIndexes: number[] = [];

    headerColumns?.forEach((column: HTMLElement) => {
      const columnIndex = (Number(column.getAttribute(htmlAttributes.ARIA_COLINDEX) ?? 1) - 1);
      const wasOnScreen = column.hasAttribute('column-onscreen');
      const columnDimensions = column.getBoundingClientRect();
      const columnLeftEdge = columnDimensions.x;
      const columnRightEdge = columnLeftEdge + columnDimensions.width;
      // Symmetric look-ahead buffer on both edges so columns render before they are
      // visible (previously only the right edge had a 300px buffer and the left had 0,
      // which made columns "load in" as they crossed the left edge).
      const buffer = this.COLUMN_ONSCREEN_BUFFER;
      const viewportRight = window.innerWidth;

      const isHidden = columnSettings[columnIndex]?.hidden;
      const isOffScreenLeft = columnRightEdge < -buffer;
      const isOffScreenRight = columnLeftEdge > viewportRight + buffer;
      const isOffScreen = isOffScreenLeft || isOffScreenRight;
      const isOnScreen = !isOffScreen && !isHidden;

      column.toggleAttribute('column-onscreen', isOnScreen);
      column.toggleAttribute('column-offscreen', isOffScreen);
      column.toggleAttribute('column-offscreen-left', isOffScreenLeft);
      column.toggleAttribute('column-offscreen-right', isOffScreenRight);

      if (isOnScreen && !wasOnScreen) enteredColumnIndexes.push(columnIndex);
    });

    // NOTE: The header's column-onscreen/column-offscreen attributes were just refreshed
    // above, so cells can once again trust them. Clear the "stale" flag so
    // IdsDataGridCell.renderCell() applies its off-screen skip during the refresh below.
    this.removeAttribute('columns-stale');

    // Only re-render the cells belonging to columns that just entered the viewport,
    // rather than re-rendering every cell of every row (previously ~columns × rows
    // renderCell calls per horizontal scroll). Cells are ordered by column, so the
    // child at `columnIndex` is the cell for that column.
    if (enteredColumnIndexes.length) {
      this.rows.forEach((row: IdsDataGridRow) => {
        const cells = row.children;
        for (let i = 0; i < enteredColumnIndexes.length; i++) {
          const cell = cells[enteredColumnIndexes[i]] as IdsDataGridCell | undefined;
          cell?.renderCell?.();
        }
      });
    }
  }

  #detachScrollEvents() {
    this.offEvent('scroll.data-grid');
    this.offEvent('scroll.data-grid.virtual-scroll');
    this.offEvent('scrollend.data-grid.virtual-scroll');
  }

  /**
   * Attach events for loading indicator state changes
   * @private
   */
  #attachLoadingIndicatorEvents() {
    // Check initial state
    if (this.loadingIndicator && !this.loadingIndicator.hasAttribute('stopped')) {
      this.wrapper?.classList.add('has-loading-indicator');
    }

    this.onEvent('started.loading-indicator', this.loadingIndicator, () => {
      this.wrapper?.classList.add('has-loading-indicator');
    });

    this.onEvent('stopped.loading-indicator', this.loadingIndicator, () => {
      this.wrapper?.classList.remove('has-loading-indicator');
    });
  }

  /**
   * Updates tree node and its children's rowHidden states for virtual scrolling
   * @param {Record<string, any>} treeNode tre node
   * @param {boolean} show show/hidden flag
   */
  #syncTreeHiddenStates(treeNode: Record<string, any>, show: boolean) {
    const hasChildren = treeNode.childrenVRefIds?.length > 0;
    const isExpanded = treeNode.rowExpanded;

    this.updateDataset(treeNode.vsRefId, { rowHidden: !show });
    treeNode.rowHidden = !show;

    if (hasChildren) {
      treeNode.childrenVRefIds.forEach((childIndex: number) => {
        this.#syncTreeHiddenStates(this.data[childIndex], isExpanded && !treeNode.rowHidden);
      });
    }
  }

  #handleTreeRowExpandCollapse(expanded: boolean, payload: Record<string, any>) {
    const rowIndex = payload.row;
    const allRows = payload.allRows;
    const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;
    const scrollIndex = Math.floor(this.container!.scrollTop / rowHeight);

    // collapseAll/expandAll invoked and already updated show/hide states
    if (allRows) {
      this.#recycleAllTreeRows(scrollIndex);
      return;
    }

    // update parent expanded state
    this.updateDataset(rowIndex, { rowExpanded: expanded, rowHidden: false });

    // sync show/hide states of subtree
    this.#syncTreeHiddenStates(this.data[rowIndex], true);

    // refresh virtual rows to reflect new show/hidden states
    this.#recycleAllTreeRows(scrollIndex);
  }

  /**
   * Recycle all dom tree rows for virtual scroll window
   * @param {number} scrollIndex scroll position row index
   * @param {Record<number, boolean>} forceRender rows to force render
   */
  #recycleAllTreeRows(scrollIndex: number, forceRender: Record<number, boolean> = {}) {
    this.#recycleAllTreeRowsImpl(scrollIndex, forceRender);
  }

  /**
   * Implementation for recycling all dom tree rows for the virtual scroll window
   * @param {number} scrollIndex scroll position row index
   * @param {Record<number, boolean>} forceRender rows to force render
   * @returns {void}
   */
  #recycleAllTreeRowsImpl(scrollIndex: number, forceRender: Record<number, boolean> = {}) {
    this.#saveEditedCellsBeforeScroll();
    const rows = this.rows;
    const data = this.data;
    const virtualRows = this.virtualRows;
    const maxVirtualRowIndex = virtualRows.length - 1;
    const scrollSettings = this.virtualScrollSettings;
    const maxRowsInDom = Math.min(scrollSettings.MAX_ROWS_IN_DOM, virtualRows.length);

    // safe scroll index
    scrollIndex = Math.max(scrollIndex, 0);
    scrollIndex = Math.min(scrollIndex, maxVirtualRowIndex);

    // first virtual row index to render
    let firstVRow = scrollIndex - scrollSettings.BUFFER_ROWS;
    firstVRow = Math.min(firstVRow, maxVirtualRowIndex - maxRowsInDom + 1);
    firstVRow = Math.max(firstVRow, 0);

    // recycle all dom rows for virtual scroll window
    let i: number;
    let vRow = firstVRow; // Initialize vRow here
    for (i = 0; i < rows.length; i++) {
      const row = rows[i];
      const dataRowIndex = virtualRows[vRow]?.vsRefId;

      if (dataRowIndex >= 0) {
        row.rowIndex = dataRowIndex;
      }

      if (forceRender[dataRowIndex]) {
        row.renderRow(dataRowIndex);
      }

      vRow++; // Increment vRow here
    }

    // clean up dom rows after recycling
    if (maxRowsInDom - rows.length !== 0) {
      // too many rows
      const excessRows = rows.length - maxRowsInDom;
      if (excessRows > 0) {
        rows.slice(-1 * excessRows).forEach((row) => row.remove());
      }

      // need more rows
      const neededRows = maxRowsInDom - rows.length;
      if (neededRows > 0) {
        this.body!.innerHTML += Array.from({ length: neededRows }, () => {
          const dataRowIndex = virtualRows[vRow]?.vsRefId;
          const result = IdsDataGridRow.template(data[dataRowIndex], dataRowIndex, dataRowIndex + 1, this);
          vRow++;
          return result;
        }).join('');
      }
    }

    // position virtual scroll window
    this.#positionVirtualScrollWindow(firstVRow, this.virtualRows.length, scrollSettings);

    this.#restoreEditedCellsAfterScroll();
  }

  #handleVirtualScroll(rowHeight: number) {
    if (!this.virtualScroll) return;

    const rowIndex = Math.floor(this.container!.scrollTop / rowHeight);

    this.highlightSearchRows();
    if (this.treeGrid) {
      this.#scrollTreeRowIntoView(rowIndex);
    } else {
      this.#scrollRowIntoView(rowIndex, { doScroll: false });
    }
  }

  /**
   * Get the current pixel height for a given row, accounting for expanded state
   * @param {number} rowIndex The row index
   * @returns {number} The row height in pixels
   */
  getRowHeight(rowIndex: number): number {
    return this.expandedRowHeights[rowIndex] || this.rowPixelHeight;
  }

  /**
   * Expand a cell to show full multi-line content
   * @param {number} rowIndex The row index
   * @param {number} columnIndex The column index
   */
  expandCell(rowIndex: number, columnIndex: number): void {
    const row = this.rowByIndex(rowIndex);
    if (!row) return;

    const cell = row.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${columnIndex + 1}"]`);
    if (!cell || cell.classList.contains('is-expanded')) return;

    const column = this.visibleColumns[columnIndex];
    const rowData = this.data[rowIndex];
    const fieldValue = column?.field ? rowData?.[column.field] : null;
    if (fieldValue == null || String(fieldValue).trim() === '') return;

    const textEl = cell.querySelector<HTMLElement>('.text-ellipsis');
    if (textEl && textEl.scrollWidth <= textEl.clientWidth && !String(fieldValue).includes('\n')) return;

    const defaultHeight = this.rowPixelHeight;

    cell.classList.add('is-expanded');

    const expandedHeight = cell.scrollHeight;

    if (expandedHeight > defaultHeight) {
      this.expandedRowHeights[rowIndex] = expandedHeight;

      const cells = row.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell');
      cells.forEach((sibling) => {
        if (sibling !== cell) {
          sibling.classList.add('is-expanded-sibling');
        }
        sibling.style.height = `${expandedHeight}px`;
      });

      this.#expandedCellInfo = { row: rowIndex, cell: columnIndex };
    } else {
      cell.classList.remove('is-expanded');
    }
  }

  /**
   * Collapse the currently expanded cell back to default row height
   * @param {boolean} clearTracking If true, also clears the expanded cell tracking (default true)
   */
  collapseCell(clearTracking = true): void {
    const expandedCell = this.container?.querySelector<IdsDataGridCell>('ids-data-grid-cell.is-expanded');
    if (!expandedCell) {
      if (clearTracking) this.#expandedCellInfo = null;
      return;
    }

    expandedCell.classList.remove('is-expanded');

    const row = expandedCell.parentRow;
    if (row) {
      const rowIndex = expandedCell.rowIndex;
      delete this.expandedRowHeights[rowIndex];

      const cells = row.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell');
      cells.forEach((cell) => {
        cell.classList.remove('is-expanded-sibling');
        cell.style.removeProperty('height');
      });
    }

    if (clearTracking) this.#expandedCellInfo = null;
  }

  /**
   * Fire scrollend event if content doesn't overflow the container,
   * so infinite scroll can load more data.
   */
  #triggerScrollEndIfNotOverflowing() {
    if (!this.virtualScroll || !this.container || !this.data.length) return;
    const { scrollHeight, clientHeight } = this.container;
    if (scrollHeight <= clientHeight + 1) {
      const lastRowIndex = this.datasource.originalData.length - 1;
      this.#triggerCustomScrollEvent(lastRowIndex, 'end');
    }
  }

  #customScrollEventCache: { [key: string]: number } = {};

  /* Trigger API scroll event */
  #triggerCustomScrollEvent(rowIndex: number, eventType?: 'start' | 'end') {
    if (!eventType) {
      this.#customScrollEventCache = {}; // reset event-cache
    } else if (rowIndex !== this.#customScrollEventCache[eventType]) {
      this.#customScrollEventCache[eventType] = rowIndex;

      this.triggerEvent(`scroll${eventType}`, this, {
        bubbles: true,
        composed: true,
        detail: { elem: this, value: rowIndex }
      });
    }
  }

  #positionVirtualScrollWindow(firstRowIndex: number, totalRows: number, scrollSettings?: Record<string, any>) {
    const { ROW_HEIGHT, MAX_ROWS_IN_DOM } = scrollSettings || this.virtualScrollSettings;
    const offsetTop = firstRowIndex * ROW_HEIGHT;
    const maxHeight = totalRows * ROW_HEIGHT;
    const paddingBottom = maxHeight - (MAX_ROWS_IN_DOM * ROW_HEIGHT) - offsetTop;
    this.body?.style.setProperty('transform', `translateY(${offsetTop}px)`);
    this.body?.style.setProperty('padding-bottom', `${Math.max(paddingBottom, 0)}px`);
  }

  #restoreScrollPosition(scrollTop: number, scrollLeft: number) {
    if (!Number.isNaN(scrollTop) && !Number.isNaN(scrollLeft) && this.container) {
      this.container.scrollTop = scrollTop;
      this.container.scrollLeft = scrollLeft;
    }
  }

  /**
   * Internal handling of scrolling to row
   * @param {number} rowIndex row index
   * @param {ScrollSettings} [settings] Optional scroll settings
   * @param {boolean} [settings.skipIfVisible] Set to true to prevent scrolling to already visible rows
   * @param {boolean} [settings.blockOuterScroll] Set to true to prevent the outer page from scrolling
   */
  #scrollTo(rowIndex: number, settings: ScrollSettings = DEFAULT_SCROLL_SETTINGS): void {
    const row = this.rowByIndex(rowIndex);
    const target = row?.cellByIndex(0);

    if (!row || !target || !this.container) return;

    // Guard: skip scrolling when the grid has no internal scrollbar
    const hasInternalScrollbar = this.container!.scrollHeight > this.container!.clientHeight;
    if (!hasInternalScrollbar) return;

    const headerHeight = this.header?.clientHeight ?? 0;
    const rowRect = target.getBoundingClientRect();

    // Guard: skip if row is already visible in the viewport
    if (settings.skipIfVisible && rowRect && rowRect.top >= headerHeight && rowRect.bottom <= window.innerHeight) return;

    const targetRect = target.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    if (!targetRect || !containerRect) return;

    const targetTop = targetRect.top;
    const containerTop = containerRect.top;
    let relativeTop = targetTop - containerTop + this.container.scrollTop;

    const scrollHeight = this.container.scrollHeight;
    const containerHeight = this.container.clientHeight;
    const scrollTop = this.container.scrollTop;
    const isScrollBottom = (scrollTop + containerHeight) >= scrollHeight;

    // offset for sticky header height
    if (!isScrollBottom) relativeTop -= headerHeight;

    if (settings.blockOuterScroll) {
      this.container.scrollTo({
        top: relativeTop
      });
    } else {
      target.scrollIntoView?.();
      this.container.scrollTop = relativeTop;
    }
  }

  /**
   * Internal handling of scrolling to tree row
   * @param {number} rowIndex row index
   */
  #scrollToTreeRow(rowIndex: number) {
    if (!this.container || !this.data.length) return;

    const { ROW_HEIGHT } = this.virtualScrollSettings;
    const isRowHidden = this.data[rowIndex]?.rowHidden;
    const forceRender: Record<number, boolean> = {};

    // if row is hidden, first expand all parent rows
    if (isRowHidden) {
      const targetRow = this.data[rowIndex];
      let parentRow = this.#findParentRow(this.data, targetRow.parentElement ?? '');

      // find parent row
      while (parentRow) {
        forceRender[parentRow.vsRefId] = true;
        this.updateDataset(parentRow.vsRefId, { rowExpanded: true, rowHidden: false });
        parentRow = this.#findParentRow(this.data, parentRow.parentElement ?? '');
      }

      const rootRow = this.#findRootRow(targetRow);
      if (rootRow) {
        this.#syncTreeHiddenStates(rootRow, true);
      }
    }

    const virtualRowIndex = this.virtualRows.indexOf(this.data[rowIndex]);
    this.#recycleAllTreeRows(virtualRowIndex, forceRender);
    this.container.scrollTop = virtualRowIndex * ROW_HEIGHT;
  }

  /**
   * Scroll a given row into view
   * @param {number} rowIndex Which row to scroll into view (1-based, where 1 is the first row).
   * @param {ScrollSettings} [settings] Optional scroll settings
   * @param {boolean} [settings.skipIfVisible] When true, skips scrolling if the row is already visible in the viewport
   * @param {boolean} [settings.blockOuterScroll] When true, prevents the outer page from scrolling
   */
  scrollRowIntoView(rowIndex: number, settings: ScrollSettings = DEFAULT_SCROLL_SETTINGS) {
    const dataIndex = Math.max(0, rowIndex - 1);

    if (this.treeGrid && this.virtualScroll) {
      this.#scrollToTreeRow(dataIndex);
      return;
    }

    this.#scrollRowIntoView(dataIndex, { doScroll: true, ...settings });
  }

  /**
   * Internal scroll a given row into view
   * @private
   * @param {number} rowIndex Which row to scroll into view (0-based index).
   * @param {ScrollSettings} [settings] Optional scroll settings
   * @param {boolean} [settings.doScroll] When true, performs the browser scroll action
   * @param {boolean} [settings.skipIfVisible] When true, skips scrolling if the row is already visible in the viewport
   * @param {boolean} [settings.blockOuterScroll] When true, prevents the outer page from scrolling
   */
  #scrollRowIntoView(rowIndex: number, settings: ScrollSettings = DEFAULT_SCROLL_SETTINGS) {
    const data = this.data;
    const rows = this.rows;
    if (!data.length || !rows.length) return;

    const virtualScrollSettings = this.virtualScrollSettings;
    const maxRowIndex = data.length - 1;
    rowIndex = Math.max(rowIndex, 0);
    rowIndex = Math.min(rowIndex, maxRowIndex);

    if (!this.virtualScroll) {
      this.#scrollTo(rowIndex, settings);
      return;
    }

    const container = this.container!;
    const body = this.body!;

    const firstRow: any = rows[0];
    const lastRow: any = rows[rows.length - 1];
    const firstRowIndex = firstRow.rowIndex;
    const lastRowIndex = lastRow.rowIndex;

    const isAboveFirstRow = rowIndex < firstRowIndex;
    const isBelowLastRow = rowIndex > lastRowIndex;
    const isInRange = !isAboveFirstRow && !isBelowLastRow;
    const reachedTheBottom = lastRowIndex >= maxRowIndex;
    let bufferRowIndex = rowIndex - virtualScrollSettings.BUFFER_ROWS;
    bufferRowIndex = Math.max(bufferRowIndex, 0);
    bufferRowIndex = Math.min(bufferRowIndex, maxRowIndex);

    if (isInRange && settings.doScroll) {
      this.offEvent('scroll.data-grid.virtual-scroll', this.container);
      this.#scrollTo(rowIndex, settings);
      this.#attachVirtualScrollEvent();
      return;
    }

    // Only save/restore when we need to recycle rows
    const needsRecycling = !isInRange || (isInRange && (bufferRowIndex !== firstRowIndex));
    if (needsRecycling) {
      this.#saveEditedCellsBeforeScroll();
    }

    if (isInRange) {
      // if rowIndex is in range of the currently visible rows:
      // then we should only move rows up or down according to how big the buffer should be.
      const moveRowsDown = bufferRowIndex - firstRowIndex;
      const moveRowsUp = Math.abs(moveRowsDown);

      if (moveRowsDown > 0) {
        if (!reachedTheBottom) {
          this.#recycleTopRowsDown(moveRowsDown);
        }
      } else if (moveRowsUp < virtualScrollSettings.MAX_ROWS_IN_DOM) {
        this.#recycleBottomRowsUp(moveRowsUp);
      } else {
        if (needsRecycling) {
          this.#restoreEditedCellsAfterScroll();
        }
        return; // exit early because nothing to do.
      }
    } else if (isAboveFirstRow) {
      // if rowIndex should appear above the currently visible rows,
      // then we must figure out how many rows we must move up from the bottom to render the rowIndex row
      this.#recycleAllRows(bufferRowIndex);
    } else if (isBelowLastRow) {
      // if rowIndex should appear below the currently visible rows,
      // then we recycle all rows, since none of the visible rows are needed
      this.#recycleAllRows(bufferRowIndex);
    }

    // NOTE: repaint of padding is more performant than margin
    const virtualRowHeight = virtualScrollSettings.ROW_HEIGHT;
    const firstRowInDom = this.rows[0].rowIndex;
    const bodyTranslateY = firstRowInDom * virtualRowHeight;

    if (!reachedTheBottom) {
      body.style.setProperty('transform', `translateY(${bodyTranslateY}px)`);
    }

    this.#setVirtualScrollPaddingBottom(virtualScrollSettings, this.data.length, bodyTranslateY);

    if (settings.doScroll) {
      container!.scrollTop = rowIndex * virtualRowHeight;

      requestAnimationTimeout(() => {
        this.#scrollTo(rowIndex, settings);
        if (needsRecycling) {
          this.#restoreEditedCellsAfterScroll();
        }
      });
      return;
    }

    if (needsRecycling) {
      this.#restoreEditedCellsAfterScroll();
    }
  }

  /**
   * Ensure a row is visible in the viewport during keyboard navigation
   * @param {number} rowIndex The row index to ensure is visible
   * @param {'up' | 'down'} direction The navigation direction
   * @private
   */
  /**
   * Convert data row index to visible row index for tree grids
   * @param {number} rowIndex - The data row index
   * @returns {number} The visible row index, or -1 if row is hidden
   * @private
   */
  #getVisibleRowIndex(rowIndex: number): number {
    if (!this.treeGrid) return rowIndex;
    return this.virtualRows.findIndex((row) => row === this.data[rowIndex]);
  }

  /**
   * Find the next non-hidden row index in the specified direction
   * @param {number} startRowIndex - The starting data row index
   * @param {IdsDirection} direction - The navigation direction (Up or Down)
   * @returns {number} The next non-hidden row index, or -1 if none found
   * @private
   */
  #findNextVisibleRow(startRowIndex: number, direction: IdsDirection): number {
    const step = direction === IdsDirection.Down ? 1 : -1;
    let nextRowIndex = startRowIndex + step;
    while (nextRowIndex >= 0 && nextRowIndex < this.data.length && this.treeGrid && this.data[nextRowIndex]?.rowHidden) {
      nextRowIndex += step;
    }
    return (nextRowIndex >= 0 && nextRowIndex < this.data.length) ? nextRowIndex : -1;
  }

  /**
   * Scroll to bring a row into view and activate its cell
   * @param {number} nextRowIndex - The data row index to bring into view
   * @param {number} columnIndex - The column index of the cell to activate
   * @param {IdsDirection} direction - The navigation direction (Up or Down)
   * @private
   */
  #scrollToActivateRow(nextRowIndex: number, columnIndex: number, direction: IdsDirection) {
    if (nextRowIndex < 0 || nextRowIndex >= this.data.length) return;

    const visibleRowIndex = this.#getVisibleRowIndex(nextRowIndex);
    if (visibleRowIndex === -1) return;

    const { ROW_HEIGHT } = this.virtualScrollSettings;
    const containerHeight = this.container!.clientHeight;
    const headerHeight = this.header?.clientHeight ?? 0;

    const targetScrollTop = direction === IdsDirection.Down
      ? (visibleRowIndex + 1) * ROW_HEIGHT - containerHeight + headerHeight
      : visibleRowIndex * ROW_HEIGHT;

    this.container!.scrollTop = Math.max(targetScrollTop, 0);

    // Wait for DOM to update after scroll before finding and activating the cell
    requestAnimationFrame(() => {
      const activeCell = this.findCellByLogicalPosition(nextRowIndex, columnIndex);
      if (activeCell) {
        activeCell.focus();
        this.setActiveCell(activeCell.columnIndex, activeCell.rowIndex, false, 'keyboard');
      }
    });
  }

  #ensureRowInViewport(rowIndex: number, direction: IdsDirection) {
    if (!this.virtualScroll || !this.container) return;

    const { ROW_HEIGHT } = this.virtualScrollSettings;
    const containerHeight = this.container.clientHeight;
    const headerHeight = this.header?.clientHeight ?? 0;
    const scrollTop = this.container.scrollTop;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight - headerHeight;

    const effectiveRowIndex = this.#getVisibleRowIndex(rowIndex);
    if (effectiveRowIndex === -1) return;

    const rowTop = effectiveRowIndex * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;

    if (direction === IdsDirection.Up && rowTop < viewportTop) {
      this.container.scrollTop = rowTop;
    } else if (direction === IdsDirection.Down && rowBottom > viewportBottom) {
      this.container.scrollTop = rowBottom - containerHeight + headerHeight;
    }
  }

  #setVirtualScrollPaddingBottom(settings: any, totalRows: number, offsetTop: number) {
    const maxHeight = totalRows * settings.ROW_HEIGHT;
    const paddingBottom = maxHeight - (settings.MAX_ROWS_IN_DOM * settings.ROW_HEIGHT) - offsetTop;
    this.body?.style.setProperty('padding-bottom', `${Math.max(paddingBottom, 0)}px`);
  }

  #scrollTreeRowIntoView(cursorIndex: number) {
    const domRows = this.rows;
    let virtualRows = this.virtualRows;

    if (!virtualRows.length || !domRows.length) {
      this.#positionVirtualScrollWindow(0, 0);
      return;
    }

    // clean up row index
    const maxRowIndex = virtualRows.length - 1;
    cursorIndex = Math.max(cursorIndex, 0);
    cursorIndex = Math.min(cursorIndex, maxRowIndex);

    // set up scroll variables
    const scrollSettings = this.virtualScrollSettings;
    let firstVisibleRow = domRows[0];
    let firstVisibleRowIndex = virtualRows.indexOf(firstVisibleRow.rowData);
    const lastVisibleRow = domRows[domRows.length - 1];
    const lastVisibleRowIndex = virtualRows.indexOf(lastVisibleRow.rowData);
    const reachedTheBottom = lastVisibleRowIndex >= maxRowIndex;

    // virtual window parameters
    let upperLimitIndex = cursorIndex - scrollSettings.BUFFER_ROWS;
    upperLimitIndex = Math.max(upperLimitIndex, 0);
    upperLimitIndex = Math.min(upperLimitIndex, maxRowIndex);

    const firstDomRow = domRows[0];
    const firstDomRowVirtualIndex = virtualRows.indexOf(firstDomRow.rowData);
    const lastDomRow = domRows[domRows.length - 1];
    const lastDomRowVirtualIndex = virtualRows.indexOf(lastDomRow.rowData);

    if (firstDomRowVirtualIndex === -1 || lastDomRowVirtualIndex === -1) {
      this.#saveEditedCellsBeforeScroll();
      this.#recycleAllTreeRows(cursorIndex);
      this.#restoreEditedCellsAfterScroll();
      this.#restoreActiveCellAfterScroll();
      return;
    }

    const moveRowsDown = upperLimitIndex - firstDomRowVirtualIndex;
    const moveRowsUp = moveRowsDown < 0 ? Math.abs(moveRowsDown) : NaN;
    const isInRange = cursorIndex >= firstDomRowVirtualIndex && cursorIndex <= lastDomRowVirtualIndex;

    // Only save/restore when we need to recycle rows
    const needsRecycling = !isInRange
    || (isInRange && (moveRowsDown > 0 || (moveRowsUp && moveRowsUp < scrollSettings.MAX_ROWS_IN_DOM)));

    const data = {
      maxRowIndex,
      firstVisibleRowIndex,
      lastVisibleRowIndex,
      dataVisibleRows: virtualRows
    };

    if (isInRange) {
      if (moveRowsDown > 0 && !reachedTheBottom) {
        this.#saveEditedCellsBeforeScroll();
        const staleRows = this.rows.slice(0, moveRowsDown);
        this.#recycleTreeRowsDown(data, staleRows);
      } else if (moveRowsUp && moveRowsUp < scrollSettings.MAX_ROWS_IN_DOM) {
        this.#saveEditedCellsBeforeScroll();
        const staleRows = this.rows.slice((-1 * moveRowsUp));
        this.#recycleTreeRowsUp(data, staleRows);
      }
    } else {
      this.#saveEditedCellsBeforeScroll();
      this.#recycleAllTreeRows(cursorIndex);
    }

    // position virtual scroll window
    virtualRows = this.virtualRows; // re query after recycling changes
    firstVisibleRow = this.rowsVisible[0];
    if (firstVisibleRow) {
      firstVisibleRowIndex = virtualRows.indexOf(firstVisibleRow.rowData);
      if (firstVisibleRowIndex === -1) firstVisibleRowIndex = 0;
    } else {
      firstVisibleRowIndex = 0;
    }
    this.#positionVirtualScrollWindow(firstVisibleRowIndex, virtualRows.length, scrollSettings);

    if (needsRecycling) {
      this.#restoreEditedCellsAfterScroll();
      this.#restoreActiveCellAfterScroll();
    }
  }

  #recycleTreeRowsDown(obj: Record<string, any>, staleRows: IdsDataGridRow[]) {
    const { maxRowIndex, lastVisibleRowIndex, dataVisibleRows } = obj;
    const rowsToMove: IdsDataGridRow[] = [];

    staleRows.forEach((row, idx) => {
      const nextIndex = lastVisibleRowIndex + (idx) + 1;
      const visibleRowData = dataVisibleRows[nextIndex];
      if (nextIndex > maxRowIndex || !visibleRowData) return;
      const dataRowIndex = visibleRowData.vsRefId;
      row.rowIndex = dataRowIndex;
      rowsToMove.push(row);
    });

    if (rowsToMove.length) {
      this.body?.append(...rowsToMove);
    }
  }

  #recycleTreeRowsUp(obj: Record<string, any>, staleRows: IdsDataGridRow[]) {
    const { firstVisibleRowIndex, dataVisibleRows } = obj;
    const rowsToMove: IdsDataGridRow[] = [];

    staleRows.forEach((row, idx) => {
      const prevIndex = firstVisibleRowIndex - (idx + 1);
      const visibleRowData = dataVisibleRows[prevIndex];
      if (prevIndex < 0 || !visibleRowData) return;
      const dataRowIndex = visibleRowData.vsRefId;
      row.rowIndex = dataRowIndex;
      rowsToMove.push(row);
    });

    if (rowsToMove.length) {
      this.body?.prepend(...rowsToMove.reverse());
    }
  }

  /* Recycle the rows during scrolling */
  #recycleAllRows(topRowIndex: number) {
    const data = this.data;
    const rows = this.rows;
    if (!data.length || !rows.length) return;

    const veryLastIndex = data.length - 1;
    topRowIndex = Math.min(topRowIndex, veryLastIndex);
    topRowIndex = Math.max(topRowIndex, 0);

    const { MAX_ROWS_IN_DOM } = this.virtualScrollSettings;

    // Using Array.every as an alternaive to using a for-loop with a break
    this.rows.every((row: any, idx) => {
      const nextRowIndex = topRowIndex + idx;
      if (nextRowIndex > veryLastIndex) {
        const moveTheRestToTop = MAX_ROWS_IN_DOM - idx;
        this.#recycleBottomRowsUp(moveTheRestToTop);
        return false;
      }
      row.rowIndex = nextRowIndex;
      return true;
    });
  }

  /* Recycle the rows during scrolling from the top */
  #recycleTopRowsDown(rowCount: number) {
    const rows = this.rows;
    if (!rowCount || !rows.length) return;
    const data = this.data;

    const bottomRow = rows[rows.length - 1];
    const bottomRowIndex = bottomRow.rowIndex;
    const staleRows = rows.slice(0, rowCount);
    const rowsToMove: IdsDataGridRow[] = [];

    // NOTE: Using Array.every as an alternaive to using a for-loop with a break
    staleRows.every((row: IdsDataGridRow, idx) => {
      const nextIndex = bottomRowIndex + (idx + 1);
      if (nextIndex >= data.length) return false;
      row.rowIndex = nextIndex;
      return rowsToMove.push(row);
    });

    if (!rowsToMove.length) return;

    // NOTE: no need to shift rows in the DOM if all the rows need to be recycled
    if (rowsToMove.length >= this.virtualScrollSettings.MAX_ROWS_IN_DOM) return;

    // NOTE: body.append is faster than body.innerHTML
    // NOTE: body.append is faster than multiple calls to appendChild()
    this.body?.append(...rowsToMove);
  }

  /* Recycle the rows during scrolling from the bottom */
  #recycleBottomRowsUp(rowCount: number) {
    const rows = this.rows;
    if (!rowCount || !rows.length) return;

    const topRow = rows[0];
    const topRowIndex = topRow.rowIndex;
    const staleRows = rows.slice((-1 * rowCount));
    const rowsToMove: IdsDataGridRow[] = [];

    // NOTE: Using Array.every as an alternaive to using a for-loop with a break
    staleRows.every((row: any, idx) => {
      const prevIndex = topRowIndex - (idx + 1);
      if (prevIndex < 0) return false;
      row.rowIndex = prevIndex;
      return rowsToMove.push(row);
    });

    if (!rowsToMove.length) return;

    // NOTE: no need to shift rows in the DOM if all the rows need to be recycled
    if (rowsToMove.length >= this.virtualScrollSettings.MAX_ROWS_IN_DOM) return;

    // NOTE: body.prepend() seems to be faster than body.innerHTML
    this.body?.prepend(...rowsToMove.reverse());
  }

  /**
   * Set the aria-label element in the DOM. This should be translated.
   * @param {string} value The aria label
   */
  set label(value: string) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
      this.shadowRoot?.querySelector('.ids-data-grid')?.setAttribute('aria-label', value);
      return;
    }

    this.removeAttribute(attributes.LABEL);
    this.shadowRoot?.querySelector('.ids-data-grid')?.setAttribute('aria-label', 'Data Grid');
  }

  get label(): string { return this.getAttribute(attributes.LABEL) || 'Data Grid'; }

  /**
   * Set the row height between extra-small, small, medium and large (default)
   * @param {string} value The row height
   */
  set rowHeight(value) {
    if (value) {
      const previousValue = this.getAttribute(attributes.ROW_HEIGHT);
      const isNew = this.initialized && previousValue !== value;
      this.setAttribute(attributes.ROW_HEIGHT, value);
      this.shadowRoot?.querySelector('.ids-data-grid')?.setAttribute('data-row-height', value);
      if (isNew) {
        this.collapseCell();
        this.expandedRowHeights = {};
        // Skip column-width recalc when no column depends on row-height padding
        if (this.#hasRowHeightDependentWidths) {
          this.maxContentColumnWidths = {};
          this.#setColumnWidths();
        }
        this.#updateRowspanCellHeights();
        this.saveSettings?.();
        if (this.virtualScroll) {
          // Row-height is a CSS-only change — cell DOM does not need to be rebuilt. Recompute
          // the virtual-scroll padding/translate math and preserve scroll position instead of
          // rebuilding every row's cells (was previously a full `redraw()` — 100 rows × N columns).
          this.#refreshVirtualScrollForRowHeightChange(previousValue);
        }
      }
    } else {
      this.removeAttribute(attributes.ROW_HEIGHT);
      this.shadowRoot?.querySelector('.ids-data-grid')?.setAttribute('data-row-height', 'lg');
      this.saveSettings?.();
    }
    this.shadowRoot?.querySelector('ids-empty-message')?.classList.remove('row-height-xxs', 'row-height-xs', 'row-height-sm', 'row-height-md', 'row-height-lg');
    this.shadowRoot?.querySelector('ids-empty-message')?.classList.add(`row-height-${value}`);
  }

  get rowHeight() { return this.getAttribute(attributes.ROW_HEIGHT) || 'lg'; }

  /**
   * Re-apply the virtual-scroll positioning math and preserve the currently visible row
   * after a row-height change. Avoids the full body/header rebuild that `redraw()` performs.
   * @private
   * @param {string | null} previousRowHeight The row-height attribute value before the change.
   * @returns {void}
   */
  #refreshVirtualScrollForRowHeightChange(previousRowHeight: string | null): void {
    const container = this.container;
    if (!container) return;

    const heights = IdsDataGrid.ROW_HEIGHT_PIXELS;
    const oldPixelHeight = heights[previousRowHeight || 'lg'] ?? heights.lg;
    const newPixelHeight = this.rowPixelHeight;

    // Anchor row: the first row currently at the top of the viewport before the change.
    const anchorRowIndex = oldPixelHeight > 0 ? Math.floor(container.scrollTop / oldPixelHeight) : 0;

    // Recompute the virtual-scroll window (padding-bottom + translateY) using the new ROW_HEIGHT
    this.#initVirtualScrollHeight();

    // Snap scrollTop so the same row stays anchored at the top after the height change.
    container.scrollTop = anchorRowIndex * newPixelHeight;
  }

  /**
   * Set the min height of the grid (for the empty message or loading indicator)
   * @param {string | null} value The min height (defaults to '350px')
   */
  set minHeight(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MIN_HEIGHT, value.toString());
      this.style.setProperty('--ids-data-grid-height-min', value);
    } else {
      this.removeAttribute(attributes.MIN_HEIGHT);
      this.style.removeProperty('--ids-data-grid-height-min');
    }
    this.saveSettings?.();
  }

  get minHeight(): string { return this.getAttribute(attributes.MIN_HEIGHT) || '350px'; }

  /**
   * Set the height of the grid
   * @param {string | null} value The height
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value.toString());
      this.style.setProperty('--ids-data-grid-height', value);
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.removeProperty('--ids-data-grid-height');
    }
    this.saveSettings?.();
    requestAnimationFrame(() => this.#checkLastRowBorder());
  }

  get height(): string | null { return this.getAttribute(attributes.HEIGHT); }

  /**
   * Set the max height of the grid
   * @param {string | null} value The max height
   */
  set maxHeight(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_HEIGHT, value.toString());
      this.style.setProperty('--ids-data-grid-height-max', value);
    } else {
      this.removeAttribute(attributes.MAX_HEIGHT);
      this.style.removeProperty('--ids-data-grid-height-max');
    }

    // Override max-height when empty to prevent it from overriding min-height
    if (value && this.wrapper?.classList.contains('has-empty-message') && !this.height) {
      this.style.setProperty('--ids-data-grid-height-max', 'none');
    }

    this.saveSettings?.();
    requestAnimationFrame(() => this.#checkLastRowBorder());
  }

  get maxHeight(): string | null { return this.getAttribute(attributes.MAX_HEIGHT); }

  /**
   * Set the width of the grid
   * @param {string | null} value The width
   */
  set width(value: string | null) {
    if (value) {
      this.setAttribute(attributes.WIDTH, value.toString());
      this.style.setProperty('--ids-data-grid-width', value);
    } else {
      this.removeAttribute(attributes.WIDTH);
      this.style.removeProperty('--ids-data-grid-width');
    }
    this.saveSettings?.();
  }

  get width(): string | null { return this.getAttribute(attributes.WIDTH); }

  /**
   * Set the min width of the grid
   * @param {string | null} value The min width
   */
  set minWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MIN_WIDTH, value.toString());
      this.style.setProperty('--ids-data-grid-width-min', value);
    } else {
      this.removeAttribute(attributes.MIN_WIDTH);
      this.style.removeProperty('--ids-data-grid-width-min');
    }
    this.saveSettings?.();
  }

  get minWidth(): string | null { return this.getAttribute(attributes.MIN_WIDTH); }

  /**
   * Set the max width of the grid
   * @param {string | null} value The max width
   */
  set maxWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_WIDTH, value.toString());
      this.style.setProperty('--ids-data-grid-width-max', value);
    } else {
      this.removeAttribute(attributes.MAX_WIDTH);
      this.style.removeProperty('--ids-data-grid-width-max');
    }
    this.saveSettings?.();
  }

  get maxWidth(): string | null { return this.getAttribute(attributes.MAX_WIDTH); }

  /**
   * Set the row index. If set, the datagrid's data set will initially load here.
   * @param {number} rowIndex The row-index at which to start showing data.
   */
  set rowStart(rowIndex: number) {
    this.setAttribute(attributes.ROW_START, String(rowIndex || 0));
  }

  /**
   * Get the start-row index
   * @returns {number} The start-row index
   */
  get rowStart(): number { return Number(this.getAttribute(attributes.ROW_START)) || 0; }

  /**
   * Sets keyboard navigation to rows
   * @param {boolean | string} value toggle row navigation
   */
  set rowNavigation(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.ROW_NAVIGATION, '');
      this.container?.classList.add('row-navigation');
    } else {
      this.removeAttribute(attributes.ROW_NAVIGATION);
      this.container?.classList.remove('row-navigation');
    }
  }

  get rowNavigation(): boolean | string {
    return this.hasAttribute(attributes.ROW_NAVIGATION);
  }

  /**
   * Set the style of the grid to list style for simple readonly lists
   * @param {boolean} value list styling to use
   */
  set listStyle(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.LIST_STYLE, String(value));
      this.shadowRoot?.querySelector('.ids-data-grid')?.classList.add('is-list-style');
    } else {
      this.removeAttribute(attributes.LIST_STYLE);
      this.shadowRoot?.querySelector('.ids-data-grid')?.classList.remove('is-list-style');
    }
  }

  get listStyle() { return stringToBool(this.getAttribute(attributes.LIST_STYLE)) || false; }

  /**
   * Set the row selection mode between false, 'single', 'multiple' and 'mixed'
   * @param {string|boolean} value selection mode to use
   */
  set rowSelection(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.ROW_SELECTION, String(value));
    } else {
      this.removeAttribute(attributes.ROW_SELECTION);
    }
  }

  get rowSelection() { return this.getAttribute(attributes.ROW_SELECTION) || false; }

  /**
   * Set the cell selection mode. Default is false.
   * @param {boolean} value True if drag selection will be used
   */
  set cellSelection(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.CELL_SELECTION, String(value));
    } else {
      this.removeAttribute(attributes.CELL_SELECTION);
    }
  }

  get cellSelection() { return stringToBool(this.getAttribute(attributes.CELL_SELECTION)); }

  /**
   * Set the column selection, true to add column header selection
   * @param {boolean} value selection mode to use
   */
  set colSelection(value: boolean) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.COL_SELECTION, String(value));
    } else {
      this.removeAttribute(attributes.COL_SELECTION);
    }
  }

  get colSelection(): boolean { return stringToBool(this.getAttribute(attributes.COL_SELECTION)); }

  /**
   * Set suppress empty message
   * @param {boolean | string} value The value
   */
  set suppressEmptyMessage(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_EMPTY_MESSAGE, '');
    } else {
      this.removeAttribute(attributes.SUPPRESS_EMPTY_MESSAGE);
    }
    setEmptyMessage.apply(this);
  }

  get suppressEmptyMessage(): boolean | string {
    return this.hasAttribute(attributes.SUPPRESS_EMPTY_MESSAGE);
  }

  /*
  * Get the loading indicator element
  * @param {IdsLoadingIndicator} the loading element
  */
  get loadingIndicator(): IdsLoadingIndicator {
    return this.shadowRoot?.querySelector('slot[name="loading-container"]')?.children[0] as IdsLoadingIndicator;
  }

  /**
   * Set to true to prevent rows from being selectedd when clicking the row,only the checkbox will select.
   * @param {string|boolean} value true or false
   */
  set suppressRowClickSelection(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_ROW_CLICK_SELECTION, value.toString());
    } else {
      this.removeAttribute(attributes.SUPPRESS_ROW_CLICK_SELECTION);
    }
  }

  /**
   * Get the current value of suppress row click selection
   * @returns {boolean} the value of suppressRowClickSelection
   */
  get suppressRowClickSelection() { return this.getAttribute(attributes.SUPPRESS_ROW_CLICK_SELECTION) || false; }

  /**
   * Set to true to prevent rows from being deselected if click or space bar the row.
   * i.e. once a row is selected, it remains selected until another row is selected in its place.
   * @param {string|boolean} value true or false
   */
  set suppressRowDeselection(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_ROW_DESELECTION, String(value));
    } else {
      this.removeAttribute(attributes.SUPPRESS_ROW_DESELECTION);
    }
  }

  get suppressRowDeselection() { return this.getAttribute(attributes.SUPPRESS_ROW_DESELECTION) || false; }

  /**
   * Set to true to prevent rows from being deactivated if clicked.
   * i.e. once a row is activated, it remains activated until another row is activated in its place.
   * @param {string|boolean} value true or false
   */
  set suppressRowDeactivation(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_ROW_DEACTIVATION, String(value));
    } else {
      this.removeAttribute(attributes.SUPPRESS_ROW_DEACTIVATION);
    }
  }

  get suppressRowDeactivation() { return this.getAttribute(attributes.SUPPRESS_ROW_DEACTIVATION) || false; }

  /**
   * Get the selected rows
   * @returns {Array<object>} An array of all currently selected rows
   */
  get selectedRows(): Array<{ index: number, data: Record<string, unknown> }> {
    return this.data.flatMap((row: Record<string, unknown>, index: number) => {
      if (row.rowSelected) return { index: Number(index), data: row };
      return [];
    });
  }

  /**
   * Map and boundary data on selected cells
   * @returns { object };
   */
  get selectedMap(): { bounds: { minRow: number, maxRow: number, minCol: number, maxCol: number }, map: Array<Array<any>> } {
    const bounds = {
      minRow: this.data.length,
      maxRow: -1,
      minCol: this.columns.length,
      maxCol: -1
    };
    const map: Array<any> = [];

    const getColIndex = (i: number) => (this.rowSelection ? i + 1 : i);
    const updateBounds = (row: number, col: number) => {
      if (row < bounds.minRow) bounds.minRow = row;
      if (row > bounds.maxRow) bounds.maxRow = row;
      if (col < bounds.minCol) bounds.minCol = col;
      if (col > bounds.maxCol) bounds.maxCol = col;
    };
    const blankArr = Array(this.visibleColumns.length).fill(false);
    const columns = this.rowSelection ? this.visibleColumns.slice(1, this.visibleColumns.length) : this.visibleColumns;

    let count = 0;

    this.data.forEach((row: any, rowIndex: number) => {
      if (row.rowSelected) {
        updateBounds(rowIndex, 0);
        updateBounds(rowIndex, columns.length - 1);

        map.push(columns.map((column: IdsDataGridColumn, colIndex: number) => ({
          row: rowIndex,
          col: colIndex,
          value: row[column.field || column.id],
          target: this.cellByIndex(rowIndex, getColIndex(colIndex)),
        })));

        count += columns.length;
      } else if (row.colSelected) {
        const mapRow: Array<any> = [];

        row.colSelected.forEach((isSelected: boolean, colIndex: number) => {
          if (isSelected) {
            const column = this.visibleColumns[getColIndex(colIndex)];

            mapRow.push({
              row: rowIndex,
              col: colIndex,
              value: row[column.field || column.id],
              target: this.cellByIndex(rowIndex, getColIndex(colIndex)),
            });
            count++;
            updateBounds(rowIndex, colIndex);
          } else {
            mapRow.push(false);
          }
        });

        map.push(mapRow);
      } else {
        map.push(blankArr);
      }
    });

    if (count === 0 && this.activeCell && this.activeCell.node) {
      bounds.minRow = this.activeCell.row;
      bounds.maxRow = this.activeCell.row;
      bounds.minCol = this.activeCell.cell;
      bounds.maxCol = this.activeCell.cell;

      const column = this.visibleColumns[getColIndex(this.activeCell.cell)];
      const value = this.data[this.activeCell.row] ? this.data[this.activeCell.row][column?.field || column?.id] : null;

      map[this.activeCell.row][this.activeCell.cell] = {
        value,
        row: this.activeCell.row,
        col: this.activeCell.cell,
        target: this.activeCell.node
      };
    }

    return { bounds, map };
  }

  /**
   * Get the selected rows across all pages, if a paged dataset is present
   * @returns {Array<object>} An array of all currently selected rows
   */
  get selectedRowsAcrossPages(): Array<{ index: number, data: Record<string, unknown> }> {
    if (!this.pager) return this.selectedRows;
    if (this.pagination === 'server-side') return this.serverSideSelections;

    const rows = this.treeGrid ? this.virtualRows : this.datasource.currentData;
    return rows.flatMap((row: Record<string, unknown>, index: number) => {
      if (row.rowSelected) return { index: Number(index), data: row };
      return [];
    });
  }

  /**
   * Get the activated row
   * @returns {any} The index of the selected row
   */
  get activatedRow(): any {
    return this.data.flatMap((row: Record<string, unknown>, index: number) => {
      if (row.rowActivated) return { index: Number(index), data: row };
      return [];
    })[0] || {};
  }

  /**
   * Update the dataset
   * @param {number} row the parent row that was clicked
   * @param {Record<string, unknown>} data the data to apply to the row
   * @param {boolean} isClear do not keep current data
   */
  updateDataset(row: number, data: Record<string, unknown>, isClear?: boolean) {
    const idCol = this.idColumn || 'id';
    // Ensure the incoming record contains a proper ID
    if (!data[idCol] && this.data[row] && data[idCol] !== '') {
      data[idCol] = this.data[row][idCol];
    }

    // Same page update
    if (row < this.data.length) {
      // Update the current data
      if (isClear) this.data[row] = data;
      else this.data[row] = { ...this.data[row], ...data };

      // Re-evaluate function-based column props whose cached result depends on this data
      this.#invalidateFunctionCacheForRow(row);
    }

    // Update the tree element in the original data
    if (this.treeGrid) {
      if (this.data[row].ariaLevel === 1) {
        this.datasource.update([data], !!isClear);
        return;
      }

      // Update the child element
      const parentRow = this.#findParentRow(this.datasource.originalData, this.data[row].parentElement ?? '');
      if (parentRow) {
        const updatedChildData = {
          ...parentRow.children[this.data[row][this.idColumn]],
          ...data
        };
        this.datasource.update([updatedChildData], !!isClear);
      }
      return;
    }

    // Non tree - update original data
    if (data[this.datasource.primaryKey] == null) data[this.datasource.primaryKey] = row;
    this.datasource.update([data], !!isClear);
  }

  /**
   * Update the dataset and refresh and add a dirty tracker if set on the column.
   * @param {number} row the parent row that was clicked
   * @param {Record<string, unknown>} data the data to apply to the row
   * @param {boolean} isClear do not keep current data
   */
  updateDatasetAndRefresh(row: number, data: Record<string, unknown>, isClear?: boolean) {
    if (this.activeCellEditor?.isEditing) {
      this.commitCellEdit();
      // Merge committed values into incoming data to prevent overwriting
      const committedRow = this.data[row];
      if (committedRow && isClear) {
        data = { ...data, ...committedRow };
      }
    }

    const existingRow = this.data[row];
    this.#saveCellOriginalValue(existingRow, row, data);
    this.updateDataset(row, data, isClear);
    this.#updateCellDirtyState(existingRow, row, data);
    IdsDataGridCell.cellCache.clear();
    this.rowByIndex(row)?.refreshRow();
    this.updateSelectedCells();
  }

  /**
   * Column properties that may be defined as a function and whose result is cached in the
   * cell cache. When row data changes, these need re-evaluation to avoid serving stale output.
   */
  static readonly CACHE_AFFECTING_FUNCTION_PROPS: readonly string[] = [
    'fieldIndicator',
    'fieldIndicatorMessage',
    'cssPart',
    'cellSelectedCssPart',
    'color',
    'disabled',
    'readonly'
  ];

  /**
   * Determine whether a column defines any cache-affecting property as a function.
   * @param {IdsDataGridColumn} column The column definition to check
   * @returns {boolean} true if the column has at least one function-valued cache-affecting property
   */
  #columnHasCacheAffectingFunction(column: IdsDataGridColumn): boolean {
    return IdsDataGrid.CACHE_AFFECTING_FUNCTION_PROPS.some(
      (prop) => typeof (column as unknown as Record<string, unknown>)[prop] === 'function'
    );
  }

  /**
   * Memoize the set of columns that use a function for any cache-affecting property.
   * Recomputed only when columns are set, so `updateDataset` stays cheap on the hot path.
   * @returns {void}
   */
  #memoizeFunctionColumns(): void {
    this.#functionColumns = (this.currentColumns ?? []).filter(
      (col) => this.#columnHasCacheAffectingFunction(col)
    );
  }

  /**
   * Invalidate the cell cache for a row's function-based columns after its data changes,
   * then re-render the affected on-screen cells (row-scoped, avoids a full grid refresh).
   * @param {number} rowIndex The row index to invalidate
   * @returns {void}
   */
  #invalidateFunctionCacheForRow(rowIndex: number): void {
    if (!this.#functionColumns.length) return;
    const rowData = this.data[rowIndex];
    if (!rowData) return;

    const functionColumnIds = new Set<string>();
    for (const col of this.#functionColumns) {
      functionColumnIds.add(col.id);
      const cacheKey = IdsDataGridCell.cacheKey(rowIndex, rowData, col, this);
      if (!cacheKey) continue;
      // Bust all three entries: the field indicator icon/message live in the template
      // (bare key), css classes in `:C`, css parts in `:P`.
      IdsDataGridCell.cellCache.delete(cacheKey);
      IdsDataGridCell.cellCache.delete(`${cacheKey}:${this.cacheCellClass}`);
      IdsDataGridCell.cellCache.delete(`${cacheKey}:${this.cacheCellPart}`);
    }

    // Off-screen rows only need the cache bust above; skip repaint if not rendered.
    const rowNode = this.rowByIndex(rowIndex);
    if (!rowNode) return;
    rowNode.querySelectorAll<IdsDataGridCell>('ids-data-grid-cell').forEach((cell) => {
      // Skip an editing cell: its indicator is hidden and re-rendering destroys the editor.
      if (cell.isEditing || !cell.column) return;
      const columnId = cell.column?.id;
      if (columnId && functionColumnIds.has(columnId)) cell.renderCell();
    });
  }

  /**
   * Save the cell's originalValue if it hasn't been set yet
   * @param {Record<string, unknown>} existingRow The current row data before update
   * @param {number} row row The row index
   * @param {Record<string, unknown>} data data The new data being applied to the row
   */
  #saveCellOriginalValue(existingRow: Record<string, any>, row: number, data: Record<string, unknown>) {
    if (!(existingRow && data)) return;

    Object.keys(data).forEach((field: any) => {
      if (existingRow[field] !== data[field]) {
        const columnIndex = this.columns.findIndex((col: any) => col.field === field);
        if (columnIndex !== -1) {
          const cell = this.rowByIndex(row)?.querySelector(
            `ids-data-grid-cell[aria-colindex="${columnIndex + 1}"]`
          ) as IdsDataGridCell;

          if (cell && cell.originalValue === undefined) {
            cell.originalValue = existingRow[field];
          }
        }
      }
    });
  }

  /**
   * Update the cell's dirty state after data has been refreshed
   * @param {Record<string, unknown>} existingRow existingRow The current row data before update
   * @param {number} row The row index
   * @param {Record<string, unknown>} data The new data being applied to the row
   */
  #updateCellDirtyState(existingRow: Record<string, any>, row: number, data: Record<string, unknown>) {
    if (!(existingRow && data)) return;

    Object.keys(data).forEach((field: any) => {
      if (existingRow[field] !== data[field]) {
        // Get the full column index from all columns
        const fullColumnIndex = this.columnIdxById(field);
        const columnData = this.columns[fullColumnIndex];

        if (columnData && fullColumnIndex !== -1 && (columnData.editor?.editorSettings?.dirtyTracker)) {
          // Find the visible index of this column
          const visibleIndex = this.visibleColumns.findIndex((col: any) => col.id === columnData.id);

          // Only proceed if the column is visible
          if (visibleIndex !== -1) {
            const rowIndex = this.rowByIndex(row);
            if (rowIndex) {
              // Use the visible index + 1 for aria-colindex
              const cell = rowIndex.querySelector(
                `ids-data-grid-cell[aria-colindex="${visibleIndex + 1}"]`
              ) as IdsDataGridCell;

              if (cell) {
                cell.trackValueChange(data[field], true);
              }
            }
          }
        }
      }
    });
  }

  /**
   * Find the parent id based on the cached props
   * @param {Array<Record<string, any>>} data the parent row that was clicked
   * @param {string} parentIds the string "1 2" of indexes
   * @returns {Record<string, unknown>} The child record
   */
  #findParentRow(data: Array<Record<string, any>>, parentIds: string): any {
    let childRow: any;
    parentIds.split(' ').forEach((r: string, index: number) => {
      if (index === 0) {
        // eslint-disable-next-line arrow-body-style
        childRow = data.find((row: Record<string, any>) => {
          if (!row) return false;
          // eslint-disable-next-line eqeqeq
          return row[this.idColumn] == r;
        });
      } else {
        // eslint-disable-next-line arrow-body-style
        childRow = childRow?.children.find((cRow: Record<string, any>) => {
          if (!cRow) return false;
          // eslint-disable-next-line eqeqeq
          return cRow[this.idColumn] == r;
        });
      }
    });
    return childRow;
  }

  #findRootRow(row: Record<string, any>) {
    if (!row.parentElement) return row;

    const rootId = row.parentElement.split(' ')[0];
    // eslint-disable-next-line eqeqeq
    const rootRow = this.data.find((item) => item[this.idColumn] == rootId);

    return rootRow;
  }

  /**
   * Updates row count attribute on container
   */
  #updateRowCount() {
    this.container?.setAttribute('aria-rowcount', this.rowCount.toString());
  }

  /**
   * Updates the selected status of the cells
   */
  updateSelectedCells() {
    const colSelectedCount = Array(this.visibleColumns.length).fill(0);

    this.data.forEach((row, rowIndex) => {
      if (row.colSelected) {
        row.colSelected.forEach((selected: boolean, colIndex: number) => {
          this.selectCell(rowIndex, colIndex, selected);
          colSelectedCount[colIndex] += selected ? 1 : 0;
        });
      }
    });

    colSelectedCount.forEach((selected: number, index: number) => {
      this.header?.setColumnCheckbox(index, selected >= this.rowCount);
    });
  }

  /**
   * Get the row HTMLElement
   * @param {number} rowIndex the zero based index
   * @returns {HTMLElement} Row HTMLElement
   */
  rowByIndex(rowIndex: number): IdsDataGridRow | undefined | null {
    const maxRowIndex = this.data.length - 1;
    rowIndex = Math.max(rowIndex, 0);
    rowIndex = Math.min(rowIndex, maxRowIndex);
    return this.shadowRoot?.querySelector<IdsDataGridRow>(`.ids-data-grid-body ids-data-grid-row[row-index="${rowIndex}"]`);
  }

  activeCellEditor?: IdsDataGridCell;

  /** Flag indicating endCellEdit is in progress; redraw() should defer */
  #isEndingCellEdit = false;

  /** Pending deferred redraw timer id */
  #pendingRedraw?: ReturnType<typeof setTimeout>;

  /**
   * Set the ending-cell-edit guard flag
   * @param {boolean} value true when endCellEdit is in progress
   */
  set isEndingCellEdit(value: boolean) { this.#isEndingCellEdit = value; }

  /**
   * Cancel any pending deferred redraw (e.g. when a new cell edit starts)
   */
  cancelPendingRedraw() {
    if (this.#pendingRedraw) {
      clearTimeout(this.#pendingRedraw);
      this.#pendingRedraw = undefined;
    }
  }

  /**
   * Commit editing on any active editor
   */
  commitCellEdit() {
    this.activeCellEditor?.endCellEdit();
  }

  /**
   * Cancel editing on any active editor
   */
  cancelCellEdit() {
    this.activeCellEditor?.cancelCellEdit();
  }

  /**
   * Add a row to the data grid
   * @param {Record<string, unknown>} data the data to add to the row
   * @param {number} index insert position for new row
   * @param {object} options optional settings
   * @param {string | number | false} options.setFocus column id or visible column index to focus, or false to skip focus.
   * Omit for no auto-focus.
   */
  addRow(data: Record<string, unknown>, index?: number, options?: AddRowSettings) {
    if (index !== undefined && index > 0 && this.data.length === 0) return;
    if (data) hideEmptyMessage.apply(this);

    const shouldSetNextId = data && (data[this.idColumn] === null || data[this.idColumn] === undefined);
    if (shouldSetNextId) data[this.idColumn] = this.nextId;

    const insertIdx = index ?? this.datasource.originalData.length;

    if (this.pager) {
      this.#updateDatasource(data, insertIdx);
      const pageNum = Math.floor(insertIdx / this.pageSize) + 1;

      if (options?.suppressPageSwitch !== true && this.pageNumber !== pageNum) {
        this.pageNumber = pageNum;
      }

      this.redrawBody();

      requestAnimationFrame(() => {
        this.#checkLastRowBorder();
      });
      this.#focusAddedRow(insertIdx, options?.setFocus);
      this.triggerEvent('rowadded', this, { detail: { data, index: insertIdx } });
      return;
    }
    const body = this.container?.querySelector('.ids-data-grid-body');

    if (insertIdx === 0) {
      // Insert at beginning - existing optimized logic
      this.#updateDatasource(data, insertIdx);
      const rowElement = IdsDataGridRow.template(data, insertIdx, insertIdx + 1, this);
      this.resetCache();
      body?.insertAdjacentHTML('afterbegin', rowElement);
      const rowsToUpdate = this.rows.slice(1);
      requestAnimationFrame(() => {
        rowsToUpdate.forEach((row) => {
          row.rowIndex += 1;
          row.refreshRow();
        });
      });
      this.#focusAddedRow(insertIdx, options?.setFocus);
    } else if (index === undefined || insertIdx >= this.rows.length) {
      // Insert at end - fast append
      this.#updateDatasource(data, insertIdx);
      const rowElement = IdsDataGridRow.template(data, insertIdx, insertIdx + 1, this);
      body?.insertAdjacentHTML('beforeend', rowElement);
      this.#focusAddedRow(insertIdx, options?.setFocus);
    } else {
      // Mid-insertion - insert row at specific position without full redraw
      this.#insertRowAtIndex(data, insertIdx, body ?? null);
      this.#focusAddedRow(insertIdx, options?.setFocus);
    }

    if (this.#activeIndicatorRow >= insertIdx) {
      this.#activeIndicatorRow += 1;
    }

    this.#updateRowCount();
    this.syncPagerAfterDatasetChange();
    requestAnimationFrame(() => this.#checkLastRowBorder());
    this.triggerEvent('rowadded', this, { detail: { data, index: insertIdx } });
  }

  /**
   * Update datasource with new row data
   * @private
   * @param {Record<string, unknown>} data Row data to add
   * @param {number} insertIdx Index to insert at
   */
  #updateDatasource(data: Record<string, unknown>, insertIdx: number) {
    this.datasource.create([data], insertIdx);
    this.datasource.refreshPreviousState();
  }

  /**
   * Insert row at specific index without full redraw
   * @private
   * @param {Record<string, unknown>} data Row data to insert
   * @param {number} insertIdx Index to insert at
   * @param {Element} body Grid body element
   */
  #insertRowAtIndex(data: Record<string, unknown>, insertIdx: number, body: Element | null) {
    const targetRow = this.rows[insertIdx];

    // Update datasource first
    this.#updateDatasource(data, insertIdx);

    // Force refresh of data getter by clearing any cache
    this.resetCache();

    // Generate template using the updated datasource data
    const rowElement = IdsDataGridRow.template(this.data[insertIdx], insertIdx, insertIdx + 1, this);

    if (targetRow) {
      targetRow.insertAdjacentHTML('beforebegin', rowElement);
    } else {
      body?.insertAdjacentHTML('beforeend', rowElement);
    }

    // Reset cache again to reflect DOM changes
    this.resetCache();

    // Only update subsequent rows if there are any (performance optimization)
    const subsequentRows = this.rows.slice(insertIdx + 1);
    if (subsequentRows.length > 0) {
      subsequentRows.forEach((row) => {
        row.rowIndex += 1;
        row.refreshRow();
      });
    }
  }

  /**
   * Add multiple rows to the data grid by calling addRow for each
   * @param {Array<Record<string, unknown>>} data Array of row data objects to add
   * @param {number} index Optional insert position for new rows (defaults to end)
   * @param {object} options optional settings
   * @param {string | number | false} options.setFocus column id or visible column index to focus on the last added row,
   * or false to skip focus. Omit for no auto-focus.
   */
  addRows(data: Array<Record<string, unknown>> = [], index?: number, options?: AddRowSettings) {
    const baseIdx = index ?? this.datasource.originalData.length;
    data.forEach((rowData, i) => {
      this.addRow(rowData, baseIdx + i, { suppressPageSwitch: options?.suppressPageSwitch });
    });
    if (options?.setFocus !== false && options?.setFocus !== undefined) {
      this.#focusAddedRow(baseIdx + data.length - 1, options.setFocus);
    }
  }

  /**
   * Focus a cell in a newly added row based on the setFocus parameter
   * @private
   * @param {number} rowIndex The index of the newly added row
   * @param {string | number | false | undefined} setFocus Column id, column index, or false/undefined to skip
   */
  #focusAddedRow(rowIndex: number, setFocus: string | number | false | undefined) {
    if (setFocus === false || setFocus === undefined) return;
    let colIndex: number;
    if (typeof setFocus === 'string') {
      colIndex = this.visibleColumnIdxById(setFocus);
      if (colIndex === -1) return;
    } else {
      colIndex = setFocus;
    }
    this.setActiveCell(colIndex, rowIndex, false, 'method');
  }

  /**
   * Remove a row by index for the data
   * @param {number} index the row index to remove
   * @param {Array<Record<string, unknown>>} data to be deleted
   */
  removeRow(index: number, data?: Record<string, unknown>) {
    // Update data
    if (data === undefined || !data[this.idColumn]) {
      data = this.data[index];
      data[this.idColumn] = this.data[index][this.idColumn] || this.uniqueId;
    }

    this.datasource.delete([data]);
    this.datasource.refreshPreviousState();

    if (this.#activeIndicatorRow === index) {
      this.#activeIndicatorRow = -1;
    } else if (this.#activeIndicatorRow > index) {
      this.#activeIndicatorRow -= 1;
    }

    // Update grid state
    this.redrawBody();
    this.#updateRowCount();
    this.syncPagerAfterDatasetChange();
    this.#checkLastRowBorder();

    this.triggerEvent('rowremoved', this, {
      detail: {
        elem: this, data
      }
    });
  }

  /**
   * Clear all values in a row a row by index
   * @param {number} index the row index to clear
   * @param {Array<Record<string, unknown>>} data to be cleared
   */
  clearRow(index: number, data?: Record<string, unknown>) {
    if (data === undefined) {
      data = {};
    }

    if (this.data[index]) {
      data.colSelected = this.data[index].colSelected;
    }

    this.updateDataset(index, data, true);
    this.datasource.refreshPreviousState();

    this.redrawBody();
  }

  /**
   * Corrects pager-related attributes after a change to the dataset
   */
  private syncPagerAfterDatasetChange() {
    if (this.pager) {
      this.pageTotal = this.datasource.total;
      if (this.pageNumber !== this.datasource.pageNumber) this.pageNumber = this.datasource.pageNumber;
    }
  }

  get isEditable() {
    return this.columns?.some((column) => !!column?.editor);
  }

  /**
   * Edit the first editable cell
   */
  editFirstCell() {
    if (this.isEditable) {
      const firstEditable = this.activeCell?.node?.parentElement
        ?.querySelector('.is-editable:not(.formatter-selectionCheckbox):not([hidden])') as IdsDataGridCell | null;
      if (firstEditable) {
        const columnIndex = firstEditable.columnIndex;
        const rowIndex = firstEditable.rowIndex;
        this.setActiveCell(columnIndex, rowIndex, false);
        firstEditable.startCellEdit();
      }
    }
  }

  /**
   * Set a row to selected
   * @param {number} index the zero based index
   * @param {boolean} triggerEvent fire an event with the selected row
   * @param {boolean} setActive also set the active cell to enable keyboard navigation
   */
  selectRow(index: number, triggerEvent = true, setActive = false) {
    const row = this.rowByIndex(index);

    // If virtual scroll and row not in DOM, just save state in data
    if (this.virtualScroll && !row && this.data[index]) {
      if (this.rowSelection === 'single') this.deSelectAllRows();
      this.updateDataset(index, { rowSelected: true });

      // Set internal state for shift-key selection to work properly
      this.#lastSelectedRow = index;
      if (this.#initiallySelectedRow === null) {
        this.#initiallySelectedRow = index;
      }
      return;
    }

    if (!row || row.selected) return;

    let canSelect = true;
    const response = (veto: any) => {
      canSelect = !!veto;
    };

    if (triggerEvent) {
      this.triggerEvent('beforerowselected', this, {
        detail: {
          elem: this, row, data: this.data[index], response
        }
      });
    }

    if (!canSelect) {
      return;
    }

    if (this.rowSelection === 'multiple' || this.rowSelection === 'mixed') {
      const checkbox = row?.querySelector('.is-selection-checkbox .ids-data-grid-checkbox');
      checkbox?.classList.add('checked');
      checkbox?.setAttribute('aria-checked', 'true');
    }

    if (this.rowSelection === 'single') {
      this.deSelectAllRows();
      const radio = row?.querySelector('.ids-data-grid-radio') || row?.querySelector('.is-selection-checkbox .ids-data-grid-checkbox');
      radio?.classList.add('checked');
      radio?.setAttribute('aria-checked', 'true');
    }

    this.updateDataset(index, { rowSelected: true });
    this.selectServerSide(index);
    row.selected = true;

    if ((this.rowSelection === 'single' || this.rowSelection === 'multiple') && row) {
      row.updateCells(index);
    }

    if (triggerEvent) {
      this.triggerEvent('rowselected', this, {
        detail: {
          elem: this, row, data: this.data[index]
        }
      });
    }

    if (this.groupSelectsChildren) row?.toggleChildRowSelection(true);
    this.header?.setHeaderCheckbox();

    // Set internal state for shift-key selection to work properly
    this.#lastSelectedRow = index;
    if (this.#initiallySelectedRow === null) {
      this.#initiallySelectedRow = index;
    }

    // Optionally set active cell to enable keyboard navigation
    if (setActive) {
      this.setActiveCell(0, index, false, 'method');
    }
  }

  /**
   * Select all cells in that column
   * @param {number} colIndex Column index
   */
  selectColumn(colIndex: number) {
    this.columns[colIndex].isSelected = true;
    for (let rowIndex = 0; rowIndex < this.rowCount; rowIndex++) {
      this.selectCell(rowIndex, colIndex, true);
    }
  }

  /**
   * Set the cell to selected or unselected
   * @param {number} rowIndex Row index of the target cell
   * @param {number} colIndex Column index of the target cell
   * @param {boolean} isSelected True to select, false to deselect
   */
  selectCell(rowIndex: number, colIndex: number, isSelected: boolean) {
    const cell = this.cellByIndex(rowIndex, colIndex);

    let colSelected = this.data[rowIndex].colSelected;
    if (!colSelected) {
      colSelected = Array(this.columns.length).fill(false);
    }

    if (isSelected) {
      cell?.classList.add('selected');
    } else {
      cell?.classList.remove('selected');
    }

    colSelected[colIndex] = isSelected;
    this.updateDataset(rowIndex, { colSelected });
  }

  /**
   * Stores a record in server-side selections
   * @param {number} index the row number to select
   * @returns {void}
   */
  selectServerSide(index: number) {
    if (this.pagination !== 'server-side') return;

    const record = this.data[index];
    // eslint-disable-next-line arrow-body-style
    const storedIndex = this.serverSideSelections.findIndex((item) => {
      return item.data[this.idColumn] === record[this.idColumn];
    });

    if (storedIndex === -1) {
      this.serverSideSelections.push({
        index,
        data: record
      });
    }
  }

  /**
   * When using server-side paging, syncs records coming from IdsDataSource
   * that should be "selected" according to previously-selected rows.
   * @returns {void}
   */
  syncServerSelections() {
    this.data.forEach((record, i) => {
      // eslint-disable-next-line arrow-body-style
      const serverSelected = this.serverSideSelections.findIndex((item) => {
        return item.data[this.idColumn] === record[this.idColumn];
      });
      if (serverSelected > -1) {
        this.data[i].rowSelected = true;
      }
    });
  }

  /**
   * Set a row to be deselected
   * @param {number} index the zero based index
   * @param {boolean} triggerEvent fire an event with the deselected row
   */
  deSelectRow(index: number, triggerEvent = true) {
    const row = this.rowByIndex(index);

    // If virtual scroll and row not in DOM, just save state in data
    if (this.virtualScroll && !row && this.data[index]) {
      this.updateDataset(index, { rowSelected: false });
      return;
    }

    if (!row) return;

    let canDeselect = true;
    const response = (veto: any) => {
      canDeselect = !!veto;
    };

    if (triggerEvent) {
      this.triggerEvent('beforerowdeselected', this, {
        detail: {
          elem: this, row, data: this.data[index], response
        }
      });
    }

    if (!canDeselect) {
      return;
    }

    if (this.rowSelection === 'mixed') {
      row.classList.remove('mixed');
    }
    row.classList.remove('selected');
    row.removeAttribute('aria-selected');

    if (this.rowSelection === 'multiple' || this.rowSelection === 'mixed') {
      const checkbox = row.querySelector('.is-selection-checkbox .ids-data-grid-checkbox');
      checkbox?.classList.remove('checked');
      checkbox?.setAttribute('aria-checked', 'false');
    }

    if (this.rowSelection === 'single') {
      const radio = row.querySelector('.ids-data-grid-radio') || row.querySelector('.is-selection-checkbox .ids-data-grid-checkbox');
      radio?.classList.remove('checked');
      radio?.setAttribute('aria-checked', 'false');
    }

    this.updateDataset(index, { rowSelected: false });
    this.deselectServerSide(index);

    if (triggerEvent) {
      this.triggerEvent('rowdeselected', this, {
        detail: {
          elem: this, row, data: this.data[index]
        }
      });
    }

    row.updateCells(index);
    if (this.groupSelectsChildren) row.toggleChildRowSelection(false);
    this.header?.setHeaderCheckbox();
  }

  /**
   * Deselect all the cell in the column
   * @param {number} colIndex Column index
   */
  deSelectColumn(colIndex: number) {
    this.columns[colIndex].isSelected = false;
    for (let rowIndex = 0; rowIndex < this.rowCount; rowIndex++) {
      this.selectCell(rowIndex, colIndex, false);
    }

    this.header?.setColumnCheckbox(colIndex, false);
  }

  /**
   * Removes a record from server-side selections
   * @param {number} index the row number to select
   * @returns {void}
   */
  deselectServerSide(index: number) {
    if (this.pagination !== 'server-side') return;

    const record = this.data[index];
    // eslint-disable-next-line arrow-body-style
    const storedIndex = this.serverSideSelections.findIndex((item) => {
      return item.data[this.idColumn] === record[this.idColumn];
    });
    if (storedIndex > -1) {
      this.serverSideSelections.splice(storedIndex, 1);
    }
  }

  /**
   * Set a row to activated
   * @param {number} index the zero based index
   */
  activateRow(index: number) {
    let row: any = index;
    if (typeof index === 'number') {
      row = this.rowByIndex(index);
    }

    if (!row || this.rowSelection !== 'mixed') {
      return;
    }

    (row as any).classList.add('activated');
    this.updateDataset(Number(row?.getAttribute('data-index')), { rowActivated: true });
    row?.updateCells(index);

    this.triggerEvent('rowactivated', this, {
      detail: {
        elem: this, row, data: this.data[index], index
      }
    });
  }

  /**
   * Set a row to be deactivated
   * @param {number} index the zero based index
   */
  deactivateRow(index: any) {
    if (typeof index === 'undefined' || index === null) {
      return;
    }
    let row = index;

    if (typeof index === 'number') {
      row = this.rowByIndex(index);
    }

    if (!row || this.rowSelection !== 'mixed') {
      return;
    }
    row.classList.remove('activated');
    this.updateDataset(Number(row?.getAttribute('data-index')), { rowActivated: undefined });
    row.updateCells(index);

    this.triggerEvent('rowdeactivated', this, {
      detail: {
        elem: this, row, data: this.data[index], index
      }
    });
  }

  /**
   * Set a all rows to be selected
   */
  selectAllRows() {
    this.data?.forEach((row: any, index: number) => {
      if (!row.disabled) {
        this.selectRow(index, !this.suppressSelectAllEvents);
        row.rowSelected = true;
      }
    });

    this.triggerEvent('selectionchanged', this, {
      detail: {
        elem: this,
        selectedRows: this.selectedRows
      }
    });
    this.header?.setHeaderCheckbox();
  }

  /**
   * Set a all rows to be deselected
   */
  deSelectAllRows() {
    this.data?.forEach((row: any, index: number) => {
      if (row.rowSelected) {
        this.deSelectRow(index, !this.suppressSelectAllEvents);
        row.rowSelected = false;
      }
    });

    if (this.colSelection || this.cellSelection) {
      this.columns.forEach((_col: IdsDataGridColumn, colIndex: number) => {
        this.deSelectColumn(colIndex);
      });
    }

    this.triggerEvent('selectionchanged', this, {
      detail: {
        elem: this,
        selectedRows: this.selectedRows
      }
    });
    this.header?.setHeaderCheckbox();
    this.#startSelectionCell = null;
    this.#endSelectionCell = null;
  }

  /**
   * Toggle the selection of all rows
   */
  toggleSelectAllRows() {
    // Check if all rows are currently selected
    const allRowsSelected = this.data?.every((row: any) => row.rowSelected);

    if (allRowsSelected) {
      // If any rows are selected, deselect all
      this.deSelectAllRows();
    } else {
      this.selectAllRows();
    }
  }

  /**
   * Set/Get the total number of records
   * @returns {number} The no of rows (flattened trees)
   */
  get rowCount() {
    return this.data.length;
  }

  /**
   * Get the row height in pixels
   * @private
   * @returns {number} The pixel height
   */
  get rowPixelHeight(): number {
    return IdsDataGrid.ROW_HEIGHT_PIXELS[this.rowHeight] ?? IdsDataGrid.ROW_HEIGHT_PIXELS.lg;
  }

  /**
   * Set the card to auto fit to its parent size
   * @param {boolean|string|null} value The auto fit
   */
  set autoFit(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.AUTO_FIT, String(value));
      return;
    }
    this.removeAttribute(attributes.AUTO_FIT);
  }

  get autoFit(): boolean {
    return stringToBool(this.getAttribute(attributes.AUTO_FIT));
  }

  /**
   * When true, walks up through ids-layout-flex-item, ids-layout-flex,
   * ids-layout-grid-cell, and ids-layout-grid ancestors and applies the
   * CSS classes needed for block-size / min-block-size to flow down so
   * the grid header and pager are sticky without requiring explicit
   * height attributes on every ancestor.
   * @param {boolean|string|null} value sticky-layout attribute value
   */
  set stickyLayout(value) {
    const val = stringToBool(value);
    this.toggleAttribute(attributes.STICKY_LAYOUT, val);

    if (val) {
      this.#applyStickyLayout();
    } else {
      this.#cleanupStickyLayout();
    }
  }

  get stickyLayout(): boolean {
    return stringToBool(this.getAttribute(attributes.STICKY_LAYOUT));
  }

  /** CSS class added to ancestors to propagate block-size constraint */
  static readonly STICKY_LAYOUT_CLASS = 'ids-data-grid-sticky-layout-ancestor';

  /** Pixel heights for each row-height token; kept in one place so setters/getters agree. */
  static readonly ROW_HEIGHT_PIXELS: Record<string, number> = {
    xxs: 24,
    xs: 30,
    sm: 34,
    md: 40,
    lg: 50
  };

  /**
   * Walks recognized layout ancestors and adds a CSS class that applies
   * block-size:100% / min-block-size:0 so height flows down without
   * explicit height attributes in the markup.
   * @private
   */
  #applyStickyLayout(): void {
    const cls = (this.constructor as typeof IdsDataGrid).STICKY_LAYOUT_CLASS;
    const tags = new Set([
      'ids-layout-flex-item',
      'ids-layout-flex',
      'ids-layout-grid-cell',
      'ids-layout-grid',
      'ids-tab-content',
      'ids-tabs-context'
    ]);

    // Check if the data grid is inside a card-content slot to calculate adjusted height
    let cardContentAncestor: HTMLElement | null = this.parentElement;
    let isInCardContent = false;
    while (cardContentAncestor) {
      if (cardContentAncestor.slot === 'card-content') {
        isInCardContent = true;
        break;
      }
      cardContentAncestor = cardContentAncestor.parentElement;
    }

    let el: HTMLElement | null = this.parentElement;
    let lastTraversed: HTMLElement | null = null;
    while (el) {
      const tag = el.tagName?.toLowerCase();
      if (!tag || !tags.has(tag)) break;
      el.classList.add(cls);

      // Auto-apply necessary attributes for constraint chain
      const appliedAttrs: string[] = [];
      if (tag === 'ids-layout-flex-item') {
        if (!el.hasAttribute(attributes.GROW)) {
          appliedAttrs.push(attributes.GROW);
          el.setAttribute(attributes.GROW, '1');
        }
        if (!el.hasAttribute(attributes.OVERFLOW)) {
          appliedAttrs.push(attributes.OVERFLOW);
          el.setAttribute(attributes.OVERFLOW, 'hidden');
        }
      } else if (tag === 'ids-layout-flex') {
        if (!el.hasAttribute(attributes.HEIGHT)) {
          // If inside card-content, calculate height accounting for padding
          if (isInCardContent) {
            const paddingY = el.getAttribute(attributes.PADDING_Y) || el.getAttribute(attributes.PADDING);
            // Padding sizes: xxs=4px, xs=8px, sm=16px, md=24px, lg=32px, xl=40px
            const paddingSizeMap: Record<string, number> = {
              xxs: 4, xs: 8, sm: 16, md: 24, lg: 32, xl: 40
            };
            appliedAttrs.push(attributes.HEIGHT);
            const paddingPx = paddingSizeMap[paddingY || ''] || 0;
            // Subtract padding + 8px buffer
            const adjustedHeight = paddingPx > 0 ? `calc(100% - ${paddingPx + 8}px)` : '100%';
            el.setAttribute(attributes.HEIGHT, adjustedHeight);
          } else {
            el.setAttribute(attributes.HEIGHT, '100%');
          }
        }
      } else if (tag === 'ids-layout-grid-cell') {
        if (!el.hasAttribute(attributes.OVERFLOW)) {
          appliedAttrs.push(attributes.OVERFLOW);
          el.setAttribute(attributes.OVERFLOW, 'hidden');
        }
      } else if (tag === 'ids-layout-grid') {
        if (!el.hasAttribute(attributes.HEIGHT)) {
          appliedAttrs.push(attributes.HEIGHT);
          el.setAttribute(attributes.HEIGHT, '100%');
        }
        if (!el.hasAttribute(attributes.OVERFLOW)) {
          appliedAttrs.push(attributes.OVERFLOW);
          el.setAttribute(attributes.OVERFLOW, 'hidden');
        }
      }

      if (appliedAttrs.length) {
        el.dataset.stickyAttrApplied = appliedAttrs.join(',');
      }

      lastTraversed = el;
      el = el.parentElement;
    }

    // Accordion fallback: if the walk stopped at an ids-accordion-panel,
    // or at a generic element (e.g. <div slot="content">) whose parent is
    // an ids-accordion-panel, the panel's content slot has height:auto so
    // percentage heights collapse. Apply a default fixed height to the topmost
    // traversed layout container to provide a block-size constraint.
    // The height is driven by the CSS custom property --ids-data-grid-sticky-accordion-height
    // which consumers can override to customize the default.
    let isAccordionContext = false;
    if (el?.tagName?.toLowerCase() === 'ids-accordion-panel') {
      isAccordionContext = true;
    } else if (el && el.slot === 'content' && el.parentElement?.tagName?.toLowerCase() === 'ids-accordion-panel') {
      isAccordionContext = true;
    }

    if (isAccordionContext && lastTraversed) {
      const contentSlotEl = lastTraversed;
      const tag = contentSlotEl.tagName.toLowerCase();
      if ((tag === 'ids-layout-flex' || tag === 'ids-layout-grid') && !contentSlotEl.dataset.stickyHeightApplied) {
        const currentHeight = contentSlotEl.getAttribute(attributes.HEIGHT);
        // Only apply default if height is 100% (set by us above) or missing —
        // meaning no explicit fixed height was provided by the consumer
        if (!currentHeight || currentHeight === '100%') {
          const customHeight = getComputedStyle(this).getPropertyValue('--ids-data-grid-sticky-accordion-height').trim();
          contentSlotEl.setAttribute(attributes.HEIGHT, customHeight || '300px');
          contentSlotEl.dataset.stickyHeightApplied = 'true';
        }
      }
    }
  }

  /**
   * Removes the sticky-layout CSS class and attributes from all ancestors.
   * @private
   */
  #cleanupStickyLayout(): void {
    const cls = (this.constructor as typeof IdsDataGrid).STICKY_LAYOUT_CLASS;
    const tags = new Set([
      'ids-layout-flex-item',
      'ids-layout-flex',
      'ids-layout-grid-cell',
      'ids-layout-grid',
      'ids-tab-content',
      'ids-tabs-context'
    ]);
    let el: HTMLElement | null = this.parentElement;
    while (el) {
      const tag = el.tagName?.toLowerCase();
      if (!tag || !tags.has(tag)) break;
      el.classList.remove(cls);

      // Remove attributes that were programmatically added by #applyStickyLayout
      if (el.dataset.stickyAttrApplied) {
        const currentEl = el;
        const attrs = currentEl.dataset.stickyAttrApplied?.split(',') ?? [];
        attrs.forEach((attr) => currentEl.removeAttribute(attr));
        delete currentEl.dataset.stickyAttrApplied;
      }
      if (el.dataset.stickyHeightApplied) {
        delete el.dataset.stickyHeightApplied;
      }

      el = el.parentElement;
    }
  }

  /**
   * Ensures a row is visible in the viewport when virtual scrolling is enabled
   * @private
   * @param {number} rowIndex The row index to check
   * @param {IdsDataGridRow} rowNode The row element
   */
  #ensureRowVisibleInViewport(rowIndex: number, rowNode: IdsDataGridRow | null | undefined) {
    if (!this.virtualScroll || !this.container || !rowNode) {
      return;
    }

    const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;
    const scrollTop = this.container.scrollTop || 0;
    const headerHeight = this.header?.clientHeight ?? 0;
    const rowTop = rowIndex * rowHeight;
    const rowBottom = rowTop + rowHeight;
    const viewportTop = scrollTop + headerHeight;
    const viewportBottom = scrollTop + this.container.clientHeight;

    if (rowBottom <= viewportTop) {
      this.#ensureRowInViewport(rowIndex, IdsDirection.Up);
    } else if (rowTop >= viewportBottom - rowHeight) {
      this.#ensureRowInViewport(rowIndex, IdsDirection.Down);
    }
  }

  /**
   * Set the container height
   * @private
   */
  #applyAutoFit() {
    if (this.autoFitSet) {
      return;
    }

    if (this.autoFit === true) {
      const hasSiblings = this.parentElement && this.parentElement.children.length > 1;
      const isInLayoutGridCell = this.parentElement?.tagName?.toLowerCase() === 'ids-layout-grid-cell';

      // Use Flexbox to fit available space if other sibling (only if not in layout-grid-cell)
      if (hasSiblings && !isInLayoutGridCell) {
        this.parentElement.style.display = 'flex';
        this.parentElement.style.flexDirection = 'column';
        this.parentElement.style.height = '100%';
        this.style.flex = '1';
        this.style.minHeight = '0';
      }

      const height = this.pagination === 'none' ? '100%' : `calc(100% - 44px)`;
      this.container?.style.setProperty('height', height);
      this.wrapper?.style.setProperty('height', '100%');
      this.autoFitSet = true;
    }
  }

  /**
   * Suppress row row and cell caching
   * @param {boolean|string|null} value false to not cache
   */
  set suppressCaching(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.SUPPRESS_CACHING, String(value));
      return;
    }
    this.removeAttribute(attributes.SUPPRESS_CACHING);
  }

  get suppressCaching(): boolean {
    return stringToBool(this.getAttribute(attributes.SUPPRESS_CACHING)) || false;
  }

  /**
   * Set the active cell for focus
   * @param {number} cellNumber The cell to focus (zero based)
   * @param {number} rowIndex The row to focus (zero based)
   * @param {boolean} noFocus If true, do not focus the cell
   * @param {string} reason Explains why the cell is being set active
   * @returns {object} the current active cell
   */
  setActiveCell(cellNumber: number, rowIndex: number, noFocus?: boolean, reason?: IdsDataGridActivationReason) {
    if (rowIndex < 0 || cellNumber < 0 || rowIndex > this.data.length - 1
      || cellNumber > this.visibleColumns.length - 1 || Number.isNaN(rowIndex) || Number.isNaN(rowIndex)) {
      return this.activeCell;
    }
    const columnSettings = this.columns.find((column) => column?.rowIndicatorConfig?.activeRowIndicator);

    if (!this.activeCell) this.activeCell = {};
    this.activeCell.cell = Number(cellNumber);
    this.activeCell.row = Number(rowIndex);

    // Cache the active cell state
    const currentCell = this.cellByIndex(rowIndex, cellNumber);
    if (reason === 'redraw') {
      this.clearActiveCellCache();
    } else {
      this.updateActiveCellCache(rowIndex, cellNumber, currentCell?.value, currentCell?.isEditing);
    }

    let rowNode = this.rowByIndex(rowIndex);
    if (!rowNode && this.virtualScroll) {
      const setActiveCellData = {
        row: rowIndex,
        cell: cellNumber
      };

      this.#afterSetActiveCellFn = () => {
        const rowEl = this.rowByIndex(setActiveCellData.row);
        const cellNode = rowEl?.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${setActiveCellData.cell + 1}"]`);
        if (cellNode) {
          cellNode.activate(Boolean(noFocus), reason);
        }
      };
      this.#scrollRowIntoView(rowIndex);
      rowNode = this.rowByIndex(rowIndex);
      return this.activeCell;
    }

    const cellNode = rowNode?.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${cellNumber + 1}"]`);

    // Ensure row is visible in viewport before activation to prevent jumps
    // Skip when restoring active cell during scroll to avoid fighting user's scroll
    if (reason !== 'internal') {
      this.#ensureRowVisibleInViewport(rowIndex, rowNode);
    }

    if (this.virtualScroll && this.body && rowNode && this.rows[0]) {
      const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;
      const firstRowIndex = this.rows[0].rowIndex || 0;
      const effectiveIndex = this.treeGrid ? this.#getVisibleRowIndex(firstRowIndex) : firstRowIndex;
      if (effectiveIndex === -1) return this.activeCell;
      const offsetTop = effectiveIndex * rowHeight;
      this.body.style.setProperty('transform', `translateY(${offsetTop}px)`);
    }

    if (cellNode) {
      cellNode.activate(Boolean(noFocus), reason);
    }

    if (columnSettings?.rowIndicatorConfig?.activeRowIndicator && reason !== 'redraw') {
      this.setActiveRowIndicator(rowIndex);
    }

    this.activeCell.node = rowNode?.querySelector<IdsDataGridCell>(`ids-data-grid-cell[aria-colindex="${cellNumber + 1}"]`) || null;
    return this.activeCell;
  }

  /**
   * Sets disable client filter
   * @param {boolean|string} value IThe value
   */
  set disableClientFilter(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISABLE_CLIENT_FILTER, '');
    } else {
      this.removeAttribute(attributes.DISABLE_CLIENT_FILTER);
    }
  }

  get disableClientFilter() {
    const value = this.getAttribute(attributes.DISABLE_CLIENT_FILTER);
    return value !== null ? stringToBool(value) : this.filters.DEFAULTS.disableClientFilter;
  }

  /**
   * Sets the data grid to be filterable
   * @param {boolean|string} value If true will set filterable
   */
  set filterable(value) {
    if (typeof value !== 'undefined' && value !== null) {
      this.setAttribute(attributes.FILTERABLE, String(value));
    } else {
      this.removeAttribute(attributes.FILTERABLE);
    }
    this.header?.setFilterRow();
  }

  get filterable() {
    const value = this.getAttribute(attributes.FILTERABLE);
    return value !== null ? stringToBool(value) : this.filters.DEFAULTS.filterable;
  }

  /**
   * Sets the minimum column index that can be reordered.
   * Columns before this index will remain fixed at the start.
   * @param {number|string} value The minimum draggable column index
   */
  set firstDraggableColumn(value: number | string | null) {
    if (typeof value !== 'undefined' && value !== null) {
      this.setAttribute(attributes.FIRST_DRAGGABLE_COLUMN, String(value));
    } else {
      this.removeAttribute(attributes.FIRST_DRAGGABLE_COLUMN);
    }
  }

  /**
   * Gets the minimum column index that can be reordered
   * @returns {number} The minimum draggable column index
   */
  get firstDraggableColumn(): number {
    const value = this.getAttribute(attributes.FIRST_DRAGGABLE_COLUMN);
    return value !== null ? parseInt(value, 10) : 0;
  }

  /**
   * Keep filter conditions when calling `data` to reload data
   * @param {boolean | string} value If true will reapply the filter
   */
  set keepFilterConditions(value: boolean | string) {
    const keep = stringToBool(value);
    if (keep) {
      this.setAttribute(attributes.KEEP_FILTER_CONDITIONS, String(keep));
    } else {
      this.removeAttribute(attributes.KEEP_FILTER_CONDITIONS);
    }
  }

  get keepFilterConditions(): boolean | string {
    return stringToBool(this.getAttribute(attributes.KEEP_FILTER_CONDITIONS));
  }

  /**
   * Sets disabled to be filter row
   * @param {boolean|string} value The value
   */
  set filterRowDisabled(value) {
    const isApply = this.filterRowDisabled !== stringToBool(value);
    if (typeof value !== 'undefined' && value !== null) {
      this.setAttribute(attributes.FILTER_ROW_DISABLED, String(value));
    } else {
      this.removeAttribute(attributes.FILTER_ROW_DISABLED);
    }
    if (isApply) this.filters?.setFilterRowDisabled();
  }

  get filterRowDisabled() {
    const value = this.getAttribute(attributes.FILTER_ROW_DISABLED);
    return value !== null ? stringToBool(value) : this.filters.DEFAULTS.filterRowDisabled;
  }

  /**
   * Sets the data grid to filter when typing
   * @param {boolean|string} value The value
   */
  set filterWhenTyping(value) {
    const isApply = this.filterWhenTyping !== stringToBool(value);
    if (typeof value !== 'undefined' && value !== null) {
      this.setAttribute(attributes.FILTER_WHEN_TYPING, String(value));
    } else {
      this.removeAttribute(attributes.FILTER_WHEN_TYPING);
    }
    if (isApply) this.filters?.setFilterWhenTyping();
  }

  get filterWhenTyping() {
    const value = this.getAttribute(attributes.FILTER_WHEN_TYPING);
    return value !== null ? stringToBool(value) : this.filters.DEFAULTS.filterWhenTyping;
  }

  /**
   * Sets the grid to render as a tree grid (does require a tree formatter column)
   * @param {boolean|string} value The value
   */
  set treeGrid(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.TREE_GRID, value.toString());
    } else {
      this.removeAttribute(attributes.TREE_GRID);
    }
  }

  get treeGrid() {
    return this.hasAttribute(attributes.TREE_GRID);
  }

  /**
   * Sets the grid to group rows (only one field is supported)
   * @param {GroupableOptions} value The group by options
   */
  set groupable(value: GroupableOptions) {
    if (value) {
      this.state.groupable = value;
    } else {
      delete this.state.groupable;
    }
  }

  get groupable(): GroupableOptions {
    return this.state.groupable;
  }

  /**
   * If true then the children will be selected when a group is selected
   * @param {boolean|string} value The value
   */
  set groupSelectsChildren(value) {
    value = stringToBool(value);
    if (value) {
      this.setAttribute(attributes.GROUP_SELECTS_CHILDREN, value.toString());
    } else {
      this.removeAttribute(attributes.GROUP_SELECTS_CHILDREN);
    }
  }

  get groupSelectsChildren() {
    return stringToBool(this.getAttribute(attributes.GROUP_SELECTS_CHILDREN)) || false;
  }

  /**
   * Used to set which column is the unique id column in the data set.
   * This is needed for some operations.
   * @param {string} value The value
   */
  set idColumn(value) {
    if (value) {
      this.setAttribute(attributes.ID_COLUMN, value.toString());
      this.datasource.primaryKey = value;
    } else {
      this.removeAttribute(attributes.ID_COLUMN);
      this.datasource.primaryKey = this.idColumn;
    }
  }

  get idColumn() {
    return this.getAttribute(attributes.ID_COLUMN) || 'id';
  }

  /**
   * If true an expandable row is present in the grid. Also requires a expandable-row-template and
   * an expander formatter.
   * @param {string} value The value
   */
  set expandableRow(value) {
    if (value) {
      this.setAttribute(attributes.EXPANDABLE_ROW, value.toString());
    } else {
      this.removeAttribute(attributes.EXPANDABLE_ROW);
    }
  }

  get expandableRow() {
    return this.getAttribute(attributes.EXPANDABLE_ROW) || false;
  }

  /**
   * This setting will allow only one expandable-row to be opened/expanded at a time.
   * @param {string} value The value
   */
  set allowOneExpandedRow(value) {
    this.toggleAttribute(attributes.ALLOW_ONE_EXPANDED_ROW, stringToBool(value));
  }

  get allowOneExpandedRow() {
    return this.hasAttribute(attributes.ALLOW_ONE_EXPANDED_ROW);
  }

  /**
   * An id that points to the template to use for expandable rows. Also requires the expandable-row setting
   * and an expander formatter.
   * @param {string} value The value
   */
  set expandableRowTemplate(value) {
    if (value) {
      this.setAttribute(attributes.EXPANDABLE_ROW_TEMPLATE, value.toString());
    } else {
      this.removeAttribute(attributes.EXPANDABLE_ROW_TEMPLATE);
    }
  }

  get expandableRowTemplate() {
    return this.getAttribute(attributes.EXPANDABLE_ROW_TEMPLATE) || '';
  }

  /**
   * Set uniqueId to save to local storage.
   * @param {number|string|null} value A uniqueId use to save to local storage.
   */
  set uniqueId(value: number | string | null) {
    const val = /number|string/g.test(typeof value) ? `${value}` : null;
    if (typeof val === 'string' && val !== '') {
      this.setAttribute(attributes.UNIQUE_ID, val);
    } else {
      this.removeAttribute(attributes.UNIQUE_ID);
    }
  }

  get uniqueId(): string | null { return this.getAttribute(attributes.UNIQUE_ID); }

  /**
   * Set to true if one or more editors is present to activate editing
   * @param {boolean} value true indicates some cells may be editable
   */
  set editable(value: boolean) {
    value = stringToBool(value);
    if (value) {
      this.setAttribute(attributes.EDITABLE, value.toString());
    } else {
      this.removeAttribute(attributes.EDITABLE);
    }
  }

  get editable(): boolean {
    return stringToBool(this.getAttribute(attributes.EDITABLE));
  }

  /**
   * Set to false to avoid moving up and down rows when editing and hitting enter
   * @param {boolean} value false indicates the grid will not switch rows on edit
   */
  set editNextOnEnterPress(value: boolean) {
    value = stringToBool(value);
    if (value) {
      this.setAttribute(attributes.EDIT_NEXT_ON_ENTER_PRESS, value.toString());
    } else {
      this.removeAttribute(attributes.EDIT_NEXT_ON_ENTER_PRESS);
    }
  }

  get editNextOnEnterPress(): boolean {
    return stringToBool(this.getAttribute(attributes.EDIT_NEXT_ON_ENTER_PRESS));
  }

  /**
   * Set to true to automatically append rows when keyboard navigating
   * the data grid in editable mode
   * @param {boolean | string} val boolean flag
   */
  set addNewAtEnd(val: boolean | string) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.ADD_NEW_AT_END, 'true');
    } else {
      this.removeAttribute(attributes.ADD_NEW_AT_END);
    }
  }

  get addNewAtEnd(): boolean | string {
    return stringToBool(this.getAttribute(attributes.ADD_NEW_AT_END));
  }

  /**
   * Get all the currently invalid cells
   * @returns {Array<{ row: number, cell: number, columnId: string | null, validationMessages: any }>} cell invalid info
   */
  get invalidCells(): Array<{ row: number, cell: number, columnId: string | null, validationMessages: any }> {
    const invalidCells: Array<{ row: number, cell: number, columnId: string | null, validationMessages: any }> = [];
    for (let index = 0; index < this.data.length; index++) {
      if (this.data[index]?.invalidCells) {
        this.data[index].invalidCells?.forEach((invalidCellInRow: any) => {
          invalidCellInRow.row = index;
        });
        invalidCells.push(...this.data[index].invalidCells);
      }
    }
    return invalidCells;
  }

  /**
   * Get all the currently dirty cells
   * @returns {Array<{ row: number, cell: number, columnId: string | null, originalValue: any }>} info about the dirty cells
   */
  get dirtyCells(): Array<{ row: number, cell: number, columnId: string | null, originalValue: any }> {
    const dirtyCells: Array<{ row: number, cell: number, columnId: string | null, originalValue: any }> = [];
    for (let index = 0; index < this.data.length; index++) {
      if (this.data[index]?.dirtyCells) {
        this.data[index].dirtyCells?.forEach((dirtyCellInRow: any) => {
          dirtyCellInRow.row = index;
        });
        dirtyCells.push(...this.data[index].dirtyCells);
      }
    }
    return dirtyCells;
  }

  /**
   * Reset any currently dirty cells
   */
  resetDirtyCells() {
    this.data.forEach((row, idx) => {
      this.resetDirtyRow(idx);
    });
  }

  /**
   * Reset any currently dirty cells for a specific row
   * @param {number} rowIndex The row index to reset the dirty state
   */
  resetDirtyRow(rowIndex: number) {
    const row = this.rowByIndex(rowIndex);
    const rowData = this.data[rowIndex];

    row?.querySelectorAll('ids-data-grid-cell.is-dirty').forEach((cell) => {
      cell.classList.remove('is-dirty');
    });

    // Reset the dirty cell state in the data as well
    if (rowData?.dirtyCells) {
      delete rowData.dirtyCells;
    }
  }

  /**
   * Toggles text mask visibility for all cells in a specified column.
   * @param {number} colIndex - The column index (1-based) to toggle
   * @param {boolean | undefined} mask - Optional mask state; if undefined, toggles current state
   * @returns {void}
   */
  toggleTextMaskColumn(colIndex: number, mask: boolean | undefined = undefined) {
    const formatterFn = new IdsDataGridFormatters();
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      const cellElement = row?.querySelector<IdsDataGridCell>(`[aria-colindex="${colIndex}"]`);
      const columnData = this.columns.at(colIndex - 1);
      const dataIndex = row.getAttribute('data-index');

      if (dataIndex === null || !cellElement || !columnData || !columnData?.formatter) {
        return;
      }

      const cellData = this.data[+dataIndex][columnData.id];

      formatterFn.textmaskAction(cellData, cellElement, columnData, mask);
      this.tooltip.detachAllEvents();
    }
  }

  /**
   * Export data grid to excel
   * @param {string} format csv or xlsx
   * @param {string} filename filename
   * @param {object} options export options
   * @param {boolean} options.keepGridFormatting keep grid formatting, or pass raw datasource data (default: true)
   * @param {boolean} options.useCurrentFilter use current filter conditions to export only filtered data (default: true)
   */
  exportToExcel(format: 'csv' | 'xlsx', filename: string, options: { keepGridFormatting?: boolean, useCurrentFilter?: boolean } = {}) {
    const { keepGridFormatting = true, useCurrentFilter = true } = options;
    const xlColumns: Record<string, ExcelColumn> = {};
    const gridColCache: Record<string, IdsDataGridColumn> = {};
    format = format === 'csv' || format === 'xlsx' ? format : 'xlsx';
    const finalKeepGridFormatting = format === 'csv' ? true : keepGridFormatting;

    // create excel col config from grid column config
    this.columns.forEach((gridCol) => {
      if (gridCol.id && gridCol.field && gridCol.name && gridCol?.exportable !== false) {
        gridColCache[gridCol.id] = gridCol;

        xlColumns[gridCol.id] = {
          id: gridCol.id,
          field: gridCol.field,
          name: Array.isArray(gridCol.name)
            ? `${gridCol.name[0]} ${gridCol.name[0].emphasis === 'subtle' ? '(' : ''}${gridCol.name[0]}${gridCol.name[0].emphasis === 'subtle' ? ')' : ''}`
            : gridCol.name,
          type: finalKeepGridFormatting ? 'string' : this.determineColType(gridCol)
        };
      }
    });

    // Get excel data from grid data source
    const elem = document.createElement('span');
    const sourceData = useCurrentFilter ? this.datasource.currentData : this.datasource.data;
    const xlData: Array<Record<string, any>> = !finalKeepGridFormatting
      ? sourceData
      : sourceData.map((rowData, rowIndex) => {
        const xlDataRow: Record<string, any> = {};

        Object.keys(xlColumns).forEach((id) => {
          const gridCol = gridColCache[id];
          const formatter = gridCol?.formatter;
          const isNumber = formatter === this.formatters.decimal || formatter === this.formatters.integer;
          const isCheckbox = formatter === this.formatters.checkbox;
          const rawValue = rowData[xlColumns[id].field];
          let excelValue = rawValue;

          // Apply custom export formatter if provided
          if (gridCol.exportFormatter) excelValue = gridCol.exportFormatter(rawValue, rowData);
          else if (isCheckbox && typeof rawValue === 'boolean') excelValue = rawValue ? 'True' : 'False';
          // use grid formatted strings for non number truthy values
          else if (rawValue !== undefined && rawValue !== null && !isNumber && formatter) {
            if (typeof formatter === 'function') {
              const formattedValue = formatter.call(this.formatters, rowData, gridCol!, rowIndex, this);
              elem.innerHTML = formattedValue;
              excelValue = elem.textContent?.trim();
            }
          }

          xlDataRow[id] = excelValue;
        });

        return xlDataRow;
      });

    const exporter = format === 'csv' ? exportToCSV : exportToXLSX;
    exporter(xlData, {
      filename: filename || 'data-grid',
      columns: Object.values(xlColumns)
    });
  }

  /**
   * Get excel data type from data grid column formattter
   * @param {IdsDataGridColumn} gridCol grid column config
   * @returns {string} matching excel type
   */
  private determineColType(gridCol: IdsDataGridColumn): string {
    let type = 'string';

    if (typeof gridCol.formatter === 'function' && (gridCol.formatter === this.formatters.integer || gridCol.formatter === this.formatters.decimal)) {
      type = 'number';
    }

    if (typeof gridCol.formatter === 'function' && gridCol.formatter === this.formatters.date) {
      type = 'date';
    }

    if (typeof gridCol.formatter === 'function' && gridCol.formatter === this.formatters.time) {
      type = 'time';
    }

    return type;
  }

  /**
   * Find a specific IdsDataGridCell element using row-index and column-index.
   * @param {number} rowIndex the zero-based row index
   * @param {number} columnIndex the zero-based column index
   * @returns {IdsDataGridCell} html element for cell
   */
  cellByIndex(rowIndex: number, columnIndex: number): IdsDataGridCell | null {
    return this.rowByIndex(rowIndex)?.cellByIndex?.(columnIndex) ?? null;
  }

  /**
   * Removes filter row menus that have been moved from filter row headers into the data grid wrapper
   * @private
   */
  #removeAttachedMenus() {
    const allPopups = this?.shadowRoot?.querySelectorAll('ids-date-picker-popup[data-filter-type="date"], ids-time-picker-popup[data-filter-type="time"], ids-popup-menu');
    allPopups?.forEach((popup: any) => {
      popup.remove();
    });
  }

  /**
   * Checks on the active cell editor to see if its current state allows it to be closed.
   * @returns {boolean} true if the cell editor is able to "close"
   */
  private activeCellCanClose(): boolean {
    if (this.activeCellEditor?.isEditing) return false;
    if (this.activeCellEditor?.editor?.popup?.visible) return false;
    if (this.activeCellEditor?.column?.editor?.inline) return false;

    return true;
  }

  /**
   * Hide any open context menus
   * @private
   */
  hideOpenMenus() {
    const openMenus = document.querySelector<IdsPopupMenu>('ids-popup-menu:not([hidden])');
    if (openMenus) {
      openMenus.hide();
    }
  }

  /**
   * Inherited from `ids-pager-mixin`.
   * Fires when the pager requires a data reload
   */
  onPagingReload() {
    if (this.pagination !== 'server-side') {
      this.redrawBody();
    }
  }

  modalTemplate = `<ids-modal id="datagrid-modal" scrollable>
      <ids-text slot="title" font-size="24" type="h2" id="datagrid-modal-title">${this.localeAPI.translate('PersonalizeColumns')}</ids-text>
      <ids-button-group slot="buttons" id="button-group-modal" appearance="primary">
        <ids-button-group-section gap="8">
          <ids-modal-button id="modal-close-btn" appearance="primary">
            <span>${this.localeAPI.translate('Close')}</span>
          </ids-modal-button>
        </ids-button-group-section>
      </ids-button-group>
      <ids-search-field clearable label="${this.localeAPI.translate('SearchColumnName')}" label-state="collapsed" size="full" class="data-grid-personalization-search"></ids-search-field>
      <ids-swappable></ids-swappable>
    </ids-modal>`;

  /**
   * Show a modal with selection options for the columns and grouped rows
   */
  async showPersonalizationDialog() {
    // Append and translate modal
    const modalSlot = this.shadowRoot!.querySelector('slot[name="modal-container"]')!;
    modalSlot.innerHTML = this.modalTemplate;
    const modal = modalSlot.querySelector('#datagrid-modal') as IdsModal;
    const closeButton = modalSlot.querySelector('#modal-close-btn')!;
    closeButton.querySelector('span')!.textContent = this.localeAPI.translate('Close');
    modal.querySelector('[slot="title"]')!.textContent = this.localeAPI.translate('PersonalizeColumns');

    const searchfield = modal.querySelector<IdsSearchField>('ids-search-field')!;
    searchfield!.placeholder = this.localeAPI.translate('SearchColumnName');
    searchfield!.label = this.localeAPI.translate('SearchColumnName');

    // Populate Columns
    let swappables = '';
    const minDraggableIndex = this.firstDraggableColumn;

    this.columns.forEach((column) => {
      if (column.formatter === 'selectionCheckbox' || column.personalizable === false) {
        return;
      }

      const originalIndex = this.columns.indexOf(column);
      const isDragDisabled = !column?.reorderable || originalIndex < minDraggableIndex;

      const item = `<ids-swappable-item ${isDragDisabled ? ' disable-drag' : ''} column-id="${column?.id}" ${column?.hideable === false ? ` disabled="true"` : ''}>
            <ids-text font-size="16" type="p">${column?.name}</ids-text>
            <ids-switch${!column?.hidden && !column?.hiddenAtRuntime ? ` checked="true"` : ''}${column?.hideable === false ? ` disabled="true"` : ''} name="${column?.id}"></ids-switch>
        </ids-swappable-item>`;
      swappables += item;
    });

    const swappable = modal.querySelector('ids-swappable');
    swappable!.innerHTML = swappables;

    // Event Handlers for the dialog
    modal.onEvent('click', closeButton, async () => {
      await modal.hide();
      modal.remove();
    });
    modal.onEvent('change', modal, async (e: CustomEvent) => {
      if (e.detail?.elem?.tagName === 'IDS-SEARCH-FIELD') return;
      this.setColumnVisible(e?.detail?.elem.getAttribute('name'), Boolean((e?.target as IdsSwitch).checked));
    });
    modal.onEvent('swapped', swappable, async (e: CustomEvent) => {
      const filtered = this.columns.filter((c) => c.formatter !== 'selectionCheckbox' && c.personalizable !== false);
      this.moveColumn(
        this.columnIdxById(filtered[e.detail?.fromIndex]?.id),
        this.columnIdxById(filtered[e.detail?.toIndex]?.id),
        true
      );
    });

    searchfield.offEvent('keyup', searchfield);
    searchfield.onEvent('keyup', searchfield, async (e: CustomEvent) => {
      if (!e.detail) this.#filterColumnList(modal, searchfield.value);
    });
    searchfield.onEvent('cleared', searchfield, async () => {
      this.#filterColumnList(modal, '');
    });
    await modal.show();
  }

  /**
   * Filter the column list
   * @param {HTMLElement} modal  the modal to filter
   * @param {string} value the value to filter by
   */
  #filterColumnList(modal: HTMLElement, value: string) {
    modal.querySelector('ids-swappable')?.querySelectorAll<IdsSwappableItem>('ids-swappable-item').forEach((item: IdsSwappableItem) => {
      const textElem = item.querySelector('ids-text');
      if (textElem?.textContent?.toLowerCase().includes(value.toLowerCase())) {
        item.hidden = false;
        textElem.innerHTML = textElem.textContent!.replace(value, `<b>${value}</b>`);
      } else {
        item.hidden = true;
      }
    });
  }

  /**
   * Handle the copy event
   * @private
   * @param {ClipboardEvent} e Clipboard event when copy is triggered
   */
  #handleCopy(e: ClipboardEvent) {
    const selectedMap = this.selectedMap;
    const bounds = selectedMap.bounds;

    if (bounds.maxRow === -1) return;

    e.preventDefault();
    e.stopPropagation();

    const copyRows: Array<string> = [];

    for (let rowIndex = bounds.minRow; rowIndex <= bounds.maxRow; rowIndex++) {
      const rowData = [];

      for (let colIndex = bounds.minCol; colIndex <= bounds.maxCol; colIndex++) {
        const cell = selectedMap.map[rowIndex][colIndex];
        let copyValue = '';

        if (cell) {
          if (new Date(cell.target.textContent.trim()) instanceof Date) {
            copyValue = cell.target.textContent.trim();
          } else {
            copyValue = cell.target?.value ? cell.target.value : cell.value;
          }
        }

        rowData.push(copyValue);
      }

      copyRows.push(rowData.join('\t'));
    }

    e.clipboardData?.setData('text/plain', copyRows.join('\n'));
  }

  /**
   * Handle the paste event
   * @private
   * @param {ClipboardEvent} e Clipboard event when paste is triggered
   */
  #handlePaste(e: ClipboardEvent) {
    const selectedMap = this.selectedMap;

    if (selectedMap.bounds.maxRow === -1) return;

    e.preventDefault();
    e.stopPropagation();

    const pasteData = e.clipboardData?.getData('text/plain') || '';
    const pasteRows = pasteData.split(/\r\n|\n|\r/).map((row) => row.split('\t'));

    const columns = this.rowSelection ? this.visibleColumns.slice(1, this.visibleColumns.length) : this.visibleColumns;
    const bounds = selectedMap.bounds;

    const activeCell = this.activeCell.node;

    if (this.activeCellEditor && this.activeCellCanClose()) {
      activeCell.cancelCellEdit();
      activeCell.focus();
    }

    if (bounds.maxRow === -1) {
      bounds.minRow = parseInt(activeCell.parentElement?.getAttribute(attributes.ROW_INDEX) || '0');
      bounds.minCol = parseInt(activeCell.getAttribute(htmlAttributes.ARIA_COLINDEX) || '0') - 1;
      bounds.minCol = this.rowSelection ? bounds.minCol - 1 : bounds.minCol;
    }

    let pRow = 0;
    let pCol;
    let shouldUpdate;

    for (let rowIndex = bounds.minRow; rowIndex <= this.data.length && pRow < pasteRows.length; rowIndex++) {
      pCol = 0;
      shouldUpdate = false;

      const updateData = { ...this.data[rowIndex] };

      for (let colIndex = bounds.minCol; colIndex <= this.columns.length && pCol < pasteRows[pRow].length; colIndex++) {
        const column = columns[colIndex];

        if (!column) continue;

        const cell = this.cellByIndex(rowIndex, this.rowSelection ? colIndex + 1 : colIndex);
        const cellEditable = !(cell?.classList.contains('is-readonly') || cell?.classList.contains('is-disabled')) && column.editor;

        if (cellEditable && pasteRows[pRow][pCol].length > 0) {
          let pasteValue: string = pasteRows[pRow][pCol];

          // Format the pasted value here
          if (column.editor && column.editor.editorSettings) {
            const settings = column.editor.editorSettings;

            if (settings.maxlength) {
              pasteValue = pasteValue.substring(0, settings.maxlength as number);
            }
          }

          shouldUpdate = true;
          updateData[column.field || column.id] = pasteValue;
        }

        pCol++;
      }

      if (shouldUpdate) {
        this.updateDatasetAndRefresh(rowIndex, updateData);
      }
      pRow++;
    }
  }

  /**
   * Forces closure of any open filter-related popup elements within the data grid
   */
  closePopupFilters() {
    const datagridChildrens = this.shadowRoot?.querySelector('.ids-data-grid-wrapper')?.children;
    if (!datagridChildrens) {
      return;
    }

    // list of popup menu tag names to be close
    const filterNodeNames = ['ids-date-picker-popup', 'ids-popup-menu', 'ids-time-picker-popup', 'ids-dropdown-list'];

    for (const child of datagridChildrens) {
      if (filterNodeNames.includes(child.nodeName?.toLowerCase())) {
        const popup = (child as any).popup;
        if (popup && popup.visible) (child as any).popup.hide();
      }
    }
  }

  /**
   * Set the text of a column header
   * @param {string} columnId The column to update
   * @param {string} text The new header text to set
   */
  setColumnHeaderText(columnId: string, text: string) {
    const column = this.columnDataById(columnId);
    if (!column || !text) return;

    const headerCell = this.shadowRoot?.querySelector(`[column-id="${columnId}"]`);
    const textElement = headerCell?.querySelector('.ids-data-grid-header-text');
    if (textElement) {
      textElement.textContent = text;
      column.name = text;
    }
  }

  /**
   * Set the text search type for search functionality
   * @param {string} value the search type to use
   */
  set textSearchType(value: string) {
    if (value) {
      this.setAttribute(attributes.TEXT_SEARCH_TYPE, value);
    } else {
      this.removeAttribute(attributes.TEXT_SEARCH_TYPE);
    }
  }

  get textSearchType(): string {
    return this.getAttribute(attributes.TEXT_SEARCH_TYPE) || 'default';
  }

  /**
   * Initialize virtual scroll height by positioning the virtual scroll window
   * @private
   */
  #initVirtualScrollHeight() {
    const settings = this.virtualScrollSettings;
    const totalRows = this.treeGrid ? this.virtualRows.length : this.data.length;
    const rows = this.rows;
    const firstRowIndex = rows.length ? (rows[0] as any).rowIndex : 0;
    this.#positionVirtualScrollWindow(firstRowIndex, totalRows, settings);
  }

  /**
   * Save all currently edited cell values before DOM recycling
   * @private
   */
  #saveEditedCellsBeforeScroll() {
    this.#editedCellsCache.clear();

    const editedCells: Array<IdsDataGridCell> = [];

    if (this.activeCell?.node?.isEditing) {
      editedCells.push(this.activeCell?.node);
      const activeCell = this.activeCell.node;
      const columnId = this.columns[this.activeCell.cell].id;
      const cellValue = this.data[this.activeCell.row][columnId];
      const currentValue = activeCell.editor?.input?.value || cellValue || '';
      this.updateActiveCellCache(this.activeCell.row, this.activeCell.cell, currentValue, true);
      this.#activeCellCache!.shouldReinstateCell = true;
      const scrollTop = this.container?.scrollTop || 0;
      const containerHeight = this.container?.clientHeight || 0;
      const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;

      const effectiveRowIndex = this.#getVisibleRowIndex(this.activeCell.row);
      if (effectiveRowIndex === -1) return;

      const rowTop = effectiveRowIndex * rowHeight;
      const rowBottom = rowTop + rowHeight;
      const viewportTop = scrollTop;
      const viewportBottom = scrollTop + containerHeight;

      const isInViewport = rowTop >= viewportTop && rowBottom <= viewportBottom;

      if (!isInViewport) {
        this.commitCellEdit();
      }
    }

    for (const dirtyCell of this.dirtyCells) {
      const idsCell = this.cellByIndex(dirtyCell.row, dirtyCell.cell);
      if (idsCell) {
        editedCells.push(idsCell);
      }
    }

    editedCells?.forEach((cell: any) => {
      const rowIndex = parseInt(cell.parentElement?.getAttribute('row-index') || '-1');
      const columnIndex = parseInt(cell.getAttribute(htmlAttributes.ARIA_COLINDEX) || '0') - 1;

      if (rowIndex >= 0 && columnIndex >= 0) {
        let currentValue = cell.value || cell.textContent || '';
        if (cell.isEditing && cell.editor?.input) {
          currentValue = cell.editor.input.value || cell.editor.input.textContent || '';
        }

        const cacheKey = `${rowIndex}-${columnIndex}`;
        this.#editedCellsCache.set(cacheKey, {
          rowIndex,
          columnIndex,
          currentValue,
          originalValue: cell.originalValue
        });
      }
    });
  }

  /**
   * Restore edited cell values after DOM recycling
   * @private
   */
  #restoreEditedCellsAfterScroll() {
    if (this.#editedCellsCache.size === 0) {
      return;
    }

    const scrollTop = this.container?.scrollTop || 0;
    const containerHeight = this.container?.clientHeight || 0;
    const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;

    this.#editedCellsCache.forEach((cachedCell) => {
      const {
        rowIndex, columnIndex, currentValue, originalValue
      } = cachedCell;

      // Check if row is in viewport
      const rowTop = rowIndex * rowHeight;
      const rowBottom = rowTop + rowHeight;
      const isInViewport = rowTop >= viewportTop && rowBottom <= viewportBottom;

      if (!isInViewport) {
        return;
      }

      const rowElement = this.rowByIndex(rowIndex);
      if (!rowElement) {
        return;
      }

      const cellElement = this.cellByIndex(rowIndex, columnIndex);
      if (!cellElement) {
        return;
      }

      if (originalValue !== undefined) {
        cellElement.originalValue = originalValue;
      }

      // Only restore editing state for the cell that was actively being edited
      if (cellElement.column?.editor && this.#activeCellCache
        && this.#activeCellCache.rowIndex === rowIndex
        && this.#activeCellCache.columnIndex === columnIndex) {
        cellElement.startCellEdit(undefined, false);
        if (cellElement.editor?.input) {
          cellElement.editor.input.value = currentValue;
        }
      }
    });

    this.#editedCellsCache.clear();
  }

  /**
   * Restore active cell focus and edit state after scroll ends
   * @private
   */
  #restoreActiveCellAfterScroll() {
    const isHeaderActive = this.header && this.header.activeColumn >= 0;
    if (!this.#activeCellCache || !this.#activeCellCache?.shouldReinstateCell || isHeaderActive) {
      return;
    }

    const {
      rowIndex, columnIndex, currentValue, wasEditing
    } = this.#activeCellCache;

    // Check if the cached row and column exist in data
    if (rowIndex < 0 || rowIndex >= this.data.length || columnIndex < 0 || columnIndex >= this.visibleColumns.length) {
      return;
    }

    // Check if the cached row is currently visible on screen
    const rowElement = this.rowByIndex(rowIndex);
    if (!rowElement) {
      return;
    }

    const scrollTop = this.container?.scrollTop || 0;
    const containerHeight = this.container?.clientHeight || 0;
    const rowHeight = this.virtualScrollSettings.ROW_HEIGHT;

    const effectiveRowIndex = this.#getVisibleRowIndex(rowIndex);
    if (effectiveRowIndex === -1) return;

    const rowTop = effectiveRowIndex * rowHeight;
    const rowBottom = rowTop + rowHeight;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;

    const isInViewport = rowTop >= viewportTop && rowBottom <= viewportBottom;

    if (!isInViewport) {
      this.#activeCellCache.hasLeaveViewport = true;
      return;
    }

    // If cell is in viewport, restore it (regardless of hasLeaveViewport state)
    const cellElement = this.cellByIndex(rowIndex, columnIndex);
    if (!cellElement) {
      return;
    }

    this.setActiveCell(columnIndex, rowIndex, false, 'internal');
    // Restore edit state only if we explicitly know the cell was being edited before
    // (wasEditing === true means it was being edited; undefined means we don't know, so don't restore)
    if (wasEditing === true && cellElement.column?.editor) {
      cellElement.startCellEdit();
      if (cellElement.editor?.input) {
        cellElement.editor.input.value = currentValue;
      }
    }

    // Re-expand cell if it was expanded before scroll
    if (this.#expandedCellInfo
      && this.#expandedCellInfo.row === rowIndex
      && this.#expandedCellInfo.cell === columnIndex
      && cellElement.column?.expandOnActivate) {
      this.expandCell(rowIndex, columnIndex);
    }
  }

  /**
   * Clears the active cell cache, optionally checking if the cached row is within viewport
   * @param {boolean} withinViewportCheck - If true, only clears cache if the cached row is visible in viewport
   * @returns {void}
   */
  clearActiveCellCache(withinViewportCheck = false) {
    if (withinViewportCheck && this.#activeCellCache) {
      const {
        rowIndex
      } = this.#activeCellCache;
      const rowElement = this.rowByIndex(rowIndex);
      const containerRect = this.container?.getBoundingClientRect();
      const rowRect = rowElement?.getBoundingClientRect();

      if (!containerRect || !rowRect) {
        return;
      }

      const isRowVisible = rowRect.top >= containerRect.top
        && rowRect.bottom <= containerRect.bottom;

      if (!isRowVisible) {
        return;
      }
    }

    this.#activeCellCache = null;
  }

  /**
   * Updates the active cell cache with new values
   * @param {number} row - The row index
   * @param {number} cell - The cell index
   * @param {string} cellValue - The current cell value
   * @param {boolean} isEditing - Whether the cell is currently in edit mode
   */
  updateActiveCellCache(row: number, cell: number, cellValue: string, isEditing?: boolean) {
    // Only update wasEditing if explicitly provided (e.g., when saving before scroll)
    // Don't set it during normal navigation to avoid preventing legitimate edit mode restoration
    const cacheUpdate: ActiveCellCache = {
      rowIndex: row,
      columnIndex: cell,
      currentValue: cellValue,
      hasLeaveViewport: false,
      shouldReinstateCell: true
    };

    if (isEditing !== undefined) {
      cacheUpdate.wasEditing = isEditing;
    }

    this.#activeCellCache = cacheUpdate;
  }

  /**
   * Add or update an edited cell in the cache
   * @param {number} rowIndex - The row index
   * @param {number} columnIndex - The column index
   * @param {string} currentValue - The current edited value
   * @param {string} originalValue - The original value before editing
   */
  cacheEditedCell(rowIndex: number, columnIndex: number, currentValue: string, originalValue?: string) {
    const cacheKey = `${rowIndex}-${columnIndex}`;
    this.#editedCellsCache.set(cacheKey, {
      rowIndex,
      columnIndex,
      currentValue,
      originalValue
    });
  }

  /**
   * Remove an edited cell from the cache (when editing is committed or cancelled)
   * @param {number} rowIndex - The row index
   * @param {number} columnIndex - The column index
   */
  removeEditedCellFromCache(rowIndex: number, columnIndex: number) {
    const cacheKey = `${rowIndex}-${columnIndex}`;
    this.#editedCellsCache.delete(cacheKey);
  }

  /**
   * Detaches and cleans up all filter-related components and their references.
   * Removes event listeners, clears DOM references, and removes filter popups to prevent memory leaks.
   * Should be called when the data grid is being destroyed or filters need to be completely reset.
   */
  detachFilters() {
    if (this.#filters) {
      this.#filters.detachAllFilterPopups(false);
      this.#filters.root = null;
      this.#filters = undefined;
    }

    this.filterFields.forEach((element: IdsTriggerField) => {
      element.disconnectEvents();
      element.input?.remove?.();
      element.container = undefined;
      const filterPopup: any = this.shadowRoot?.querySelector(`.ids-data-grid-wrapper [target="#${element.id}"]`);
      filterPopup?.detachState();
    });

    // Find and remove any remaining filter popups that may have been moved
    const allPopups = this?.shadowRoot?.querySelectorAll('ids-date-picker-popup[data-filter-type="date"], ids-time-picker-popup[data-filter-type="time"], ids-popup-menu');
    allPopups?.forEach((popup: any) => {
      popup?.detachReference?.();
      popup.remove();
    });

    this.filterFields = [];
    this.#header = undefined;
  }

  private buildRowIdIndexMap(): Map<string | number, number> {
    const map = new Map<string | number, number>();
    const primaryKey = this.datasource?.primaryKey;
    if (!primaryKey) return map;

    for (let i = 0; i < this.data.length; i++) {
      const id = this.data[i]?.[primaryKey];
      if (id != null) map.set(String(id), i);
    }
    return map;
  }

  /**
   * Get all dirty cells with row/cell state information.
   * Returns dirty cell states using rowId and columnId instead of indices.
   * @returns {Array<{rowId: string | number, columnId: string, originalValue: any}>} Array of dirty cell states
   */
  getDirtyCells(): Array<{ rowId: string | number; columnId: string; originalValue: any }> {
    try {
      const dirtyCellsWithIds: Array<{ rowId: string | number; columnId: string; originalValue: any }> = [];

      if (!this.datasource?.primaryKey) {
        return dirtyCellsWithIds;
      }

      const primaryKey = this.datasource.primaryKey;

      for (let index = 0; index < this.data.length; index++) {
        const rowData = this.data[index];
        if (rowData?.dirtyCells) {
          const rowId = rowData[primaryKey] ?? null;
          if (rowId !== null) {
            rowData.dirtyCells.forEach((dirtyCellInRow: any) => {
              if (dirtyCellInRow.columnId && 'originalValue' in dirtyCellInRow) {
                dirtyCellsWithIds.push({
                  rowId,
                  columnId: dirtyCellInRow.columnId,
                  originalValue: dirtyCellInRow.originalValue
                });
              }
            });
          }
        }
      }

      return dirtyCellsWithIds;
    } catch (error) {
      // getDirtyCells failed — return empty array
      return [];
    }
  }

  /**
   * Set dirty cells using row/cell state information.
   * Restores dirty cell states using rowId and columnId instead of indices.
   * Note: rowId values are compared as strings to avoid strict type mismatch between string/number.
   * @param {Array<{rowId: string | number, columnId: string, originalValue: any}>} dirtyCells - Array of dirty cell entries
   */
  setDirtyCells(dirtyCells: Array<{ rowId: string | number; columnId: string; originalValue: any }>): void {
    try {
      if (!this.datasource?.primaryKey || !Array.isArray(dirtyCells)) return;

      const idToIndex = this.buildRowIdIndexMap();

      // Resolve indices once, then group by row for batched updateDataset calls
      const rowUpdates = new Map<number, Array<{ columnIndex: number; columnId: string; originalValue: any }>>();

      for (const cell of dirtyCells) {
        if (!this.validateDirtyCellState(cell)) continue;

        const rowIndex = idToIndex.get(String(cell.rowId)) ?? null;
        const columnIndex = this.visibleColumnIdxById(cell.columnId);
        if (rowIndex === null || columnIndex === -1) continue;

        if (!rowUpdates.has(rowIndex)) rowUpdates.set(rowIndex, []);
        rowUpdates.get(rowIndex)!.push({ columnIndex, columnId: cell.columnId, originalValue: cell.originalValue });
      }

      // Apply batched per row
      rowUpdates.forEach((cells, rowIndex) => {
        const rowData = this.data[rowIndex];
        if (!rowData) return;

        if (!rowData.dirtyCells) {
          rowData.dirtyCells = [];
        }
        const rowDirtyCells = rowData.dirtyCells;
        let changed = false;

        cells.forEach(({ columnIndex, columnId, originalValue }) => {
          const alreadyDirty = rowDirtyCells.some((item: any) => item.cell === columnIndex);
          if (alreadyDirty) return;

          rowDirtyCells.push({ cell: columnIndex, columnId, originalValue });
          changed = true;

          const cellElement = this.cellByIndex(rowIndex, columnIndex);
          if (cellElement) cellElement.classList.add('is-dirty');
        });

        if (changed) {
          this.updateDataset(rowIndex, { dirtyCells: rowDirtyCells });
        }
      });
    } catch (error) { /* ignore */ }
  }

  /**
   * Get all row states with row/cell state information.
   * Returns row states using rowId instead of indices.
   * @returns {Array<{rowId: string | number, stateName: string}>} Array of row states
   */
  getRowStates(): Array<{ rowId: string | number; stateName: string }> {
    try {
      const rowStatesWithIds: Array<{ rowId: string | number; stateName: string }> = [];

      if (!this.datasource?.primaryKey) {
        return rowStatesWithIds;
      }

      const primaryKey = this.datasource.primaryKey;

      for (let index = 0; index < this.data.length; index++) {
        const rowData = this.data[index];
        if (rowData?.rowState) {
          const rowId = rowData[primaryKey] ?? null;
          if (rowId !== null) {
            rowStatesWithIds.push({ rowId, stateName: rowData.rowState });
          }
        }
      }

      return rowStatesWithIds;
    } catch (error) {
      // getRowStates failed — return empty array
      return [];
    }
  }

  /**
   * Set row states using row/cell state information.
   * Restores row states using rowId instead of indices.
   * Note: rowId values are compared as strings to avoid strict type mismatch between string/number.
   * @param {Array<{rowId: string | number, stateName: string}>} states - Array of row states to restore
   */
  setRowStates(states: Array<{ rowId: string | number; stateName: string }>): void {
    try {
      if (!this.datasource?.primaryKey || !Array.isArray(states)) return;

      const idToIndex = this.buildRowIdIndexMap();

      for (const state of states) {
        if (!this.validateRowState(state)) continue;

        const rowIndex = idToIndex.get(String(state.rowId)) ?? null;
        if (rowIndex !== null) {
          this.setRowState(rowIndex, state.stateName);
        }
      }
    } catch (error) {
      // setRowStates failed — no-op
    }
  }

  /**
   * Validate that an object conforms to the DirtyCellState interface.
   * Uses `'originalValue' in state` to allow `undefined` as a legitimate original value.
   * @param {any} state - The object to validate
   * @returns {boolean} True if the object is a valid DirtyCellState
   * @private
   */
  private validateDirtyCellState(state: any): state is { rowId: string | number; columnId: string; originalValue: any } {
    return (
      state != null
      && typeof state === 'object'
      && (typeof state.rowId === 'string' || typeof state.rowId === 'number')
      && typeof state.columnId === 'string'
      && 'originalValue' in state
    );
  }

  /**
   * Validate that an object conforms to the RowState interface
   * @param {any} state - The object to validate
   * @returns {boolean} True if the object is a valid RowState
   * @private
   */
  private validateRowState(state: any): state is { rowId: string | number; stateName: string } {
    return (
      state != null
      && typeof state === 'object'
      && (typeof state.rowId === 'string' || typeof state.rowId === 'number')
      && typeof state.stateName === 'string'
      && (this.VALID_ROW_STATES as readonly string[]).includes(state.stateName)
    );
  }
}
